/* eslint-disable */
import { Zodios, type ZodiosOptions, makeApi } from "@zodios/core";
import { z } from "zod";

export const ArtifactStatus = z.enum([
  "pending",
  "received",
  "processed",
  "success",
  "failure",
]);
export type ArtifactStatus = z.infer<typeof ArtifactStatus>;

export const ALDEmpty = z.object({ type: z.literal("empty").default("empty") });
export type ALDEmpty = z.infer<typeof ALDEmpty>;

export const ALDOrderBackfill = z.object({
  type: z.literal("enedis_order_backfill").default("enedis_order_backfill"),
  flow: z.string(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  way: z
    .union([
      z.enum(["production", "consumption", "injection", "unknown"]),
      z.null(),
    ])
    .nullish(),
  company: z.union([z.string(), z.null()]).nullish(),
});
export type ALDOrderBackfill = z.infer<typeof ALDOrderBackfill>;

export const ALDSerieDataSchema = z.object({
  name: z.string(),
  ts_target: z.union([z.string(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  nrows: z.union([z.number(), z.null()]).nullish(),
  nans: z.union([z.number(), z.null()]).nullish(),
  raw_data: z
    .union([z.record(z.union([z.number(), z.null()])), z.null()])
    .nullish(),
});
export type ALDSerieDataSchema = z.infer<typeof ALDSerieDataSchema>;

export const ALDParseTSSuccess = z.object({
  column_names: z.array(z.string()),
  ts_data: z.array(ALDSerieDataSchema).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: z.literal("enedis_ts_parse_success").default("enedis_ts_parse_success"),
});
export type ALDParseTSSuccess = z.infer<typeof ALDParseTSSuccess>;

export const ContractState = z.enum([
  "in_operation",
  "inaccessible",
  "canceled",
  "improductive",
]);
export type ContractState = z.infer<typeof ContractState>;

export const Meter = z.object({
  serial_number: z.string(),
  registration_number: z.union([z.string(), z.null()]).nullish(),
  accessibility: z.union([z.boolean(), z.null()]).nullish(),
  intensity: z.union([z.string(), z.null()]).nullish(),
  voltage: z.union([z.string(), z.null()]).nullish(),
  property_type: z.union([z.string(), z.null()]).nullish(),
  model: z.union([z.string(), z.null()]).nullish(),
});
export type Meter = z.infer<typeof Meter>;

export const Metering = z.object({
  meter_model: z.string(),
  frequency: z.union([z.string(), z.null()]),
  meters: z.array(Meter),
});
export type Metering = z.infer<typeof Metering>;

export const Segment = z.enum([
  "p1",
  "p2",
  "p3",
  "p4",
  "c1",
  "c2",
  "c3",
  "c4",
  "c5",
  "unknown",
]);
export type Segment = z.infer<typeof Segment>;

export const VoltageDomain = z.enum(["BTINF", "BTSUP", "HTA", "HTB"]);
export type VoltageDomain = z.infer<typeof VoltageDomain>;

export const UnitValue = z.object({ value: z.number(), unit: z.string() });
export type UnitValue = z.infer<typeof UnitValue>;

export const LinkType = z.enum([
  "main",
  "backup",
  "deducting",
  "host",
  "consumption",
  "production",
  "internal",
  "distribution_network",
  "unimplemented",
]);
export type LinkType = z.infer<typeof LinkType>;

export const LinkedPRM = z.object({ prm: z.string(), type: LinkType });
export type LinkedPRM = z.infer<typeof LinkedPRM>;

export const NetworkConnection = z.object({
  voltage_domain: VoltageDomain,
  power_production: UnitValue,
  power_consumption: UnitValue,
  linked: z.array(LinkedPRM),
});
export type NetworkConnection = z.infer<typeof NetworkConnection>;

export const Address = z.object({
  address: z.string(),
  postal_code: z.string(),
  city: z.string(),
});
export type Address = z.infer<typeof Address>;

export const EnedisNetworkCfg = z.object({
  prm: z.string(),
  state: z.union([ContractState, z.null()]).nullish(),
  metering: Metering,
  segment: Segment,
  service_level: z.union([z.number(), z.null()]).nullish(),
  network: NetworkConnection,
  address: Address,
  technos: z.array(z.string()),
  raw_data: z.record(z.any()),
});
export type EnedisNetworkCfg = z.infer<typeof EnedisNetworkCfg>;

export const ALDEnedisParseStaticSuccess = z.object({
  type: z
    .literal("enedis_static_parse_success")
    .default("enedis_static_parse_success"),
  filename: z.string(),
  content: EnedisNetworkCfg,
});
export type ALDEnedisParseStaticSuccess = z.infer<
  typeof ALDEnedisParseStaticSuccess
>;

export const EnedisOrderStatus = z.enum([
  "Active",
  "power_state_not_compatible",
  "meter_not_compatible",
  "bad_contract_state",
  "already_ordered",
  "no_consumption_contract",
  "no_injection_contract",
  "error",
  "missing_prm",
  "prm_not_found",
  "prm_terminated",
  "prm_not_in_service",
  "not_teleoperable",
]);
export type EnedisOrderStatus = z.infer<typeof EnedisOrderStatus>;

export const ALDEnedisOrderMeasurementsSuccess = z.object({
  status: EnedisOrderStatus,
  order_id: z.union([z.string(), z.null()]).nullish(),
  service_id: z.union([z.string(), z.null()]).nullish(),
  code: z.union([z.string(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  order_date: z.coerce.date().nullish(),
  prm: z.union([z.string(), z.null()]).nullish(),
  error_code: z.union([z.string(), z.null()]).nullish(),
  n_failures: z.union([z.number(), z.null()]).nullish(),
  type: z
    .literal("enedis_order_measurements_success")
    .default("enedis_order_measurements_success"),
});
export type ALDEnedisOrderMeasurementsSuccess = z.infer<
  typeof ALDEnedisOrderMeasurementsSuccess
>;

export const ALDELDReceived = z.object({
  flow: z.string(),
  filename: z.string(),
  fmetadata: z.record(z.any()),
  type: z.literal("eld_received").default("eld_received"),
});
export type ALDELDReceived = z.infer<typeof ALDELDReceived>;

export const ALDELDParseTS = z.object({
  column_names: z.array(z.string()),
  ts_data: z.array(ALDSerieDataSchema).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  flow: z.string(),
  filename: z.string(),
  fmetadata: z.record(z.any()),
  type: z.literal("eld_ts_parse").default("eld_ts_parse"),
});
export type ALDELDParseTS = z.infer<typeof ALDELDParseTS>;

export const _InvoicingAttributesMixin = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  quantity: z.number(),
  price: z.number(),
  amount: z.number(),
  is_regulation: z.union([z.boolean(), z.null()]).nullish(),
  is_summable: z.boolean(),
  product: z.string(),
  quantity_unit: z.string(),
  extra: z.union([z.record(z.string()), z.null()]).nullish(),
});
export type _InvoicingAttributesMixin = z.infer<
  typeof _InvoicingAttributesMixin
>;

export const ALDELDParseInvoice = z.object({
  flow: z.string(),
  filename: z.string(),
  fmetadata: z.record(z.any()),
  type: z.literal("eld_invoice_parse").default("eld_invoice_parse"),
  site_name: z.union([z.string(), z.null()]).nullish(),
  contract_purchase_order: z.union([z.string(), z.null()]).nullish(),
  prm: z.union([z.string(), z.null()]).nullish(),
  invoice_lines: z.array(_InvoicingAttributesMixin),
  techno: z.union([z.string(), z.null()]).nullish(),
});
export type ALDELDParseInvoice = z.infer<typeof ALDELDParseInvoice>;

export const ALDApiTSUpsert = z.object({
  type: z.literal("api_ts_upsert").default("api_ts_upsert"),
  previous: ALDSerieDataSchema,
  inserted: ALDSerieDataSchema,
});
export type ALDApiTSUpsert = z.infer<typeof ALDApiTSUpsert>;

export const ALDError = z.object({
  type: z.literal("error").default("error"),
  err_name: z.string(),
  err_msg: z.string(),
});
export type ALDError = z.infer<typeof ALDError>;

export const ALDEnedisNotReceivedFailure = z.object({
  request_id: z.string(),
  receivability: z.string(),
  publication_status: z.string(),
  file_key: z.union([z.number(), z.null()]).nullish(),
  reason: z.union([z.string(), z.null()]).nullish(),
  type: z.literal("enedis_not_received").default("enedis_not_received"),
  filename: z.string(),
  timestamp: z.coerce.date(),
});
export type ALDEnedisNotReceivedFailure = z.infer<
  typeof ALDEnedisNotReceivedFailure
>;

export const ALDEnedisParseError = z.object({
  type: z.literal("enedis_parse_error").default("enedis_parse_error"),
  err_name: z.string(),
  err_msg: z.string(),
  filename: z.string(),
  timestamp: z.coerce.date(),
});
export type ALDEnedisParseError = z.infer<typeof ALDEnedisParseError>;

export const ALDELDParseError = z.object({
  flow: z.string(),
  filename: z.string(),
  fmetadata: z.record(z.any()),
  type: z.literal("eld_parse_error").default("eld_parse_error"),
  err_name: z.string(),
  err_msg: z.string(),
});
export type ALDELDParseError = z.infer<typeof ALDELDParseError>;

export const ALDEnedisOrderMeasurementsError = z.object({
  type: z
    .literal("enedis_order_measurements_error")
    .default("enedis_order_measurements_error"),
  err_name: z.string(),
  err_msg: z.string(),
  status: z.string(),
  prm: z.union([z.string(), z.null()]).nullish(),
  error_code: z.union([z.string(), z.null()]).nullish(),
  refusal: z.union([z.boolean(), z.null()]).nullish(),
});
export type ALDEnedisOrderMeasurementsError = z.infer<
  typeof ALDEnedisOrderMeasurementsError
>;

export const ArtifactLogSchema = z.object({
  status: ArtifactStatus,
  origin: z.string(),
  data: z.discriminatedUnion("type", [
    ALDEmpty,
    ALDOrderBackfill,
    ALDParseTSSuccess,
    ALDEnedisParseStaticSuccess,
    ALDEnedisOrderMeasurementsSuccess,
    ALDELDReceived,
    ALDELDParseTS,
    ALDELDParseInvoice,
    ALDApiTSUpsert,
    ALDError,
    ALDEnedisNotReceivedFailure,
    ALDEnedisParseError,
    ALDELDParseError,
    ALDEnedisOrderMeasurementsError,
  ]),
  created_at: z.coerce.date(),
  initiator: z.union([z.string(), z.null()]),
});
export type ArtifactLogSchema = z.infer<typeof ArtifactLogSchema>;

export const ArtifactKind = z.enum([
  "api_async",
  "api_sync",
  "email_reception",
  "email_send",
  "dropped_file",
  "secured_file_transfer",
  "manual_edition",
]);
export type ArtifactKind = z.infer<typeof ArtifactKind>;

export const ArtifactSProviderSchema = z.object({ name: z.string() });
export type ArtifactSProviderSchema = z.infer<typeof ArtifactSProviderSchema>;

export const ArtifactSchema = z.object({
  uuid: z.string().uuid(),
  logs: z.array(ArtifactLogSchema),
  code: z.string(),
  parse_flow: z.union([z.string(), z.null()]),
  kind: ArtifactKind,
  sprovider: z.union([ArtifactSProviderSchema, z.null()]).nullish(),
});
export type ArtifactSchema = z.infer<typeof ArtifactSchema>;

export const Unauthorized = z.object({
  name: z.literal("Unauthorized"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type Unauthorized = z.infer<typeof Unauthorized>;

export const NoObjectFound = z.object({
  name: z.literal("NoObjectFound"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type NoObjectFound = z.infer<typeof NoObjectFound>;

export const ValidationError = z.object({
  loc: z.array(z.union([z.string(), z.number()])),
  msg: z.string(),
  type: z.string(),
});
export type ValidationError = z.infer<typeof ValidationError>;

export const HTTPValidationError = z.object({
  detail: z.array(ValidationError).nullish(),
});
export type HTTPValidationError = z.infer<typeof HTTPValidationError>;

export const CompanyKind = z.enum([
  "company",
  "assetowner",
  "assetmanager",
  "holding",
  "offtaker",
  "tso",
  "dso",
]);
export type CompanyKind = z.infer<typeof CompanyKind>;

export const CompanySchemaShort = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
});
export type CompanySchemaShort = z.infer<typeof CompanySchemaShort>;

export const MeilisearchInfos = z.object({
  datastudio_index: z.string(),
  topology_index: z.string(),
  api_key: z.union([z.string(), z.null()]).nullish(),
});
export type MeilisearchInfos = z.infer<typeof MeilisearchInfos>;

export const UIFeature = z.enum([
  "commissioning_mgmt",
  "supervision",
  "invoice_table_display_publication_date",
  "invoice_table_filter_validation_date",
  "allow_internal_credit_note",
]);
export type UIFeature = z.infer<typeof UIFeature>;

export const User = z.object({
  sub: z.string(),
  email: z.union([z.string(), z.null()]),
  name: z.union([z.string(), z.null()]),
  permissions: z.array(
    z.union([
      z.enum([
        "read:company",
        "write:company",
        "read:contract",
        "write:contract",
        "read:document",
        "write:document",
        "upload:document",
        "read:event",
        "write:event",
        "read:invoice",
        "write:invoice",
        "write:invoice_accounted",
        "read:market",
        "write:market",
        "read:note",
        "write:note",
        "read:supervision",
        "write:supervision",
        "read:datastudio",
        "read:finance",
        "read:partner",
        "read:timeseries",
        "write:timeseries",
        "order:timeseries",
        "write:datahealth",
        "write:geo",
        "write:goldensource",
        "write:metric",
      ]),
      z.string(),
    ])
  ),
  org_id: z.string(),
  company: CompanySchemaShort,
  meilisearch: z.union([MeilisearchInfos, z.null()]).nullish(),
  ui_features: z.array(UIFeature).nullish(),
});
export type User = z.infer<typeof User>;

export const RegionSchema = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  code: z.string(),
  uuid: z.string().uuid(),
  tz: z.string(),
});
export type RegionSchema = z.infer<typeof RegionSchema>;

export const CountrySchema = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  code: z.string(),
});
export type CountrySchema = z.infer<typeof CountrySchema>;

export const PortfolioSchema = z.object({
  name: z.string(),
  uuid: z.string().uuid(),
});
export type PortfolioSchema = z.infer<typeof PortfolioSchema>;

export const NetworkVoltageKind = z.enum([
  "HTA",
  "HTB",
  "HTB2",
  "BTSUP",
  "BTINF",
]);
export type NetworkVoltageKind = z.infer<typeof NetworkVoltageKind>;

export const SiteType = z.enum([
  "injection",
  "partial_self_cons",
  "total_self_cons",
  "acc_prod",
  "acc_cons",
  "consumption",
]);
export type SiteType = z.infer<typeof SiteType>;

export const AssetOwnerSiteSchema = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  holding: z.union([CompanySchemaShort, z.null()]).nullish(),
});
export type AssetOwnerSiteSchema = z.infer<typeof AssetOwnerSiteSchema>;

export const SiteStatus = z.enum([
  "no_status",
  "development",
  "financing",
  "connection_requested",
  "construction",
  "waiting_commissioning",
  "commissioned",
  "added_to_warrant",
  "test_passed",
  "test_failed",
  "work_acceptance",
  "handed_over",
  "handed_over_with_reserve",
  "on_hold",
  "dismanteled",
  "sold",
  "capacity_change",
  "damaged",
  "repaired",
]);
export type SiteStatus = z.infer<typeof SiteStatus>;

export const DeliveryPointCodeKind = z.enum(["malo", "card", "ean"]);
export type DeliveryPointCodeKind = z.infer<typeof DeliveryPointCodeKind>;

export const DeliveryPointType = z.enum([
  "injection",
  "production",
  "consumption",
  "collective_autoconsumption",
]);
export type DeliveryPointType = z.infer<typeof DeliveryPointType>;

export const DSOSchema = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  code: z.string(),
});
export type DSOSchema = z.infer<typeof DSOSchema>;

export const DeliveryPointKind = z.enum(["meter", "headmeter", "submeter"]);
export type DeliveryPointKind = z.infer<typeof DeliveryPointKind>;

export const TurbineType = z.enum(["onshore", "offshore"]);
export type TurbineType = z.infer<typeof TurbineType>;

export const WindTurbineSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: TurbineType,
  hub_height: z.union([z.number(), z.null()]).nullish(),
  model: z.string(),
  manufacturer: z.string(),
  powercurve: z.array(z.array(z.any())),
});
export type WindTurbineSchema = z.infer<typeof WindTurbineSchema>;

export const SolarMountingType = z.enum([
  "ground_mounted",
  "canopy",
  "rooftop",
]);
export type SolarMountingType = z.infer<typeof SolarMountingType>;

export const SolarTrackingType = z.enum([
  "fix",
  "single_axis",
  "single_axis_azimuth",
  "dual_axis",
]);
export type SolarTrackingType = z.infer<typeof SolarTrackingType>;

export const OrientationReference = z.enum(["south", "north"]);
export type OrientationReference = z.infer<typeof OrientationReference>;

export const SolarFieldSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: SolarMountingType,
  tracking: SolarTrackingType,
  tilt: z.number(),
  orientation: z.number(),
  orientation_reference: OrientationReference,
  panel_capacity: z.union([z.number(), z.null()]).nullish(),
});
export type SolarFieldSchema = z.infer<typeof SolarFieldSchema>;

export const HydroType = z.enum([
  "run_of_river",
  "pumped_storage",
  "reservoir",
]);
export type HydroType = z.infer<typeof HydroType>;

export const HydroTurbineSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: HydroType,
});
export type HydroTurbineSchema = z.infer<typeof HydroTurbineSchema>;

export const CogeType = z.enum(["biomass", "waste", "other"]);
export type CogeType = z.infer<typeof CogeType>;

export const CogenerationSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: CogeType,
});
export type CogenerationSchema = z.infer<typeof CogenerationSchema>;

export const ConsumerType = z.literal("industry_retail");
export type ConsumerType = z.infer<typeof ConsumerType>;

export const ConsumerSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: ConsumerType,
});
export type ConsumerSchema = z.infer<typeof ConsumerSchema>;

export const BatteryType = z.literal("default");
export type BatteryType = z.infer<typeof BatteryType>;

export const BatterySchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: BatteryType,
  energy_capacity: z.number(),
  charge_capacity: z.union([z.number(), z.null()]).nullish(),
});
export type BatterySchema = z.infer<typeof BatterySchema>;

export const OnHoldReason = z.enum([
  "missing_iban",
  "waiting_buyer",
  "waiting_legal_confirmation",
  "waiting_acc_topo",
  "verification_needed",
  "incorrect_config",
  "edfoa_producer_does_not_exist",
  "edfoa_no_rights_on_account",
  "counter_reset",
  "other",
]);
export type OnHoldReason = z.infer<typeof OnHoldReason>;

export const DPContractualMappingSchema = z.object({
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  external_site_ref: z.union([z.string(), z.null()]).nullish(),
  default_on_hold_reasons: z.array(OnHoldReason).nullish(),
  uuid: z.string().uuid(),
});
export type DPContractualMappingSchema = z.infer<
  typeof DPContractualMappingSchema
>;

export const DeliveryPointSchema = z.object({
  code: z.union([z.string(), z.null()]).nullish(),
  reference: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  code_kind: z.union([DeliveryPointCodeKind, z.null()]).nullish(),
  type: z.union([DeliveryPointType, z.null()]).nullish(),
  extra: z.union([z.record(z.any()), z.null()]).nullish(),
  network_extra: z.union([EnedisNetworkCfg, z.null()]).nullish(),
  dso: DSOSchema,
  kind: DeliveryPointKind,
  uuid: z.string().uuid(),
  assets: z.array(
    z.union([
      WindTurbineSchema,
      SolarFieldSchema,
      HydroTurbineSchema,
      CogenerationSchema,
      ConsumerSchema,
      BatterySchema,
    ])
  ),
  contractual_mappings: z
    .array(DPContractualMappingSchema)
    .nullish()
    .default([]),
});
export type DeliveryPointSchema = z.infer<typeof DeliveryPointSchema>;

export const Civility = z.enum(["mister", "madam", "madam_mister", "couple"]);
export type Civility = z.infer<typeof Civility>;

export const CloudImageUrl = z.string();
export type CloudImageUrl = z.infer<typeof CloudImageUrl>;

export const ContactCloudImages = z.object({
  signature: z.union([CloudImageUrl, z.null()]).nullish(),
  avatar: CloudImageUrl.min(1).max(2083).url().nullish(),
});
export type ContactCloudImages = z.infer<typeof ContactCloudImages>;

export const ContactInfoSchema = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  uuid: z.string().uuid(),
  team_name: z.union([z.string(), z.null()]).nullish(),
  position: z.union([z.string(), z.null()]).nullish(),
  company_phone: z.union([z.string(), z.null()]).nullish(),
});
export type ContactInfoSchema = z.infer<typeof ContactInfoSchema>;

export const ContactRole = z.enum([
  "invoice_sender",
  "invoice_recipient",
  "e_invoice_recipient",
  "invoice_email_signer",
  "invoice_cc_email",
  "invoice_bcc_email",
  "notif_accounting_export",
  "notif_soregie_validate_energy_index",
  "notif_static_import",
  "sms_spot_price_negative",
  "warrant_recipient",
  "technical_coordinator_recipient",
  "lease_receipt_sender",
]);
export type ContactRole = z.infer<typeof ContactRole>;

export const ContactInfoWithRolesSchema = z.object({
  contact_info: ContactInfoSchema,
  roles: z.array(ContactRole),
});
export type ContactInfoWithRolesSchema = z.infer<
  typeof ContactInfoWithRolesSchema
>;

export const AddressSchema = z.object({
  uuid: z.string().uuid(),
  name: z.union([z.string(), z.null()]).nullish(),
  address: z.union([z.string(), z.null()]).nullish(),
  address_extra: z.union([z.string(), z.null()]).nullish(),
  postal_box: z.union([z.string(), z.null()]).nullish(),
  city: z.union([z.string(), z.null()]).nullish(),
  postal_code: z.union([z.string(), z.null()]).nullish(),
  country: z.union([z.string(), z.null()]).nullish(),
});
export type AddressSchema = z.infer<typeof AddressSchema>;

export const Inverter = z.object({
  brand: z.union([z.string(), z.null()]).nullish(),
  model: z.union([z.string(), z.null()]).nullish(),
  capacity: z.union([z.number(), z.null()]).nullish(),
});
export type Inverter = z.infer<typeof Inverter>;

export const Module = z.object({
  model: z.union([z.string(), z.null()]).nullish(),
  capacity: z.union([z.number(), z.null()]).nullish(),
  number: z.union([z.number(), z.null()]).nullish(),
});
export type Module = z.infer<typeof Module>;

export const PublicHoliday = z.object({
  day: z.number().int(),
  month: z.number().int(),
});
export type PublicHoliday = z.infer<typeof PublicHoliday>;

export const PeriodDefinition = z.object({
  months: z
    .array(z.number())
    .nullish()
    .default([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  weekdays: z.array(z.number()).nullish().default([0, 1, 2, 3, 4, 5, 6]),
  hours: z
    .array(z.number())
    .nullish()
    .default([
      0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
      21, 22, 23,
    ]),
  days_to_exclude: z.array(PublicHoliday).nullish(),
  days_to_include: z.array(PublicHoliday).nullish(),
});
export type PeriodDefinition = z.infer<typeof PeriodDefinition>;

export const ReactiveSetpointDirective = z.enum(["null", "inject", "offtake"]);
export type ReactiveSetpointDirective = z.infer<
  typeof ReactiveSetpointDirective
>;

export const ReactiveSetpointTanPhi = z.object({
  type: z.literal("tanphi"),
  periods: z.array(PeriodDefinition).nullish(),
  connection_capacity: z.number().gt(0),
  directive: ReactiveSetpointDirective,
  tanphi_min: z.number(),
  tanphi_max: z.number().gte(0),
  low_production_coefficient: z.number().gte(0).lte(1),
  q_max: z.number().gte(0),
  suitable_tanphi: z.union([z.number(), z.null()]).nullish(),
});
export type ReactiveSetpointTanPhi = z.infer<typeof ReactiveSetpointTanPhi>;

export const ReactiveSetpointLocalVoltageRegulation = z.object({
  type: z.literal("local_voltage_regulation"),
  periods: z.array(PeriodDefinition).nullish(),
  connection_capacity: z.number().gt(0),
  directive: z.literal("null"),
});
export type ReactiveSetpointLocalVoltageRegulation = z.infer<
  typeof ReactiveSetpointLocalVoltageRegulation
>;

export const TechnicalData = z.object({
  inverters: z.union([z.array(Inverter), z.null()]).nullish(),
  extra: z.union([z.record(z.any()), z.null()]).nullish(),
  modules: z.union([z.array(Module), z.null()]).nullish(),
  tension_threshold: z.union([z.number(), z.null()]).nullish(),
  reactive_power_setpoint: z
    .record(
      z.discriminatedUnion("type", [
        ReactiveSetpointTanPhi,
        ReactiveSetpointLocalVoltageRegulation,
      ])
    )
    .nullish(),
});
export type TechnicalData = z.infer<typeof TechnicalData>;

export const SiteService = z.enum([
  "work_acceptance_only",
  "commercial_mgmt",
  "full",
]);
export type SiteService = z.infer<typeof SiteService>;

export const SiteStatusHistorySchema = z.object({
  date: z.coerce.date(),
  comment: z.union([z.string(), z.null()]).nullish(),
  status: z.union([SiteStatus, z.null()]).nullish(),
  uuid: z.string().uuid(),
});
export type SiteStatusHistorySchema = z.infer<typeof SiteStatusHistorySchema>;

export const SiteSchema = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: AssetOwnerSiteSchema,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  deliverypoints: z.array(DeliveryPointSchema),
  contact_info_associations: z.array(ContactInfoWithRolesSchema),
  address: z.union([AddressSchema, z.null()]).nullish(),
  technical_data: z.union([TechnicalData, z.null()]).nullish(),
  service: SiteService,
  statuses: z.array(SiteStatusHistorySchema),
  has_passed_performance_test: z.boolean(),
  has_failed_performance_test: z.boolean(),
  has_work_acceptance: z.boolean(),
  is_ready_for_hand_over: z.boolean(),
  is_handed_over: z.boolean(),
  is_self_consumption: z.boolean(),
  has_operation_data: z.boolean(),
  has_rt_data: z.boolean(),
  has_revenue_data: z.boolean(),
  has_budget_data: z.boolean(),
  has_technical_metering_data: z.boolean(),
  has_negative_hours_data: z.boolean(),
});
export type SiteSchema = z.infer<typeof SiteSchema>;

export const SiteSchemaUpdate = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: z.union([RegionSchema, z.null()]).nullish(),
  country: z.union([CountrySchema, z.null()]).nullish(),
  portfolio: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  technical_data: z.union([TechnicalData, z.null()]).nullish(),
  assetowner: z.union([z.string(), z.null()]).nullish(),
});
export type SiteSchemaUpdate = z.infer<typeof SiteSchemaUpdate>;

export const is_external = z.union([z.boolean(), z.null()]).nullish();
export type is_external = z.infer<typeof is_external>;

export const statuses = z.union([z.array(SiteStatus), z.null()]).nullish();
export type statuses = z.infer<typeof statuses>;

export const MachineSubType = z.enum([
  "wind_turbine_onshore",
  "wind_turbine_offshore",
  "solar_field_ground_mounted",
  "solar_field_rooftop",
  "solar_field_canopy",
  "hydro_turbine_run_of_river",
  "hydro_turbine_pumped_storage",
  "hydro_turbine_reservoir",
  "cogeneration_biomass",
  "cogeneration_waste",
  "cogeneration_other",
  "consumption_site_industry_retail",
  "battery_default",
]);
export type MachineSubType = z.infer<typeof MachineSubType>;

export const technos = z.union([z.array(MachineSubType), z.null()]).nullish();
export type technos = z.infer<typeof technos>;

export const portfolio_names = z
  .union([z.array(z.string()), z.null()])
  .nullish();
export type portfolio_names = z.infer<typeof portfolio_names>;

export const SiteContractStatus = z.enum([
  "market_access",
  "feed_in_tarif",
  "contract_for_difference",
  "ppa",
  "lease",
  "maintenance",
  "supply",
  "balancing",
  "supply_energy",
  "energy_efficiency",
  "new_project",
  "not_contracted",
]);
export type SiteContractStatus = z.infer<typeof SiteContractStatus>;

export const contract_statuses = z
  .union([z.array(SiteContractStatus), z.null()])
  .nullish();
export type contract_statuses = z.infer<typeof contract_statuses>;

export const commissioning_from = z.coerce.date().nullable().nullish();
export type commissioning_from = z.infer<typeof commissioning_from>;

export const site_types = z.union([z.array(SiteType), z.null()]).nullish();
export type site_types = z.infer<typeof site_types>;

export const PastillaName = z.enum([
  "power_production",
  "availability",
  "data_health",
  "pr",
]);
export type PastillaName = z.infer<typeof PastillaName>;

export const PastillaStatus = z.enum([
  "missing",
  "missing_configuration",
  "good",
  "warning",
  "error",
  "compute_error",
  "skipped",
  "na",
]);
export type PastillaStatus = z.infer<typeof PastillaStatus>;

export const PastillaSchemaShort = z.object({
  name: PastillaName,
  status: PastillaStatus,
  gauge_value: z.union([z.number(), z.null()]).nullish(),
  budget_value: z.union([z.number(), z.null()]).nullish(),
  last_update: z.coerce.date(),
  last_status_change: z.coerce.date(),
  comment: z.union([z.string(), z.null()]).nullish(),
});
export type PastillaSchemaShort = z.infer<typeof PastillaSchemaShort>;

export const SiteSchemaOverview = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: CompanySchemaShort,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  cached_pastilla_values: z.record(PastillaSchemaShort),
  contract_status: z.array(SiteContractStatus),
  dp_codes: z.array(z.string()),
  dp_references: z.array(z.string()),
  dso: z.union([DSOSchema, z.null()]).nullish(),
});
export type SiteSchemaOverview = z.infer<typeof SiteSchemaOverview>;

export const WindTurbineSchemaCreate = z.object({
  name: z.string(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  hub_height: z.union([z.number(), z.null()]).nullish(),
  model: z.string(),
  manufacturer: z.string(),
});
export type WindTurbineSchemaCreate = z.infer<typeof WindTurbineSchemaCreate>;

export const SolarFieldSchemaCreate = z.object({
  name: z.string(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  type: SolarMountingType,
  capacity: z.number(),
  tracking: SolarTrackingType,
  tilt: z.number(),
  orientation: z.number(),
  orientation_reference: OrientationReference,
  panel_capacity: z.union([z.number(), z.null()]).nullish(),
});
export type SolarFieldSchemaCreate = z.infer<typeof SolarFieldSchemaCreate>;

export const HydroTurbineSchemaCreate = z.object({
  name: z.string(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  type: HydroType,
  capacity: z.number(),
});
export type HydroTurbineSchemaCreate = z.infer<typeof HydroTurbineSchemaCreate>;

export const CogenerationSchemaCreate = z.object({
  name: z.string(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  type: CogeType,
  capacity: z.number(),
});
export type CogenerationSchemaCreate = z.infer<typeof CogenerationSchemaCreate>;

export const ConsumerSchemaCreate = z.object({
  name: z.string(),
  latitude: z.union([z.number(), z.null()]).nullish(),
  longitude: z.union([z.number(), z.null()]).nullish(),
  type: ConsumerType,
  capacity: z.number().nullish(),
});
export type ConsumerSchemaCreate = z.infer<typeof ConsumerSchemaCreate>;

export const DeliveryPointSchemaCreate = z.object({
  code: z.union([z.string(), z.null()]).nullish(),
  reference: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  code_kind: DeliveryPointCodeKind,
  type: DeliveryPointType,
  extra: z.union([z.record(z.any()), z.null()]).nullish(),
  network_extra: z.union([EnedisNetworkCfg, z.null()]).nullish(),
  dso: DSOSchema,
  assets: z.array(
    z.union([
      WindTurbineSchemaCreate,
      SolarFieldSchemaCreate,
      HydroTurbineSchemaCreate,
      CogenerationSchemaCreate,
      ConsumerSchemaCreate,
    ])
  ),
});
export type DeliveryPointSchemaCreate = z.infer<
  typeof DeliveryPointSchemaCreate
>;

export const SiteSchemaCreate = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  assetowner_uuid: z.union([z.string(), z.null()]).nullish(),
  deliverypoints: z.array(DeliveryPointSchemaCreate).nullish().default([]),
});
export type SiteSchemaCreate = z.infer<typeof SiteSchemaCreate>;

export const MissingDataError = z.object({
  name: z.literal("MissingDataError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type MissingDataError = z.infer<typeof MissingDataError>;

export const ObjectAlreadyExists = z.object({
  name: z.literal("ObjectAlreadyExists"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type ObjectAlreadyExists = z.infer<typeof ObjectAlreadyExists>;

export const SiteStatusHistorySchemaCreate = z.object({
  date: z.coerce.date().nullable().nullish(),
  comment: z.union([z.string(), z.null()]).nullish(),
  status: z.union([SiteStatus, z.null()]).nullish(),
  site_uuid: z.string().uuid(),
});
export type SiteStatusHistorySchemaCreate = z.infer<
  typeof SiteStatusHistorySchemaCreate
>;

export const SiteStatusHistorySchemaUpdate = z.object({
  date: z.coerce.date().nullable().nullish(),
  comment: z.union([z.string(), z.null()]).nullish(),
  status: z.union([SiteStatus, z.null()]).nullish(),
});
export type SiteStatusHistorySchemaUpdate = z.infer<
  typeof SiteStatusHistorySchemaUpdate
>;

export const freq = z.union([z.string(), z.null()]).nullish().default("1MS");
export type freq = z.infer<typeof freq>;

export const missing_left_idx_tol = z
  .union([z.number(), z.null()])
  .nullish()
  .default(0.5);
export type missing_left_idx_tol = z.infer<typeof missing_left_idx_tol>;

export const IOFormatDF = z.enum(["json", "feather"]);
export type IOFormatDF = z.infer<typeof IOFormatDF>;

export const io_format = z
  .union([IOFormatDF, z.null()])
  .nullish()
  .default("feather");
export type io_format = z.infer<typeof io_format>;

export const QueryTooExpensive = z.object({
  name: z.literal("QueryTooExpensive"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type QueryTooExpensive = z.infer<typeof QueryTooExpensive>;

export const quantiles = z
  .union([z.array(z.number()), z.null()])
  .nullish()
  .default([0.05, 0.95]);
export type quantiles = z.infer<typeof quantiles>;

export const year_range = z.union([z.number(), z.null()]).nullish().default(10);
export type year_range = z.infer<typeof year_range>;

export const extrapolation_window = z
  .union([z.number(), z.null()])
  .nullish()
  .default(14);
export type extrapolation_window = z.infer<typeof extrapolation_window>;

export const freq__2 = z
  .union([z.enum(["5min", "15min", "30min", "1D", "1MS"]), z.null()])
  .nullish();
export type freq__2 = z.infer<typeof freq__2>;

export const unit = z.union([z.string(), z.null()]).nullish();
export type unit = z.infer<typeof unit>;

export const ReactiveValue = z.object({
  index: z.coerce.date(),
  value: z.number(),
});
export type ReactiveValue = z.infer<typeof ReactiveValue>;

export const ReactiveDataFrame = z.object({
  active_power: z.array(ReactiveValue),
  valid_reactive: z.array(ReactiveValue),
  invalid_reactive: z.array(ReactiveValue),
});
export type ReactiveDataFrame = z.infer<typeof ReactiveDataFrame>;

export const ReactiveChartDataOnPeriod = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  setpoint: z.union([
    z.discriminatedUnion("type", [
      ReactiveSetpointTanPhi,
      ReactiveSetpointLocalVoltageRegulation,
    ]),
    z.null(),
  ]),
  observed_tan_phi: z.union([z.number(), z.null()]),
  dataframe: z.union([ReactiveDataFrame, z.null()]),
});
export type ReactiveChartDataOnPeriod = z.infer<
  typeof ReactiveChartDataOnPeriod
>;

export const TSAcquisitionGroup = z.enum([
  "irradiance",
  "wind_speed",
  "index",
  "load_profile",
  "realtime",
  "forecast",
]);
export type TSAcquisitionGroup = z.infer<typeof TSAcquisitionGroup>;

export const TSTargetModel = z.enum([
  "site",
  "asset",
  "deliverypoint",
  "submeter",
  "headmeter",
  "meter",
  "country",
]);
export type TSTargetModel = z.infer<typeof TSTargetModel>;

export const TSTargetSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  model: TSTargetModel,
});
export type TSTargetSchema = z.infer<typeof TSTargetSchema>;

export const TSTagCategory = z.enum([
  "Physical Qty",
  "Way",
  "Maturity",
  "quality",
  "techno",
  "flow",
  "market",
  "coef",
  "coef_freq",
  "signal",
]);
export type TSTagCategory = z.infer<typeof TSTagCategory>;

export const TSTagSchema = z.object({
  code: z.string(),
  name: z.string(),
  category: TSTagCategory,
  rank: z.number().int(),
});
export type TSTagSchema = z.infer<typeof TSTagSchema>;

export const TSSchema = z.object({
  name: z.string(),
  freq: z.string(),
  unit: z.string(),
  target: TSTargetSchema,
  tags: z.array(TSTagSchema),
  active: z.boolean(),
  sp_code: z.union([z.record(z.any()), z.null()]).nullish(),
});
export type TSSchema = z.infer<typeof TSSchema>;

export const TimeseriesGroup = z.object({
  group: TSAcquisitionGroup,
  timeseries: z.array(TSSchema),
  backfillable: z.boolean(),
});
export type TimeseriesGroup = z.infer<typeof TimeseriesGroup>;

export const SiteCollectedTimeseries = z.object({
  sp_name: z.union([z.string(), z.null()]),
  available_data: z.array(TimeseriesGroup),
});
export type SiteCollectedTimeseries = z.infer<typeof SiteCollectedTimeseries>;

export const TSReadableName = z.enum(["daily_index", "monthly_load"]);
export type TSReadableName = z.infer<typeof TSReadableName>;

export const TSWay = z.enum([
  "consumption",
  "production",
  "injection",
  "autoconsumption",
]);
export type TSWay = z.infer<typeof TSWay>;

export const SiteEditableTimeseries = z.object({
  ts_name: z.string(),
  readable_name: TSReadableName,
  way: TSWay,
  target: TSTargetSchema,
  tz: z.string(),
  unit: z.string(),
  freq: z.string(),
  active: z.boolean(),
  sp_code: z.union([z.record(z.any()), z.null()]).nullish(),
});
export type SiteEditableTimeseries = z.infer<typeof SiteEditableTimeseries>;

export const SiteMetering = z.object({
  dso_names: z.array(z.string()),
  collected_data: z.array(SiteCollectedTimeseries),
  editable_timeseries: z.array(SiteEditableTimeseries),
});
export type SiteMetering = z.infer<typeof SiteMetering>;

export const BudgetScope = z.enum(["operational", "business_plan"]);
export type BudgetScope = z.infer<typeof BudgetScope>;

export const Percentile = z.enum(["p50", "p90"]);
export type Percentile = z.infer<typeof Percentile>;

export const BudgetSchema = z.object({
  uuid: z.string().uuid(),
  creation_date: z.coerce.date(),
  scope: BudgetScope,
  p50: z.union([z.number(), z.null()]).nullish(),
  p90: z.union([z.number(), z.null()]).nullish(),
  preferred_percentile: Percentile,
  target_pr: z.union([z.number(), z.null()]).nullish(),
  installed_capacity: z.number(),
  availability_rate: z.number(),
  inflation_rate: z.number(),
  yearly_degradation_rate: z.number(),
  energy_reference_year: z.union([z.number(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  prefer_budget_tarif: z.boolean(),
  performance_seasonality: z.union([z.array(z.number()), z.null()]).nullish(),
  energy_seasonality: z.union([z.array(z.number()), z.null()]).nullish(),
  description: z.union([z.string(), z.null()]).nullish(),
});
export type BudgetSchema = z.infer<typeof BudgetSchema>;

export const BusinessPlanSchema = z.object({
  target_pr: z.union([z.number(), z.null()]).nullish(),
});
export type BusinessPlanSchema = z.infer<typeof BusinessPlanSchema>;

export const CommissioningGroupName = z.enum([
  "currently_testing",
  "test_passed",
  "handed_over",
]);
export type CommissioningGroupName = z.infer<typeof CommissioningGroupName>;

export const CommissioningSubgroupName = z.enum([
  "missing_data",
  "test_failed",
  "not_tested_yet",
  "one_month_failed",
  "two_months_failed",
  "perf_test_late",
  "perf_test_on_time",
  "unclassified",
]);
export type CommissioningSubgroupName = z.infer<
  typeof CommissioningSubgroupName
>;

export const CommissioningSite = z.object({
  missing_data: z.boolean(),
  number_perf_test_failed: z.union([z.number(), z.null()]).nullish(),
  month_pr_reached: z.coerce.date().nullable().nullish(),
  max_pr_reached: z.union([z.number(), z.null()]).nullish(),
  pr: z.record(z.union([z.number(), z.null()])).nullish(),
  target_pr: z.number(),
  name: z.string(),
  uuid: z.string().uuid(),
  external_id: z.string(),
  tz: z.string(),
  capacity: z.number(),
  machine_type: z.string(),
  assetowner: CompanySchemaShort,
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  commissioning: z.coerce.date(),
  status: SiteStatus,
  statuses: z.array(SiteStatusHistorySchema),
  has_passed_performance_test: z.boolean(),
  has_failed_performance_test: z.boolean(),
  has_work_acceptance: z.boolean(),
  is_handed_over: z.boolean(),
  business_plan_budget: z.union([BusinessPlanSchema, z.null()]).nullish(),
  group: CommissioningGroupName,
  subgroup: CommissioningSubgroupName,
});
export type CommissioningSite = z.infer<typeof CommissioningSite>;

export const CommissioningComputationError = z.object({
  name: z.literal("CommissioningComputationError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type CommissioningComputationError = z.infer<
  typeof CommissioningComputationError
>;

export const DuplicateSiteStatusError = z.object({
  name: z.literal("DuplicateSiteStatusError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type DuplicateSiteStatusError = z.infer<typeof DuplicateSiteStatusError>;

export const SiteNotReadyForHandOverError = z.object({
  name: z.literal("SiteNotReadyForHandOverError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type SiteNotReadyForHandOverError = z.infer<
  typeof SiteNotReadyForHandOverError
>;

export const SupplyEnergySource = z.enum(["electricity", "gas"]);
export type SupplyEnergySource = z.infer<typeof SupplyEnergySource>;

export const EETarifRecord = z.object({
  contract_year: z.number().int().gte(1),
  energy: SupplyEnergySource,
  tarif: z.number().gt(0),
  source: z.enum(["invoices", "manual"]).nullish().default("manual"),
});
export type EETarifRecord = z.infer<typeof EETarifRecord>;

export const TarifBounds = z.object({ min: z.number(), max: z.number() });
export type TarifBounds = z.infer<typeof TarifBounds>;

export const SupplyInvoiceYear = z.object({
  clause_uuid: z.string().uuid(),
  contract_year: z.number().int(),
  energy: SupplyEnergySource,
  start: z.coerce.date(),
  end: z.coerce.date(),
  covered_months: z.number().int(),
  missing_months: z.array(z.coerce.date()),
  consumption: z.union([z.number(), z.null()]),
  total_amount: z.union([z.number(), z.null()]),
  average_tarif: z.union([z.number(), z.null()]),
  proposed_tarif: z.union([z.number(), z.null()]),
  tarif_record: z.union([EETarifRecord, z.null()]),
  requires_manual_validation: z.boolean(),
  billed_amount: z.number(),
  billed_month_count: z.number().int(),
  due_tarif: z.number(),
  due_tarif_year: z.number().int(),
  due_consumption: z.number(),
  due_amount: z.number(),
  to_regularise: z.number(),
  tarif_bounds: z.union([TarifBounds, z.null()]),
});
export type SupplyInvoiceYear = z.infer<typeof SupplyInvoiceYear>;

export const FixedRedevanceYear = z.object({
  clause_uuid: z.string().uuid(),
  contract_year: z.number().int(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  redevance: z.number(),
  billed_amount: z.number(),
  billed_month_count: z.number().int(),
  due_amount: z.number(),
  to_regularise: z.number(),
});
export type FixedRedevanceYear = z.infer<typeof FixedRedevanceYear>;

export const InvoiceStatus = z.enum([
  "error",
  "incomplete",
  "computed",
  "waiting_for_publication",
  "capped",
  "informative",
  "draft",
  "published",
  "validated",
  "waiting",
  "submitting",
  "submitted",
  "payment",
  "rejected",
  "production_null",
  "canceled_by_credit_note",
  "to_validate",
  "to_redo",
  "paid",
  "deleted",
  "e_invoice_transmitted",
  "pdf_generation_failed",
  "e_invoice_generation_failed",
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

export const SupplyInvoiceProvenance = z.enum(["anthracit", "manual_fix"]);
export type SupplyInvoiceProvenance = z.infer<typeof SupplyInvoiceProvenance>;

export const SupplyInvoiceExclusionReason = z.enum([
  "duplicate",
  "premium",
  "supplier_change",
  "other",
]);
export type SupplyInvoiceExclusionReason = z.infer<
  typeof SupplyInvoiceExclusionReason
>;

export const SupplyInvoiceReviewReason = z.enum([
  "overlapping_period",
  "credit_note",
]);
export type SupplyInvoiceReviewReason = z.infer<
  typeof SupplyInvoiceReviewReason
>;

export const SupplyInvoiceLineSchema = z.object({
  uuid: z.string().uuid(),
  deliverypoint_uuid: z.string().uuid(),
  deliverypoint_reference: z.union([z.string(), z.null()]),
  energy: SupplyEnergySource,
  start: z.coerce.date(),
  end: z.coerce.date(),
  month: z.coerce.date(),
  contract_year: z.union([z.number(), z.null()]),
  quantity: z.number(),
  quantity_unit: z.string(),
  amount: z.number(),
  vat_rate: z.union([z.number(), z.null()]),
});
export type SupplyInvoiceLineSchema = z.infer<typeof SupplyInvoiceLineSchema>;

export const SupplyInvoiceSchema = z.object({
  uuid: z.string().uuid(),
  document_uuid: z.string().uuid(),
  filename: z.string(),
  supplier_name: z.string(),
  publication_date: z.coerce.date(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  month: z.coerce.date(),
  contract_year: z.union([z.number(), z.null()]),
  total_quantity: z.number(),
  total_amount: z.number(),
  total_vat_amount: z.number(),
  status: InvoiceStatus,
  provenance: z.union([SupplyInvoiceProvenance, z.null()]),
  excluded: z.boolean(),
  excluded_reason: z.union([SupplyInvoiceExclusionReason, z.null()]),
  review_reasons: z.array(SupplyInvoiceReviewReason),
  lines: z.array(SupplyInvoiceLineSchema),
});
export type SupplyInvoiceSchema = z.infer<typeof SupplyInvoiceSchema>;

export const SiteSupplyInvoices = z.object({
  years: z.array(SupplyInvoiceYear),
  fixed_redevance_years: z.array(FixedRedevanceYear),
  invoices: z.array(SupplyInvoiceSchema),
});
export type SiteSupplyInvoices = z.infer<typeof SiteSupplyInvoices>;

export const SupplyInvoiceLineUpdate = z.object({
  uuid: z.string().uuid(),
  deliverypoint_uuid: z.union([z.string(), z.null()]).nullish(),
  energy: z.union([SupplyEnergySource, z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  quantity: z.union([z.number(), z.null()]).nullish(),
  amount: z.union([z.number(), z.null()]).nullish(),
  vat_rate: z.union([z.number(), z.null()]).nullish(),
});
export type SupplyInvoiceLineUpdate = z.infer<typeof SupplyInvoiceLineUpdate>;

export const SupplyInvoiceUpdate = z.object({
  excluded_reason: z.union([SupplyInvoiceExclusionReason, z.null()]).nullish(),
  lines: z.array(SupplyInvoiceLineUpdate).nullish().default([]),
});
export type SupplyInvoiceUpdate = z.infer<typeof SupplyInvoiceUpdate>;

export const InvalidSupplyInvoiceUpdate = z.object({
  name: z.literal("InvalidSupplyInvoiceUpdate"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvalidSupplyInvoiceUpdate = z.infer<
  typeof InvalidSupplyInvoiceUpdate
>;

export const InvoiceSource = z.enum([
  "renewex",
  "renewex_with_buyer_input",
  "buyer",
  "buyer_with_rnx_input",
  "assetmanager",
]);
export type InvoiceSource = z.infer<typeof InvoiceSource>;

export const CompanyCloudImages = z.object({
  logo: z.union([CloudImageUrl, z.null()]).nullish(),
  stamp: z.union([CloudImageUrl, z.null()]).nullish(),
});
export type CompanyCloudImages = z.infer<typeof CompanyCloudImages>;

export const InvoiceCompanySchema = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
});
export type InvoiceCompanySchema = z.infer<typeof InvoiceCompanySchema>;

export const InvoiceSiteDetailsSchema = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: InvoiceCompanySchema,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  dp_codes: z.array(z.string()),
});
export type InvoiceSiteDetailsSchema = z.infer<typeof InvoiceSiteDetailsSchema>;

export const PriceMechanism = z.enum([
  "market_access",
  "feed_in_tarif",
  "contract_for_difference",
  "ppa",
  "supply",
  "balancing",
  "lease",
  "maintenance",
  "supply_energy",
  "energy_efficiency",
]);
export type PriceMechanism = z.infer<typeof PriceMechanism>;

export const LIndexVersionDesc = z.enum([
  "YS-JAN",
  "YS-FEB",
  "YS-MAR",
  "YS-APR",
  "YS-MAY",
  "YS-JUN",
  "YS-JUL",
  "YS-AUG",
  "YS-SEP",
  "YS-OCT",
  "YS-NOV",
  "YS-DEC",
  "YS-BIRTHDAY",
]);
export type LIndexVersionDesc = z.infer<typeof LIndexVersionDesc>;

export const InseeIndex = z.enum([
  "FM0ABE",
  "ICHT",
  "BT47",
  "IPC",
  "TCH",
  "ICC",
  "IPC_HT",
]);
export type InseeIndex = z.infer<typeof InseeIndex>;

export const IndexCoefficient = z.object({
  index: InseeIndex,
  coefficient: z.union([z.number(), z.null()]).nullish(),
});
export type IndexCoefficient = z.infer<typeof IndexCoefficient>;

export const LIndexCfg = z.object({
  month_base: LIndexVersionDesc,
  on_birthday: z.boolean(),
  retroactive: z.boolean().nullish(),
  defaults: z.array(IndexCoefficient).nullish(),
});
export type LIndexCfg = z.infer<typeof LIndexCfg>;

export const ContractTypeSchema = z.object({
  name: z.string(),
  price_mechanism: PriceMechanism,
  vat_rate: z.number(),
  vat_term: z.union([z.string(), z.null()]),
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  is_revenue: z.boolean(),
});
export type ContractTypeSchema = z.infer<typeof ContractTypeSchema>;

export const LegalEntityKind = z.enum([
  "person",
  "company",
  "assetowner",
  "assetmanager",
  "holding",
  "offtaker",
  "tso",
  "dso",
]);
export type LegalEntityKind = z.infer<typeof LegalEntityKind>;

export const InvoicePersonSchema = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.string(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  uuid: z.string().uuid(),
  kind: LegalEntityKind,
  name: z.string(),
  address: z.union([AddressSchema, z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
});
export type InvoicePersonSchema = z.infer<typeof InvoicePersonSchema>;

export const ToleranceMode = z.enum(["absolute", "relative"]);
export type ToleranceMode = z.infer<typeof ToleranceMode>;

export const ToleranceCfg = z.object({
  lower: z.union([z.number(), z.null()]).nullish(),
  upper: z.union([z.number(), z.null()]).nullish(),
  mode: ToleranceMode.nullish(),
});
export type ToleranceCfg = z.infer<typeof ToleranceCfg>;

export const InvoiceToleranceCfg = z.object({
  amount: ToleranceCfg.nullish(),
  quantity: ToleranceCfg.nullish(),
});
export type InvoiceToleranceCfg = z.infer<typeof InvoiceToleranceCfg>;

export const InvoicingToleranceCfg = z.object({
  computation: z.union([InvoiceToleranceCfg, z.null()]).nullish(),
  validation: z.union([InvoiceToleranceCfg, z.null()]).nullish(),
});
export type InvoicingToleranceCfg = z.infer<typeof InvoicingToleranceCfg>;

export const AssetManagerLegalEntityLinkSchema = z.object({
  uuid: z.string().uuid(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
  invoicing_tolerance_cfg: InvoicingToleranceCfg,
});
export type AssetManagerLegalEntityLinkSchema = z.infer<
  typeof AssetManagerLegalEntityLinkSchema
>;

export const InvoiceSubmitChannel = z.enum([
  "email",
  "platform",
  "cegedim",
  "generix",
  "pdf",
  "disabled",
  "excel",
]);
export type InvoiceSubmitChannel = z.infer<typeof InvoiceSubmitChannel>;

export const ContactEmailSchema = z.object({
  name: z.union([z.string(), z.null()]),
  email: z.union([z.string(), z.null()]),
});
export type ContactEmailSchema = z.infer<typeof ContactEmailSchema>;

export const EmailContactsSchema = z.object({
  from_email: z.union([ContactEmailSchema, z.null()]),
  to_email: z.union([ContactEmailSchema, z.null()]),
  cc_emails: z.array(ContactEmailSchema),
  bcc_emails: z.array(ContactEmailSchema).nullish().default([]),
});
export type EmailContactsSchema = z.infer<typeof EmailContactsSchema>;

export const InvoiceContractSchema = z.object({
  uuid: z.string().uuid(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  type: ContractTypeSchema,
  buyer: z.union([InvoicePersonSchema, InvoiceCompanySchema]),
  seller: z.union([InvoicePersonSchema, InvoiceCompanySchema]),
  buyer_accounting_infos: z
    .union([AssetManagerLegalEntityLinkSchema, z.null()])
    .nullish(),
  invoice_submit_channel: InvoiceSubmitChannel,
  invoice_source: InvoiceSource,
  is_synchronized: z.boolean(),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  invoicing_on_hold_reasons: z
    .union([z.array(OnHoldReason), z.null()])
    .nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  vat_rate: z.number(),
  email_contacts: EmailContactsSchema,
  offtaker: z.union([InvoicePersonSchema, InvoiceCompanySchema]),
});
export type InvoiceContractSchema = z.infer<typeof InvoiceContractSchema>;

export const InvoiceExternalStatus = z.enum([
  "matched",
  "energy_mismatch",
  "price_mismatch",
  "energy_and_price_mismatch",
  "missing_external_lines",
  "missing_internal_lines",
]);
export type InvoiceExternalStatus = z.infer<typeof InvoiceExternalStatus>;

export const InvoiceAction = z.enum(["credit_note", "reject", "accept"]);
export type InvoiceAction = z.infer<typeof InvoiceAction>;

export const ClauseProduct = z.enum([
  "energy",
  "energy_temporary",
  "energy_final",
  "energy_non_injected",
  "m0_deviation",
  "energy_winter_base",
  "energy_winter_peak",
  "energy_winter_super_peak",
  "energy_winter_off_peak",
  "energy_summer_base",
  "energy_summer_peak",
  "energy_summer_off_peak",
  "energy_base",
  "energy_compensed",
  "energy_penalized",
  "livestock_effluents",
  "quality_majoration",
  "production_majoration",
  "management_fee",
  "spot_management_fee",
  "steering",
  "energy_above",
  "energy_below",
  "neg_price_compensation",
  "energy_covered_peak",
  "energy_covered_off_peak",
  "energy_uncovered_peak",
  "energy_uncovered_off_peak",
  "energy_covered",
  "energy_uncovered",
  "energy_off_peak",
  "energy_peak",
  "ft_cost",
  "majoration",
  "management_bonus",
  "participatory_investment_majoration",
  "negative_price_penalty",
  "null_production_compensation",
  "incorrect_stop_request",
  "undelivered_energy_penalty",
  "energy_positive_prices",
  "energy_negative_prices",
  "guarantees_of_origin",
  "flow_tracking",
  "guarantees_of_capacity",
  "gc_certification_fee",
  "gc_management_fee",
  "credit_note",
  "deferment",
  "energy_injected",
  "energy_autoconsumed",
  "license_fee",
  "bonus_malus",
  "settlement",
  "injection_penalty",
  "unavailability_penalty",
  "excise_tax",
  "replacement",
  "profit_sharing",
  "corrected_energy",
  "energy_guarantee",
  "energy_guarantee_risk_premium",
  "maintenance",
  "indexation_regulation",
  "lease_product",
  "ee_redevance",
  "supply_energy",
  "total",
  "production_capacity_deduction",
  "penalty",
  "activation_amount",
  "bm_capacity_reservation",
  "bm_production_compensation",
  "bm_activation_compensation",
  "smart_oa_compensation",
  "octroi_de_mer",
]);
export type ClauseProduct = z.infer<typeof ClauseProduct>;

export const ClauseSchemaShort = z.object({
  uuid: z.string().uuid(),
  type: z.string(),
});
export type ClauseSchemaShort = z.infer<typeof ClauseSchemaShort>;

export const DeliveryPointInvoiceLine = z.object({
  kind: z.literal("deliverypoint"),
  uuid: z.string().uuid(),
  code: z.union([z.string(), z.null()]).nullish(),
  prm: z.union([z.string(), z.null()]).nullish(),
});
export type DeliveryPointInvoiceLine = z.infer<typeof DeliveryPointInvoiceLine>;

export const SiteInvoiceLine = z.object({
  kind: z.literal("site"),
  uuid: z.string().uuid(),
  name: z.string(),
});
export type SiteInvoiceLine = z.infer<typeof SiteInvoiceLine>;

export const AssetOwnerInvoiceLine = z.object({
  uuid: z.string().uuid(),
  kind: z.literal("assetowner"),
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
});
export type AssetOwnerInvoiceLine = z.infer<typeof AssetOwnerInvoiceLine>;

export const ContractInvoiceLine = z.object({
  kind: z.literal("contract"),
  uuid: z.string().uuid(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
});
export type ContractInvoiceLine = z.infer<typeof ContractInvoiceLine>;

export const InvoiceLineSchema = z.object({
  uuid: z.string().uuid(),
  product: ClauseProduct,
  description_text: z.union([z.string(), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  quantity: z.number(),
  price: z.number(),
  amount: z.number(),
  vat_rate: z.union([z.number(), z.null()]).nullish(),
  vat_amount: z.union([z.number(), z.null()]).nullish(),
  amount_including_vat: z.number(),
  quantity_unit: z.string(),
  is_external: z.boolean(),
  is_summable: z.boolean(),
  is_regulation: z.boolean(),
  is_credit_note: z.boolean(),
  clause: ClauseSchemaShort,
  target: z.discriminatedUnion("kind", [
    DeliveryPointInvoiceLine,
    SiteInvoiceLine,
    AssetOwnerInvoiceLine,
    ContractInvoiceLine,
  ]),
});
export type InvoiceLineSchema = z.infer<typeof InvoiceLineSchema>;

export const InvoiceLogAction = z.object({
  date: z.coerce.date(),
  status: z.union([InvoiceStatus, z.null()]).nullish(),
  action: z.union([z.string(), z.null()]).nullish(),
  initiator: z.union([z.string(), z.null()]).nullish(),
  comments: z.union([z.array(z.string()), z.null()]).nullish(),
});
export type InvoiceLogAction = z.infer<typeof InvoiceLogAction>;

export const InseeIndexComputationDetails = z.object({
  name: InseeIndex,
  coef: z.number(),
  rebased: z.number(),
  rebase_coefs: z.record(z.number()),
  fraction: z.number(),
  latest_value: z.number(),
  initial_value: z.number(),
  base_year: z.number().int(),
  published_version: z.coerce.date(),
});
export type InseeIndexComputationDetails = z.infer<
  typeof InseeIndexComputationDetails
>;

export const LIndexComputationDetails = z.object({
  version: z.coerce.date(),
  L: z.number(),
  map_name_indexes: z.record(InseeIndexComputationDetails),
});
export type LIndexComputationDetails = z.infer<typeof LIndexComputationDetails>;

export const InvoiceCoefDetails = z.object({
  L_details: z.union([LIndexComputationDetails, z.null()]).nullish(),
  indexed_tarif: z.union([z.number(), z.null()]).nullish(),
  M0: z.union([z.number(), z.null()]).nullish(),
  credit_note_cap: z.union([z.number(), z.null()]).nullish(),
  cumulated_deferment: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceCoefDetails = z.infer<typeof InvoiceCoefDetails>;

export const InvoicingMode = z.enum(["index", "load_profile"]);
export type InvoicingMode = z.infer<typeof InvoicingMode>;

export const MarketPriceType = z.enum([
  "fix_price",
  "epex_spot",
  "imbalance_short",
  "imbalance_long",
]);
export type MarketPriceType = z.infer<typeof MarketPriceType>;

export const TarifType = z.object({
  price_type: MarketPriceType,
  fix_price: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type TarifType = z.infer<typeof TarifType>;

export const _HydroTarifBase = z.object({
  tarif: z.number(),
  majoration: z.union([z.number(), z.null()]).nullish(),
  uncovered_tarif: z.union([z.record(z.any()), z.null()]).nullish(),
});
export type _HydroTarifBase = z.infer<typeof _HydroTarifBase>;

export const HydroTarifComponents = z.object({
  winter_base: z.union([_HydroTarifBase, z.null()]).nullish(),
  winter_off_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
  winter_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
  summer_base: z.union([_HydroTarifBase, z.null()]).nullish(),
  summer_off_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
  summer_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
  base_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
  base_off_peak: z.union([_HydroTarifBase, z.null()]).nullish(),
});
export type HydroTarifComponents = z.infer<typeof HydroTarifComponents>;

export const ACCProductionSiteInfos = z.object({
  name: z.string(),
  prm: z.string(),
  acc_prod: z.number(),
  consumed_by_client: z.number(),
});
export type ACCProductionSiteInfos = z.infer<typeof ACCProductionSiteInfos>;

export const InvoiceClauseDetails = z.object({
  tarif: z.union([z.number(), z.null()]).nullish(),
  energy_threshold: z.union([z.number(), z.null()]).nullish(),
  tarif_above_threshold: z.union([z.number(), z.null()]).nullish(),
  tarif_winter: z.union([z.number(), z.null()]).nullish(),
  invoicing_mode: z.union([InvoicingMode, z.null()]).nullish(),
  octroi_de_mer_rate: z.union([z.number(), z.null()]).nullish(),
  fix_price: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_coef: z.union([z.number(), z.null()]).nullish(),
  majoration: z.union([z.number(), z.null()]).nullish(),
  coef_k: z.union([z.number(), z.null()]).nullish(),
  management_bonus: z.union([z.number(), z.null()]).nullish(),
  formula: z.union([z.string(), z.null()]).nullish(),
  power_threshold: z.union([z.number(), z.null()]).nullish(),
  below_tarif: z.union([z.number(), TarifType, z.null()]).nullish(),
  above_tarif: z.union([z.number(), TarifType, z.null()]).nullish(),
  tarif_components: z.union([HydroTarifComponents, z.null()]).nullish(),
  max_quality_majoration: z.union([z.number(), z.null()]).nullish(),
  quality_majoration: z.union([z.number(), z.null()]).nullish(),
  quality_coefficient: z.union([z.number(), z.null()]).nullish(),
  deliverypoint_references: z
    .union([z.array(z.union([z.string(), z.null()])), z.null()])
    .nullish(),
  pae: z.union([z.number(), z.null()]).nullish(),
  spot_prices_coefficient: z.union([z.number(), z.null()]).nullish(),
  premium_autoconso: z.union([z.number(), z.null()]).nullish(),
  cumulated_actualisation_rate: z.union([z.number(), z.null()]).nullish(),
  contractual_amount: z.union([z.number(), z.null()]).nullish(),
  actualised_amount: z.union([z.number(), z.null()]).nullish(),
  bonus_malus: z.union([z.number(), z.null()]).nullish(),
  autoconsumption_rate: z.union([z.number(), z.null()]).nullish(),
  vat_amount: z.union([z.number(), z.null()]).nullish(),
  price: z.union([z.number(), z.null()]).nullish(),
  number_of_spot_negative_hours: z.union([z.number(), z.null()]).nullish(),
  number_of_spot_negative_hours_with_production: z
    .union([z.number(), z.null()])
    .nullish(),
  number_of_spot_without_production: z.union([z.number(), z.null()]).nullish(),
  penalty_way_coefficient: z.union([z.number(), z.null()]).nullish(),
  neg_price_threshold: z.union([z.number(), z.null()]).nullish(),
  neg_prices_source: z.union([z.string(), z.null()]).nullish(),
  deductible_hours: z.union([z.number(), z.null()]).nullish(),
  compensated_hours: z.union([z.string(), z.null()]).nullish(),
  deductible_base: z.union([z.string(), z.null()]).nullish(),
  price_tolerance_threshold: z.union([z.number(), z.null()]).nullish(),
  capacity_threshold_ratio: z.union([z.number(), z.null()]).nullish(),
  availability_threshold: z.union([z.number(), z.null()]).nullish(),
  availability_rate: z.union([z.number(), z.null()]).nullish(),
  injection_penalty: z.union([z.number(), z.null()]).nullish(),
  max_injected_capa_value: z.union([z.number(), z.null()]).nullish(),
  effluents_tarif: z.union([z.number(), z.null()]).nullish(),
  effluents_rate: z.union([z.number(), z.null()]).nullish(),
  effluents_rate_threshold: z.union([z.number(), z.null()]).nullish(),
  ft_cost: z.union([z.number(), z.null()]).nullish(),
  discount_rate: z.union([z.number(), z.null()]).nullish(),
  comparison_amount: z.union([z.number(), z.null()]).nullish(),
  positive_imbalance_coef: z.union([z.number(), z.null()]).nullish(),
  negative_imbalance_coef: z.union([z.number(), z.null()]).nullish(),
  monthly_coef: z.union([z.number(), z.null()]).nullish(),
  excise_tax: z.union([z.number(), z.null()]).nullish(),
  acc_id: z.union([z.string(), z.null()]).nullish(),
  acc_details: z.union([z.array(ACCProductionSiteInfos), z.null()]).nullish(),
  rule: z.union([MarketPriceType, z.null()]).nullish(),
  disconnection_hours: z.union([z.number(), z.null()]).nullish(),
  production_on_disconnected_hours: z.union([z.number(), z.null()]).nullish(),
  invoiced_energy_since_birthday: z.union([z.number(), z.null()]).nullish(),
  has_lease_increased: z.union([z.boolean(), z.null()]).nullish(),
  smart_oa_nb_episodes: z.union([z.number(), z.null()]).nullish(),
  smart_oa_nb_hours_compensated: z.union([z.number(), z.null()]).nullish(),
  smart_oa_double_extension: z.union([z.boolean(), z.null()]).nullish(),
  smart_oa_uncounted_cap_kwh: z.union([z.number(), z.null()]).nullish(),
  smart_oa_uncounted_dafr_kwh: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceClauseDetails = z.infer<typeof InvoiceClauseDetails>;

export const InvoiceIndexInfo = z.object({
  value: z.number().int(),
  is_interpolated: z.boolean().nullish(),
  is_extrapolated: z.boolean().nullish(),
});
export type InvoiceIndexInfo = z.infer<typeof InvoiceIndexInfo>;

export const MeterChangeSource = z.enum(["estimated", "dso"]);
export type MeterChangeSource = z.infer<typeof MeterChangeSource>;

export const InvoiceIndexMeterChange = z.object({
  date: z.coerce.date(),
  old_meter_removal: z.number(),
  new_meter_install: z.number(),
  source: MeterChangeSource.nullish(),
});
export type InvoiceIndexMeterChange = z.infer<typeof InvoiceIndexMeterChange>;

export const InvoiceIndex = z.object({
  start: z.union([InvoiceIndexInfo, z.null()]).nullish(),
  end: z.union([InvoiceIndexInfo, z.null()]).nullish(),
  capacity_change: z.union([InvoiceIndexInfo, z.null()]).nullish(),
  meter_changes: z.array(InvoiceIndexMeterChange).nullish(),
  value: z.union([z.number(), z.null()]).nullish(),
  is_reset: z.boolean().nullish(),
});
export type InvoiceIndex = z.infer<typeof InvoiceIndex>;

export const InvoiceExtraDetails = z.object({
  prod_index: InvoiceIndex.nullish(),
  conso_index: InvoiceIndex.nullish(),
  prod: z.union([z.number(), z.null()]).nullish(),
  conso: z.union([z.number(), z.null()]).nullish(),
  prorata_coef: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceExtraDetails = z.infer<typeof InvoiceExtraDetails>;

export const ClauseTypes = z.enum([
  "cfd_cr_16",
  "cfd_cr_17",
  "cfd_majoration",
  "cfd_autoconso",
  "cfd_neg_price_compensation",
  "cfd_m0_yearly_deviation",
  "ft_solar",
  "ft_solar_trial",
  "ft_wind_monthly",
  "ft_hydro_monthly",
  "ft_biogas_monthly",
  "disconnection_compensation",
  "market_access",
  "market_access_imb",
  "ppa_da",
  "ppa_fix",
  "ppa_threshold",
  "ppa_forecast_hedge",
  "ppa_tarif_components",
  "ppa_threshold_multi_period",
  "ppa_management_fee",
  "ppa_da_indexed_fee",
  "ppa_imbalance_indexed_fee",
  "undelivered_energy_penalty",
  "ppa_asia",
  "ppa_fixed_management_fee",
  "neg_price",
  "spot_management_fee",
  "goo",
  "flow_tracking",
  "gc",
  "credit_note_cap",
  "settlement",
  "autoconso",
  "autoconso_insee_indexed",
  "autoconso_master",
  "collective_autoconsumption",
  "sunflow_acc",
  "energy_guarantee",
  "energy_guarantee_risk_premium",
  "maintenance",
  "lease",
  "ee_tarif",
  "supply_energy",
  "balancing_mechanism",
  "balancing_mechanism_compensation",
  "bm_capacity_reservation",
  "smart_oa_compensation",
]);
export type ClauseTypes = z.infer<typeof ClauseTypes>;

export const ClauseComputationResultDetails = z.object({
  coef: z.union([InvoiceCoefDetails, z.null()]).nullish(),
  clause: z.union([InvoiceClauseDetails, z.null()]).nullish(),
  extra: z.union([InvoiceExtraDetails, z.null()]).nullish(),
  external_extra: z.union([InvoiceExtraDetails, z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  clause_type: ClauseTypes,
});
export type ClauseComputationResultDetails = z.infer<
  typeof ClauseComputationResultDetails
>;

export const DPIndexValue = z.object({
  prod: z.union([z.number(), z.null()]).nullish(),
  cons: z.union([z.number(), z.null()]).nullish(),
});
export type DPIndexValue = z.infer<typeof DPIndexValue>;

export const DPMeterChange = z.object({
  date: z.coerce.date(),
  source: MeterChangeSource.nullish(),
  before: DPIndexValue,
  after: DPIndexValue,
});
export type DPMeterChange = z.infer<typeof DPMeterChange>;

export const PlatformCredentialIdentitySchema = z.object({
  email: z.union([z.string(), z.null()]).nullish(),
});
export type PlatformCredentialIdentitySchema = z.infer<
  typeof PlatformCredentialIdentitySchema
>;

export const InvoiceSchema = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  computable_asof: z.coerce.date(),
  creation_date: z.coerce.date(),
  publication_date: z.coerce.date().nullable().nullish(),
  computation_date: z.coerce.date().nullable().nullish(),
  payment_date: z.coerce.date().nullable().nullish(),
  publication_id: z.union([z.string(), z.null()]).nullish(),
  external_order_number: z.union([z.string(), z.null()]).nullish(),
  status: InvoiceStatus,
  source: z.union([InvoiceSource, z.null()]).nullish(),
  sites: z.array(InvoiceSiteDetailsSchema),
  contract: InvoiceContractSchema,
  is_birthday: z.boolean(),
  is_accounted: z.boolean(),
  is_only_credit_note: z.boolean(),
  has_credit_note_lines: z.boolean(),
  is_internal_credit_note: z.boolean(),
  accounted_date: z.coerce.date().nullable().nullish(),
  accounted_initiator: z.union([z.string(), z.null()]).nullish(),
  external_status: z.union([InvoiceExternalStatus, z.null()]).nullish(),
  action: z.union([InvoiceAction, z.null()]).nullish(),
  has_pdf: z.boolean(),
  has_notes: z.boolean(),
  lines: z.array(InvoiceLineSchema),
  external_lines: z.array(InvoiceLineSchema),
  log_actions: z.array(InvoiceLogAction),
  details: z.array(ClauseComputationResultDetails),
  email_object: z.string(),
  total_vat_amount: z.number(),
  total_amount: z.number(),
  total_amount_including_vat: z.number(),
  meter_changes: z.array(DPMeterChange).nullish(),
  platform_credential: z
    .union([PlatformCredentialIdentitySchema, z.null()])
    .nullish(),
  site: z.union([InvoiceSiteDetailsSchema, z.null()]).nullish(),
  is_credit_note: z.boolean(),
});
export type InvoiceSchema = z.infer<typeof InvoiceSchema>;

export const InvoiceIndexEditSchema = z.object({
  start: z.union([z.number(), z.null()]).nullish(),
  end: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceIndexEditSchema = z.infer<typeof InvoiceIndexEditSchema>;

export const MeterChangeEditSchema = z.object({
  date: z.coerce.date(),
  before: DPIndexValue,
  after: DPIndexValue,
});
export type MeterChangeEditSchema = z.infer<typeof MeterChangeEditSchema>;

export const InvoiceExtraIndexModeEditSchema = z.object({
  prod_index: z.union([z.array(InvoiceIndexEditSchema), z.null()]).nullish(),
  conso_index: z.union([z.array(InvoiceIndexEditSchema), z.null()]).nullish(),
  meter_change: z.union([MeterChangeEditSchema, z.null()]).nullish(),
});
export type InvoiceExtraIndexModeEditSchema = z.infer<
  typeof InvoiceExtraIndexModeEditSchema
>;

export const InvoiceExtraLoadProfileModeEditSchema = z.object({
  prod: z.union([z.number(), z.null()]).nullish(),
  conso: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceExtraLoadProfileModeEditSchema = z.infer<
  typeof InvoiceExtraLoadProfileModeEditSchema
>;

export const InvoiceUpdateSchema = z.object({
  extra: z
    .union([
      InvoiceExtraIndexModeEditSchema,
      InvoiceExtraLoadProfileModeEditSchema,
      z.null(),
    ])
    .nullish(),
});
export type InvoiceUpdateSchema = z.infer<typeof InvoiceUpdateSchema>;

export const InvoiceStatusError = z.object({
  name: z.literal("InvoiceStatusError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoiceStatusError = z.infer<typeof InvoiceStatusError>;

export const MeterChangeInputError = z.object({
  name: z.literal("MeterChangeInputError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type MeterChangeInputError = z.infer<typeof MeterChangeInputError>;

export const InvoiceLineSchemaUpdate = z.object({
  description_text: z.union([z.string(), z.null()]).nullish(),
  amount: z.union([z.number(), z.null()]).nullish(),
  quantity: z.union([z.number(), z.null()]).nullish(),
  price: z.union([z.number(), z.null()]).nullish(),
  quantity_unit: z.union([z.string(), z.null()]).nullish(),
  vat_rate: z.union([z.number(), z.null()]).nullish(),
});
export type InvoiceLineSchemaUpdate = z.infer<typeof InvoiceLineSchemaUpdate>;

export const NoDataFound = z.object({
  name: z.literal("NoDataFound"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type NoDataFound = z.infer<typeof NoDataFound>;

export const Body_get_invoices_pdf_api_invoices_document_post = z.object({
  uuid: z.array(z.string()).max(150),
});
export type Body_get_invoices_pdf_api_invoices_document_post = z.infer<
  typeof Body_get_invoices_pdf_api_invoices_document_post
>;

export const Body_get_invoices_data_api_invoices_excel_post = z.object({
  uuid: z.array(z.string()).max(150),
});
export type Body_get_invoices_data_api_invoices_excel_post = z.infer<
  typeof Body_get_invoices_data_api_invoices_excel_post
>;

export const RegulationFilter = z.enum([
  "has_regulation_lines",
  "is_only_regulation",
  "contains_regulation",
]);
export type RegulationFilter = z.infer<typeof RegulationFilter>;

export const InvoiceQueryFilters = z.object({
  uuids: z.array(z.string()).nullish(),
  portfolio: z.array(z.union([z.string(), z.null()])).nullish(),
  invoice_source: z.array(InvoiceSource).nullish(),
  invoice_submit_channel: z.array(InvoiceSubmitChannel).nullish(),
  buyer: z.array(z.string()).nullish(),
  counterpart: z.array(z.string()).nullish(),
  contract_type: z.array(z.string()).nullish(),
  price_mechanism: z.array(PriceMechanism).nullish(),
  assetowner: z.array(z.string()).nullish(),
  clause_type: z.array(ClauseTypes).nullish(),
  status: z.array(InvoiceStatus).nullish(),
  site_status: z.array(SiteStatus).nullish(),
  regulation_filter: z.union([RegulationFilter, z.null()]).nullish(),
  has_external_lines: z.union([z.boolean(), z.null()]).nullish(),
  invoicing_on_hold_reasons: z
    .array(z.union([OnHoldReason, z.null()]))
    .nullish(),
});
export type InvoiceQueryFilters = z.infer<typeof InvoiceQueryFilters>;

export const InvoiceSiteSchemaShort = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: CompanySchemaShort,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  dp_codes: z.array(z.string()),
});
export type InvoiceSiteSchemaShort = z.infer<typeof InvoiceSiteSchemaShort>;

export const InvoicePersonSchemaShort = z.object({
  uuid: z.string().uuid(),
  kind: LegalEntityKind,
  name: z.string(),
});
export type InvoicePersonSchemaShort = z.infer<typeof InvoicePersonSchemaShort>;

export const InvoiceContractSchemaShort = z.object({
  uuid: z.string().uuid(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  type: ContractTypeSchema,
  buyer: z.union([InvoicePersonSchemaShort, CompanySchemaShort]),
  seller: z.union([InvoicePersonSchemaShort, CompanySchemaShort]),
  buyer_accounting_infos: z
    .union([AssetManagerLegalEntityLinkSchema, z.null()])
    .nullish(),
  invoice_submit_channel: InvoiceSubmitChannel,
  invoice_source: InvoiceSource,
  is_synchronized: z.boolean(),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  invoicing_on_hold_reasons: z
    .union([z.array(OnHoldReason), z.null()])
    .nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
});
export type InvoiceContractSchemaShort = z.infer<
  typeof InvoiceContractSchemaShort
>;

export const InvoiceSchemaShort = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  computable_asof: z.coerce.date(),
  creation_date: z.coerce.date(),
  publication_date: z.coerce.date().nullable().nullish(),
  computation_date: z.coerce.date().nullable().nullish(),
  payment_date: z.coerce.date().nullable().nullish(),
  publication_id: z.union([z.string(), z.null()]).nullish(),
  external_order_number: z.union([z.string(), z.null()]).nullish(),
  status: InvoiceStatus,
  source: z.union([InvoiceSource, z.null()]).nullish(),
  sites: z.array(InvoiceSiteSchemaShort),
  contract: InvoiceContractSchemaShort,
  is_birthday: z.boolean(),
  is_accounted: z.boolean(),
  is_only_credit_note: z.boolean(),
  has_credit_note_lines: z.boolean(),
  is_internal_credit_note: z.boolean(),
  accounted_date: z.coerce.date().nullable().nullish(),
  accounted_initiator: z.union([z.string(), z.null()]).nullish(),
  external_status: z.union([InvoiceExternalStatus, z.null()]).nullish(),
  action: z.union([InvoiceAction, z.null()]).nullish(),
  has_pdf: z.boolean(),
  has_notes: z.boolean(),
  lines: z.array(InvoiceLineSchema),
  external_lines: z.array(InvoiceLineSchema),
  site: z.union([InvoiceSiteSchemaShort, z.null()]).nullish(),
  is_credit_note: z.boolean(),
});
export type InvoiceSchemaShort = z.infer<typeof InvoiceSchemaShort>;

export const GroupInvoiceStatus = z.enum(["warning", "todo", "done"]);
export type GroupInvoiceStatus = z.infer<typeof GroupInvoiceStatus>;

export const InvoicingSubgroup = z.object({
  status: InvoiceStatus,
  invoices_per_month: z.record(z.array(z.string())),
  amount_per_month: z.union([z.record(z.number()), z.null()]).nullish(),
});
export type InvoicingSubgroup = z.infer<typeof InvoicingSubgroup>;

export const InvoicingGroup = z.object({
  group_status: GroupInvoiceStatus,
  children: z.array(InvoicingSubgroup),
});
export type InvoicingGroup = z.infer<typeof InvoicingGroup>;

export const ContractStatus = z.enum([
  "no_status",
  "connection_requested",
  "commissioned",
  "form_filling",
  "under_verification",
  "contract_available",
  "signed",
  "countersigned",
  "litigation",
  "transferred",
  "provisional",
  "cancelled",
  "ended",
  "vendu",
]);
export type ContractStatus = z.infer<typeof ContractStatus>;

export const InvoicesToRecoverContractSchema = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  invoice_source: InvoiceSource,
  invoice_submit_channel: InvoiceSubmitChannel,
  is_synchronized: z.boolean(),
  buyer: z.string(),
  seller: z.string(),
  is_revenue: z.boolean(),
  contract_type: z.string(),
  price_mechanism: PriceMechanism,
  nb_invoices: z.number().int(),
  current_status: ContractStatus,
  purchase_order: z.union([z.string(), z.null()]),
});
export type InvoicesToRecoverContractSchema = z.infer<
  typeof InvoicesToRecoverContractSchema
>;

export const InvoicesToRecoverSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  computed: z.number(),
  paid: z.number(),
  validated: z.number(),
  not_contracted: z.number(),
  portfolio: z.union([z.string(), z.null()]),
  assetowner: z.string(),
  assetowner_uuid: z.string().uuid(),
  contracts: z.array(InvoicesToRecoverContractSchema),
  capacity: z.number(),
  commissioning: z.coerce.date().nullable(),
  revenue_start: z.coerce.date().nullable(),
});
export type InvoicesToRecoverSchema = z.infer<typeof InvoicesToRecoverSchema>;

export const CieScope = z.enum(["all", "base_only", "regulation_only"]);
export type CieScope = z.infer<typeof CieScope>;

export const Body_compute_invoice_from_model_api__model___uuid__invoices_compute_post =
  z.object({
    start: z.coerce.date().nullable().nullish(),
    end: z.coerce.date().nullable().nullish(),
    cie_scope: CieScope.nullish(),
    flt_on_valuation_horizon: z
      .union([z.boolean(), z.null()])
      .nullish()
      .default(true),
  });
export type Body_compute_invoice_from_model_api__model___uuid__invoices_compute_post =
  z.infer<
    typeof Body_compute_invoice_from_model_api__model___uuid__invoices_compute_post
  >;

export const InvoiceComputationSchema = z.object({
  new: z.union([z.array(InvoiceSchema), z.null()]).nullish(),
  refreshed: z.union([z.array(InvoiceSchema), z.null()]).nullish(),
  published: z.union([z.array(InvoiceSchema), z.null()]).nullish(),
});
export type InvoiceComputationSchema = z.infer<typeof InvoiceComputationSchema>;

export const InvoiceLineTargetError = z.object({
  name: z.literal("InvoiceLineTargetError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoiceLineTargetError = z.infer<typeof InvoiceLineTargetError>;

export const Body_create_regulation_invoice_for_contract_api_contracts__uuid__invoices_regulation_post =
  z.object({ start: z.coerce.date(), end: z.coerce.date() });
export type Body_create_regulation_invoice_for_contract_api_contracts__uuid__invoices_regulation_post =
  z.infer<
    typeof Body_create_regulation_invoice_for_contract_api_contracts__uuid__invoices_regulation_post
  >;

export const PendingRegulation = z.object({
  name: z.literal("PendingRegulation"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type PendingRegulation = z.infer<typeof PendingRegulation>;

export const InvalidRegulation = z.object({
  name: z.literal("InvalidRegulation"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvalidRegulation = z.infer<typeof InvalidRegulation>;

export const Body_create_payment_schedule_from_model_api__model___uuid__invoices_payment_schedule_post =
  z.object({
    start: z.coerce.date(),
    end: z.coerce.date().nullable().nullish(),
  });
export type Body_create_payment_schedule_from_model_api__model___uuid__invoices_payment_schedule_post =
  z.infer<
    typeof Body_create_payment_schedule_from_model_api__model___uuid__invoices_payment_schedule_post
  >;

export const InvoicingOnHoldError = z.object({
  name: z.literal("InvoicingOnHoldError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoicingOnHoldError = z.infer<typeof InvoicingOnHoldError>;

export const Body_submit_invoice_from_model_api__model___uuid__invoices_submit_post =
  z.object({
    start: z.coerce.date().nullable().nullish(),
    end: z.coerce.date().nullable().nullish(),
  });
export type Body_submit_invoice_from_model_api__model___uuid__invoices_submit_post =
  z.infer<
    typeof Body_submit_invoice_from_model_api__model___uuid__invoices_submit_post
  >;

export const InternalCreditNoteNotSubmittable = z.object({
  name: z.literal("InternalCreditNoteNotSubmittable"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InternalCreditNoteNotSubmittable = z.infer<
  typeof InternalCreditNoteNotSubmittable
>;

export const Body_publish_invoice_from_model_api__model___uuid__invoices_publish_post =
  z.object({
    start: z.coerce.date().nullable().nullish(),
    end: z.coerce.date().nullable().nullish(),
    as_draft: z.boolean().nullish(),
  });
export type Body_publish_invoice_from_model_api__model___uuid__invoices_publish_post =
  z.infer<
    typeof Body_publish_invoice_from_model_api__model___uuid__invoices_publish_post
  >;

export const InvoicePublicationError = z.object({
  name: z.literal("InvoicePublicationError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoicePublicationError = z.infer<typeof InvoicePublicationError>;

export const InvoiceNotReadyForPublication = z.object({
  name: z.literal("InvoiceNotReadyForPublication"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoiceNotReadyForPublication = z.infer<
  typeof InvoiceNotReadyForPublication
>;

export const InvoiceNotConcernedByPublication = z.object({
  name: z.literal("InvoiceNotConcernedByPublication"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoiceNotConcernedByPublication = z.infer<
  typeof InvoiceNotConcernedByPublication
>;

export const InvoiceAlreadyHasPublicationId = z.object({
  name: z.literal("InvoiceAlreadyHasPublicationId"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoiceAlreadyHasPublicationId = z.infer<
  typeof InvoiceAlreadyHasPublicationId
>;

export const InvoicePublicationIdAlreadyUsed = z.object({
  name: z.literal("InvoicePublicationIdAlreadyUsed"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvoicePublicationIdAlreadyUsed = z.infer<
  typeof InvoicePublicationIdAlreadyUsed
>;

export const InternalCreditNoteNotAllowed = z.object({
  name: z.literal("InternalCreditNoteNotAllowed"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InternalCreditNoteNotAllowed = z.infer<
  typeof InternalCreditNoteNotAllowed
>;

export const CreditNoteWithoutLines = z.object({
  name: z.literal("CreditNoteWithoutLines"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type CreditNoteWithoutLines = z.infer<typeof CreditNoteWithoutLines>;

export const CreditNoteOfCreditNoteLines = z.object({
  name: z.literal("CreditNoteOfCreditNoteLines"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type CreditNoteOfCreditNoteLines = z.infer<
  typeof CreditNoteOfCreditNoteLines
>;

export const InvoiceSyncCompanySchema = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
});
export type InvoiceSyncCompanySchema = z.infer<typeof InvoiceSyncCompanySchema>;

export const InvoiceSyncSiteSchema = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: InvoiceSyncCompanySchema,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  dp_codes: z.array(z.string()),
});
export type InvoiceSyncSiteSchema = z.infer<typeof InvoiceSyncSiteSchema>;

export const InvoiceSyncPersonSchema = z.object({
  uuid: z.string().uuid(),
  kind: LegalEntityKind,
  name: z.string(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
});
export type InvoiceSyncPersonSchema = z.infer<typeof InvoiceSyncPersonSchema>;

export const InvoiceContractSchemaSync = z.object({
  uuid: z.string().uuid(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  type: ContractTypeSchema,
  buyer: z.union([InvoiceSyncPersonSchema, InvoiceSyncCompanySchema]),
  seller: z.union([InvoiceSyncPersonSchema, InvoiceSyncCompanySchema]),
  buyer_accounting_infos: z
    .union([AssetManagerLegalEntityLinkSchema, z.null()])
    .nullish(),
  invoice_submit_channel: InvoiceSubmitChannel,
  invoice_source: InvoiceSource,
  is_synchronized: z.boolean(),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  invoicing_on_hold_reasons: z
    .union([z.array(OnHoldReason), z.null()])
    .nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  offtaker: z.union([InvoiceSyncPersonSchema, InvoiceSyncCompanySchema]),
});
export type InvoiceContractSchemaSync = z.infer<
  typeof InvoiceContractSchemaSync
>;

export const InvoiceSchemaSync = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  computable_asof: z.coerce.date(),
  creation_date: z.coerce.date(),
  publication_date: z.coerce.date().nullable().nullish(),
  computation_date: z.coerce.date().nullable().nullish(),
  payment_date: z.coerce.date().nullable().nullish(),
  publication_id: z.union([z.string(), z.null()]),
  external_order_number: z.union([z.string(), z.null()]).nullish(),
  status: InvoiceStatus,
  source: z.union([InvoiceSource, z.null()]).nullish(),
  sites: z.array(InvoiceSyncSiteSchema),
  contract: InvoiceContractSchemaSync,
  is_birthday: z.boolean(),
  is_accounted: z.boolean(),
  is_only_credit_note: z.boolean(),
  has_credit_note_lines: z.boolean(),
  is_internal_credit_note: z.boolean(),
  accounted_date: z.coerce.date().nullable().nullish(),
  accounted_initiator: z.union([z.string(), z.null()]).nullish(),
  external_status: z.union([InvoiceExternalStatus, z.null()]).nullish(),
  action: z.union([InvoiceAction, z.null()]).nullish(),
  has_pdf: z.boolean(),
  has_notes: z.boolean(),
  lines: z.array(InvoiceLineSchema),
  external_lines: z.array(InvoiceLineSchema),
  log_actions: z.array(InvoiceLogAction),
  site: z.union([InvoiceSyncSiteSchema, z.null()]).nullish(),
  is_credit_note: z.boolean(),
  offtaker_accounting_id: z.union([z.string(), z.null()]).nullish(),
  assetowner_accounting_id: z.union([z.string(), z.null()]).nullish(),
});
export type InvoiceSchemaSync = z.infer<typeof InvoiceSchemaSync>;

export const SecurityError = z.object({
  name: z.literal("SecurityError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type SecurityError = z.infer<typeof SecurityError>;

export const Body_set_invoice_accounted_api_invoices__uuid__accounted_post =
  z.object({ is_accounted: z.boolean().nullish().default(true) });
export type Body_set_invoice_accounted_api_invoices__uuid__accounted_post =
  z.infer<typeof Body_set_invoice_accounted_api_invoices__uuid__accounted_post>;

export const PeregreenValidationError = z.object({
  name: z.literal("PeregreenValidationError"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type PeregreenValidationError = z.infer<typeof PeregreenValidationError>;

export const PersonSchema = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.string(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  uuid: z.string().uuid(),
  kind: LegalEntityKind,
  name: z.string(),
  address: z.union([AddressSchema, z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
});
export type PersonSchema = z.infer<typeof PersonSchema>;

export const CompanySchemaWithLogo = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
});
export type CompanySchemaWithLogo = z.infer<typeof CompanySchemaWithLogo>;

export const TargetGranularity = z.enum([
  "deliverypoint",
  "site",
  "assetowner",
  "contract",
]);
export type TargetGranularity = z.infer<typeof TargetGranularity>;

export const SiteSchemaShort = z.object({
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  description: z.union([z.record(z.any()), z.null()]).nullish(),
  region: RegionSchema,
  country: CountrySchema,
  commissioning: z.coerce.date().nullable().nullish(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  uuid: z.string().uuid(),
  type: SiteType,
  capacity: z.number(),
  p50: z.union([z.number(), z.null()]).nullish(),
  machine_type: z.string(),
  assetowner: CompanySchemaShort,
  status: SiteStatus,
  is_external: z.boolean(),
  estimated_commissioning: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
});
export type SiteSchemaShort = z.infer<typeof SiteSchemaShort>;

export const DateOp = z.enum(["add", "ceil", "floor"]);
export type DateOp = z.infer<typeof DateOp>;

export const PaymentDelay = z.object({ op: DateOp, freq: z.string() });
export type PaymentDelay = z.infer<typeof PaymentDelay>;

export const IndexationParameters = z.object({
  index: InseeIndex,
  coefficient: z.union([z.number(), z.null()]).nullish(),
  initial_value: z.number(),
  base_year: z.number().int(),
});
export type IndexationParameters = z.infer<typeof IndexationParameters>;

export const PartialEnergyParameters = z.object({
  freq: z.union([z.string(), z.null()]).nullish(),
  shift_hours: z.union([z.number(), z.null()]).nullish(),
  cap_to_capa: z.union([z.boolean(), z.null()]).nullish(),
  exclude_neg_volumes: z.union([z.boolean(), z.null()]).nullish(),
  round_energy_before_amount: z.union([z.boolean(), z.null()]).nullish(),
  best_of_tags: z
    .union([
      z.array(
        z.union([
          z.enum(["R4Q", "R4M", "RP50", "RP12", "R172", "RSP"]),
          z.array(z.string()),
        ])
      ),
      z.null(),
    ])
    .nullish(),
  sprovider_priority: z.union([z.array(z.string()), z.null()]).nullish(),
});
export type PartialEnergyParameters = z.infer<typeof PartialEnergyParameters>;

export const InvoicingFreq = z.enum([
  "monthly",
  "semestrial",
  "yearly",
  "quarterly",
]);
export type InvoicingFreq = z.infer<typeof InvoicingFreq>;

export const InvoicingCfgDetails = z.object({
  freq: InvoicingFreq,
  is_freq_rounded: z.boolean(),
  freq_anchor: z.coerce.date().nullable().nullish(),
  delay: z.array(z.string()).nullish(),
  delay_reference: z.enum(["start", "end"]).nullish().default("end"),
  grouping_key_suffix: z.union([z.string(), z.null()]).nullish(),
  allow_overlapping_horizons: z.union([z.boolean(), z.null()]).nullish(),
});
export type InvoicingCfgDetails = z.infer<typeof InvoicingCfgDetails>;

export const InvoicingCfg = z.object({
  base: z.union([InvoicingCfgDetails, z.null()]).nullish(),
  regulation: z.union([InvoicingCfgDetails, z.null()]).nullish(),
  tarif_change_on_birthday: z.boolean().nullish(),
});
export type InvoicingCfg = z.infer<typeof InvoicingCfg>;

export const StaggeringGroup = z.enum(["A", "B"]);
export type StaggeringGroup = z.infer<typeof StaggeringGroup>;

export const CfdCr16 = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_cr_16"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.number(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  management_bonus: z.number(),
});
export type CfdCr16 = z.infer<typeof CfdCr16>;

export const CfdCr17 = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_cr_17"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.number(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  management_bonus: z.number(),
  energy_threshold: z.number(),
  tarif_above_threshold: z.number().nullish().default(40),
});
export type CfdCr17 = z.infer<typeof CfdCr17>;

export const CfdMajoration = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_majoration"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.number(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  majoration: z.number(),
  coef_k: z.number().nullish().default(1),
});
export type CfdMajoration = z.infer<typeof CfdMajoration>;

export const CompensatedHours = z.enum(["peak", "all", "default"]);
export type CompensatedHours = z.infer<typeof CompensatedHours>;

export const CfdNegPriceCompensation = z.object({
  type: z.literal("cfd_neg_price_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  capacity_ratio: z.union([z.number(), z.null()]).nullish(),
  deductible_hours: z.union([z.number(), z.null()]).nullish(),
  compensated_hours: CompensatedHours.nullish(),
  capacity_threshold_ratio: z.union([z.number(), z.null()]).nullish(),
});
export type CfdNegPriceCompensation = z.infer<typeof CfdNegPriceCompensation>;

export const CfdM0YearlyDeviation = z.object({
  type: z.literal("cfd_m0_yearly_deviation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type CfdM0YearlyDeviation = z.infer<typeof CfdM0YearlyDeviation>;

export const FeedInTarifWindMonthly = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_wind_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.number(),
});
export type FeedInTarifWindMonthly = z.infer<typeof FeedInTarifWindMonthly>;

export const FTClauseThresholdMode = z.enum([
  "warning",
  "error",
  "ignore",
  "default",
]);
export type FTClauseThresholdMode = z.infer<typeof FTClauseThresholdMode>;

export const ProrataTemporisMode = z.enum(["ndays", "timedelta"]);
export type ProrataTemporisMode = z.infer<typeof ProrataTemporisMode>;

export const FeedInTarifSolar = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_solar"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.number(),
  majoration: z.number().nullish(),
  single_line: z.boolean().nullish().default(true),
  energy_threshold: z.number(),
  tarif_above_threshold: z.number().nullish().default(40),
  apply_indexation_above_tarif: z.boolean().nullish(),
  invoicing_mode: z.enum(["index", "load_profile"]),
  index_interpolate_allowed: z.boolean().nullish(),
  with_other_invoicing_mode_fallback: z.boolean().nullish().default(true),
  clause_threshold_mode: FTClauseThresholdMode.nullish(),
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
  use_prorata_temporis: z.boolean().nullish(),
  acc_start_date: z.coerce.date().nullable().nullish(),
  acc_end_date: z.coerce.date().nullable().nullish(),
  octroi_de_mer_rate: z.union([z.number(), z.null()]).nullish(),
});
export type FeedInTarifSolar = z.infer<typeof FeedInTarifSolar>;

export const FeedInTarifSolarTrial = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_solar_trial"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif_base: z.number(),
  tarif_winter: z.number(),
});
export type FeedInTarifSolarTrial = z.infer<typeof FeedInTarifSolarTrial>;

export const DisconnectionCompensationRecord = z.object({
  year: z.number().int(),
  hours: z.number(),
  penalized_production: z.number(),
});
export type DisconnectionCompensationRecord = z.infer<
  typeof DisconnectionCompensationRecord
>;

export const DisconnectionCompensation = z.object({
  type: z.literal("disconnection_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  records: z.array(DisconnectionCompensationRecord).nullish(),
});
export type DisconnectionCompensation = z.infer<
  typeof DisconnectionCompensation
>;

export const SeasonEnum = z.enum(["base", "winter", "summer"]);
export type SeasonEnum = z.infer<typeof SeasonEnum>;

export const HourPeriod = z.enum(["base", "off_peak", "peak", "super_peak"]);
export type HourPeriod = z.infer<typeof HourPeriod>;

export const PpaTarifComponent = z.object({
  price_type: MarketPriceType,
  fix_price: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_coef: z.number().nullish().default(1),
  floor_neg_spot_to_zero: z.boolean().nullish().default(true),
});
export type PpaTarifComponent = z.infer<typeof PpaTarifComponent>;

export const TarifComponent = z.object({
  tarif: z.number(),
  majoration: z.union([z.number(), z.null()]).nullish(),
  season: SeasonEnum.nullish(),
  hour_period: HourPeriod.nullish(),
  period_definitions: z.array(PeriodDefinition).min(1).nullish(),
  power_threshold: z.union([z.number(), z.null()]).nullish(),
  uncovered_tarif: z.union([PpaTarifComponent, z.null()]).nullish(),
});
export type TarifComponent = z.infer<typeof TarifComponent>;

export const FeedInTarifHydro = z.object({
  tarif_components: z.array(TarifComponent).min(1),
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_hydro_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  max_quality_majoration: z.number(),
  quality_coefficient: z.number(),
  prorata: z.number().nullish().default(1),
  invoicing_mode: z
    .enum(["index", "load_profile"])
    .nullish()
    .default("load_profile"),
});
export type FeedInTarifHydro = z.infer<typeof FeedInTarifHydro>;

export const IndexPriceFallback = z.enum([
  "offtaker",
  "renewex",
  "assetmanager",
  "site",
  "no_fallback",
]);
export type IndexPriceFallback = z.infer<typeof IndexPriceFallback>;

export const MarketAccess = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("market_access"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  index_price_fallback: IndexPriceFallback.nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_fee_coefficient: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.boolean().nullish(),
  exclude_neg_price_periods_for_management_fee: z
    .boolean()
    .nullish()
    .default(true),
  single_line: z.boolean().nullish(),
});
export type MarketAccess = z.infer<typeof MarketAccess>;

export const SpotManagementFee = z.object({
  type: z.literal("spot_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  index_price_fallback: IndexPriceFallback.nullish(),
  formula: z.string(),
  min_value: z.union([z.number(), z.null()]).nullish(),
  max_value: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.boolean().nullish(),
});
export type SpotManagementFee = z.infer<typeof SpotManagementFee>;

export const MarketAccessImbalance = z.object({
  type: z.literal("market_access_imb"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type MarketAccessImbalance = z.infer<typeof MarketAccessImbalance>;

export const DayAheadPricesSource = z.enum([
  "market_15min",
  "market_hourly_resampled",
  "market_hourly_resampled_rounded",
  "cre",
  "follow_decree",
  "follow_decree_rounded",
]);
export type DayAheadPricesSource = z.infer<typeof DayAheadPricesSource>;

export const PpaDa = z.object({
  type: z.literal("ppa_da"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  formula: z.string().nullish().default("management_coef * spot_prices"),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_coef: z.number().nullish().default(1),
  exclude_neg_price_periods: z.boolean().nullish(),
  spot_price_source: DayAheadPricesSource.nullish(),
});
export type PpaDa = z.infer<typeof PpaDa>;

export const PpaDaIndexedFee = z.object({
  type: z.literal("ppa_da_indexed_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.number().nullish(),
  management_coef: z.number().nullish().default(1),
  prorata: z.number().nullish().default(1),
  exclude_neg_price_periods: z.boolean().nullish(),
  spot_price_source: DayAheadPricesSource.nullish(),
});
export type PpaDaIndexedFee = z.infer<typeof PpaDaIndexedFee>;

export const PPAImbalanceIndexedFee = z.object({
  type: z.literal("ppa_imbalance_indexed_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  positive_imbalance_coef: z.number().nullish().default(1),
  negative_imbalance_coef: z.number().nullish().default(1),
});
export type PPAImbalanceIndexedFee = z.infer<typeof PPAImbalanceIndexedFee>;

export const PpaFixedManagementFee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_fixed_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fixed_management_fee: z.number().nullish(),
});
export type PpaFixedManagementFee = z.infer<typeof PpaFixedManagementFee>;

export const PpaFix = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_fix"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.number(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.boolean().nullish().default(true),
});
export type PpaFix = z.infer<typeof PpaFix>;

export const PpaManagementFee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.number().nullish(),
  exclude_neg_price_periods: z.boolean().nullish().default(true),
});
export type PpaManagementFee = z.infer<typeof PpaManagementFee>;

export const PowerThreshCalRecord = z.object({
  date: z.coerce.date(),
  value: z.number(),
});
export type PowerThreshCalRecord = z.infer<typeof PowerThreshCalRecord>;

export const PpaThreshold = z.object({
  type: z.literal("ppa_threshold"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif_below_cap: PpaTarifComponent,
  tarif_above_cap: PpaTarifComponent,
  power_threshold: z.union([z.number(), z.array(PowerThreshCalRecord)]),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  day_ahead_prices_source: DayAheadPricesSource.nullish(),
});
export type PpaThreshold = z.infer<typeof PpaThreshold>;

export const MonthlyForecastRecord = z.object({
  month: z.number().int().gte(1).lte(12),
  forecast: z.number(),
});
export type MonthlyForecastRecord = z.infer<typeof MonthlyForecastRecord>;

export const QuarterlyFixPriceRecord = z.object({
  quarter: z.number().int().gte(1).lte(4),
  price: z.number(),
});
export type QuarterlyFixPriceRecord = z.infer<typeof QuarterlyFixPriceRecord>;

export const PpaForecastHedge = z.object({
  type: z.literal("ppa_forecast_hedge"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  forecast_share: z.number().gt(0).lte(1),
  monthly_forecast: z.array(MonthlyForecastRecord),
  fix_prices_quarterly: z.array(QuarterlyFixPriceRecord),
  uncovered_tarif: PpaTarifComponent,
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.boolean().nullish().default(true),
  spot_price_source: DayAheadPricesSource.nullish(),
});
export type PpaForecastHedge = z.infer<typeof PpaForecastHedge>;

export const PpaMultiTarif = z.object({
  tarif_components: z.array(TarifComponent).min(1),
  type: z.literal("ppa_tarif_components"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type PpaMultiTarif = z.infer<typeof PpaMultiTarif>;

export const PriceType = z.enum([
  "spot",
  "imbalance",
  "fix",
  "m0_spread",
  "disabled",
]);
export type PriceType = z.infer<typeof PriceType>;

export const PenaltyPriceFilter = z.enum([
  "negative_only",
  "positive_only",
  "all_prices",
]);
export type PenaltyPriceFilter = z.infer<typeof PenaltyPriceFilter>;

export const CapacityBasedCompensation = z.object({
  type: z.literal("capacity_based_coefficient"),
  capacity_based_coefficient: z.union([z.number(), z.null()]).nullish(),
});
export type CapacityBasedCompensation = z.infer<
  typeof CapacityBasedCompensation
>;

export const P50Compensation = z.object({
  type: z.literal("p50_prorata"),
  p50: z.union([z.number(), z.null()]).nullish(),
});
export type P50Compensation = z.infer<typeof P50Compensation>;

export const ForecastCompensation = z.object({
  type: z.literal("forecast_timeserie"),
  ts_name: z.union([z.string(), z.null()]).nullish(),
});
export type ForecastCompensation = z.infer<typeof ForecastCompensation>;

export const TrapezeCompensation = z.object({
  type: z.literal("trapeze"),
  resolution_freq: z.enum(["1h", "15min"]).nullish().default("1h"),
});
export type TrapezeCompensation = z.infer<typeof TrapezeCompensation>;

export const NegativePrice = z.object({
  type: z.literal("neg_price"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price_type: z.union([PriceType, z.record(z.number())]),
  neg_prices_source: DayAheadPricesSource.nullish(),
  spot_price_source_for_penalty: z
    .union([DayAheadPricesSource, z.null()])
    .nullish(),
  use_absolute_value: z.boolean().nullish(),
  penalty_price_filter: PenaltyPriceFilter.nullish(),
  cap_by_contract_price: z.boolean().nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  penalty_way_coefficient: z.union([z.number(), z.null()]).nullish().default(1),
  neg_price_threshold: z.number().nullish(),
  power_cutoff_threshold: z.union([z.number(), z.null()]).nullish(),
  penalize_excess_above_power_cutoff: z
    .union([z.boolean(), z.null()])
    .nullish(),
  neg_price_compensation: z
    .union([
      z.discriminatedUnion("type", [
        CapacityBasedCompensation,
        P50Compensation,
        ForecastCompensation,
        TrapezeCompensation,
      ]),
      z.null(),
    ])
    .nullish(),
});
export type NegativePrice = z.infer<typeof NegativePrice>;

export const DefermentRecord = z.object({
  value: z.number(),
  date: z.coerce.date(),
});
export type DefermentRecord = z.infer<typeof DefermentRecord>;

export const CreditNoteCap = z.object({
  type: z.literal("credit_note_cap"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  with_deferment: z.boolean().nullish().default(true),
  statuses: z
    .array(InvoiceStatus)
    .nullish()
    .default(["published", "paid", "computed", "submitted", "capped"]),
  known_deferment: z.array(DefermentRecord).nullish().default([]),
});
export type CreditNoteCap = z.infer<typeof CreditNoteCap>;

export const Goo = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("goo"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.number(),
});
export type Goo = z.infer<typeof Goo>;

export const MaxCapacityRecord = z.object({
  value: z.number(),
  year: z.number().int(),
});
export type MaxCapacityRecord = z.infer<typeof MaxCapacityRecord>;

export const CfdAutoconso = z.object({
  type: z.literal("cfd_autoconso"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.number(),
  premium_autoconso: z.number().nullish().default(10),
  yearly_max_capacity: z.array(MaxCapacityRecord).nullish(),
});
export type CfdAutoconso = z.infer<typeof CfdAutoconso>;

export const PaymentFreq = z.enum([
  "monthly",
  "semestrial",
  "yearly",
  "quarterly",
  "use_invoicing_freq",
]);
export type PaymentFreq = z.infer<typeof PaymentFreq>;

export const BonusMalusRecord = z.object({
  value: z.number(),
  year: z.number().int(),
  recovery_date: z.coerce.date().nullable().nullish(),
});
export type BonusMalusRecord = z.infer<typeof BonusMalusRecord>;

export const Autoconso = z.object({
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
  payment_schedule_freq: PaymentFreq.nullish(),
  type: z.literal("autoconso"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  yearly_tarif: z.number(),
  yearly_actualisation_rate: z.number(),
  bonus_malus: z.array(BonusMalusRecord).nullish(),
  missing_bonus_malus_resilient: z.boolean().nullish(),
});
export type Autoconso = z.infer<typeof Autoconso>;

export const InseeIndexationFormula = z.enum(["A", "B"]);
export type InseeIndexationFormula = z.infer<typeof InseeIndexationFormula>;

export const YearlyUnavailabilityRecord = z.object({
  rate: z.number().gte(0).lte(1),
  year: z.number().int(),
});
export type YearlyUnavailabilityRecord = z.infer<
  typeof YearlyUnavailabilityRecord
>;

export const UnavailabilityPenalty = z.object({
  threshold: z.number().gt(0).lte(1),
  availability_records: z.array(YearlyUnavailabilityRecord).nullish(),
});
export type UnavailabilityPenalty = z.infer<typeof UnavailabilityPenalty>;

export const AutoconsoInseeIndexed = z.object({
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
  payment_schedule_freq: PaymentFreq.nullish(),
  type: z.literal("autoconso_insee_indexed"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  yearly_tarif: z.number(),
  indexation_formula: InseeIndexationFormula.nullish(),
  indexation_coefficient: z.array(IndexCoefficient),
  unavailability_penalty: z.union([UnavailabilityPenalty, z.null()]).nullish(),
});
export type AutoconsoInseeIndexed = z.infer<typeof AutoconsoInseeIndexed>;

export const Settlement = z.object({
  type: z.literal("settlement"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.number().nullish().default(1),
});
export type Settlement = z.infer<typeof Settlement>;

export const UndeliveredEnergyPenalty = z.object({
  type: z.literal("undelivered_energy_penalty"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  pae: z.number(),
  spot_prices_coefficient: z.number(),
});
export type UndeliveredEnergyPenalty = z.infer<typeof UndeliveredEnergyPenalty>;

export const FeedInTarifBiogas = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_biogas_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.number(),
  shift_hours: z.number().int().nullish(),
  effluents_rate: z.number().nullish(),
  effluents_rate_threshold: z.number().nullish().default(0.6),
  effluents_tarif: z.number().nullish().default(50),
});
export type FeedInTarifBiogas = z.infer<typeof FeedInTarifBiogas>;

export const PpaThresholdMultiPeriod = z.object({
  tarif_components: z.array(TarifComponent).min(1),
  type: z.literal("ppa_threshold_multi_period"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  epex_fee: z.number().nullish().default(0.07),
  spot_price_source: DayAheadPricesSource.nullish(),
});
export type PpaThresholdMultiPeriod = z.infer<typeof PpaThresholdMultiPeriod>;

export const EnergyGuarantee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("energy_guarantee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.number(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.boolean().nullish().default(true),
  yearly_cashflow: z.array(z.number()),
  monthly_seasonality: z
    .array(z.number())
    .nullish()
    .default([1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]),
});
export type EnergyGuarantee = z.infer<typeof EnergyGuarantee>;

export const EnergyGuaranteeRiskPremium = z.object({
  type: z.literal("energy_guarantee_risk_premium"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  guarantee_percentage: z.number(),
});
export type EnergyGuaranteeRiskPremium = z.infer<
  typeof EnergyGuaranteeRiskPremium
>;

export const PpaAsia = z.object({
  tarif_components: z.array(TarifComponent).min(1),
  type: z.literal("ppa_asia"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  ft_cost: z.union([z.number(), z.null()]).nullish(),
  discount_rate: z.number().nullish(),
});
export type PpaAsia = z.infer<typeof PpaAsia>;

export const GCRecord = z.object({
  year: z.number().int(),
  capacity: z.number(),
  price: z.number(),
});
export type GCRecord = z.infer<typeof GCRecord>;

export const GuaranteeOfCapacity = z.object({
  type: z.literal("gc"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  records: z.array(GCRecord).nullish(),
  certification_fee: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  separate_fee_invoice: z.boolean().nullish(),
});
export type GuaranteeOfCapacity = z.infer<typeof GuaranteeOfCapacity>;

export const IndexationTimeRef = z.enum(["calendar", "contractual"]);
export type IndexationTimeRef = z.infer<typeof IndexationTimeRef>;

export const InseeIndexation = z.object({
  indexation: z.array(IndexationParameters),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("insee").default("insee"),
  indexation_year_start: z.union([z.number(), z.null()]).nullish(),
  is_last_period_regulated: z.boolean().nullish(),
  time_reference: IndexationTimeRef.nullish(),
  indexation_formula: z.string().nullish().default("L = (ICHTn / ICHT(0))"),
});
export type InseeIndexation = z.infer<typeof InseeIndexation>;

export const FixedIndexation = z.object({
  type: z.literal("fixed").default("fixed"),
  indexation_year_start: z.union([z.number(), z.null()]).nullish(),
  is_last_period_regulated: z.boolean().nullish(),
  time_reference: IndexationTimeRef.nullish(),
  yearly_rate: z.number(),
});
export type FixedIndexation = z.infer<typeof FixedIndexation>;

export const FixedPrice = z.object({
  type: z.literal("fixed").default("fixed"),
  yearly_tarif: z.number().gt(0),
});
export type FixedPrice = z.infer<typeof FixedPrice>;

export const _MaintenanceTarifComponent = z.object({
  energy_tarif: z.number().gt(0),
  energy_share: z.number().gt(0).lte(1),
});
export type _MaintenanceTarifComponent = z.infer<
  typeof _MaintenanceTarifComponent
>;

export const SharePrice = z.object({
  type: z.literal("revenue_share").default("revenue_share"),
  revenue_share: z.number().gt(0).lte(1),
  energy_tarif: z.union([z.number(), z.array(_MaintenanceTarifComponent)]),
});
export type SharePrice = z.infer<typeof SharePrice>;

export const InterventionFreq = z.object({
  count: z.number().int(),
  every_n_years: z.number().int().nullish().default(1),
});
export type InterventionFreq = z.infer<typeof InterventionFreq>;

export const MaintenanceInformation = z.object({
  corrective_freq: z
    .union([InterventionFreq, z.literal("unlimited"), z.null()])
    .nullish(),
  preventive_freq: z
    .union([InterventionFreq, z.literal("unlimited"), z.null()])
    .nullish(),
  cleaning_freq: z
    .union([InterventionFreq, z.literal("unlimited"), z.null()])
    .nullish(),
  performance_ratios: z
    .union([z.array(z.record(z.number())), z.null()])
    .nullish(),
  manufacturer_inverter_warranty_years: z
    .union([z.number(), z.null()])
    .nullish(),
  has_extended_inverter_warranty: z.union([z.boolean(), z.null()]).nullish(),
  inverter_replacement_coverage: z.union([z.string(), z.null()]).nullish(),
  panel_replacement_coverage: z.union([z.string(), z.null()]).nullish(),
});
export type MaintenanceInformation = z.infer<typeof MaintenanceInformation>;

export const ClauseMaintenance = z.object({
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
  payment_schedule_freq: PaymentFreq.nullish(),
  type: z.literal("maintenance"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  indexation_logic: z
    .union([
      z.discriminatedUnion("type", [InseeIndexation, FixedIndexation]),
      z.null(),
    ])
    .nullish(),
  price_logic: z.discriminatedUnion("type", [FixedPrice, SharePrice]),
  infos: z.union([MaintenanceInformation, z.null()]).nullish(),
});
export type ClauseMaintenance = z.infer<typeof ClauseMaintenance>;

export const LeaseProduct = z.object({
  yearly_amount: z.number(),
  description: z.string(),
});
export type LeaseProduct = z.infer<typeof LeaseProduct>;

export const PaymentMode = z.enum(["transfer", "check"]);
export type PaymentMode = z.infer<typeof PaymentMode>;

export const Lease = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
  payment_schedule_freq: PaymentFreq.nullish(),
  type: z.literal("lease"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  products: z.array(LeaseProduct).nullish(),
  first_invoice_products: z.array(LeaseProduct).nullish(),
  indexation_formula: z.union([z.string(), z.null()]).nullish(),
  l_can_decrease: z.boolean().nullish().default(true),
  generate_answer: z.boolean().nullish(),
  payment_mode: PaymentMode.nullish(),
  as_letter: z.boolean().nullish(),
});
export type Lease = z.infer<typeof Lease>;

export const ExciseType = z.enum(["household", "pme", "hp"]);
export type ExciseType = z.infer<typeof ExciseType>;

export const CollectiveAutoconsumption = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.boolean().nullish(),
  use_precomputed: z.boolean().nullish(),
  pre_computed: z.array(LIndexComputationDetails).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("collective_autoconsumption"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.number(),
  excise_type: ExciseType.nullish(),
});
export type CollectiveAutoconsumption = z.infer<
  typeof CollectiveAutoconsumption
>;

export const SunFlow = z.object({
  type: z.literal("sunflow_acc"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.number(),
  rule: MarketPriceType,
  split_share: z.number().nullish().default(0.5),
});
export type SunFlow = z.infer<typeof SunFlow>;

export const FlowTracking = z.object({
  type: z.literal("flow_tracking"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.number(),
  exclude_neg_price_periods: z.boolean().nullish(),
});
export type FlowTracking = z.infer<typeof FlowTracking>;

export const ShareCapacityTier = z.object({
  capacity: z.number().gte(0),
  share: z.number().gte(0).lte(1),
});
export type ShareCapacityTier = z.infer<typeof ShareCapacityTier>;

export const BalancingMechanism = z.object({
  type: z.literal("balancing_mechanism"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  redistribution_share: z
    .union([z.number(), z.array(ShareCapacityTier)])
    .nullish()
    .default(1),
});
export type BalancingMechanism = z.infer<typeof BalancingMechanism>;

export const BalancingMechanismCapacityReservation = z.object({
  type: z.literal("bm_capacity_reservation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  redistribution_share: z.union([z.number(), z.null()]).nullish(),
});
export type BalancingMechanismCapacityReservation = z.infer<
  typeof BalancingMechanismCapacityReservation
>;

export const BalancingMechanismCompensation = z.object({
  type: z.literal("balancing_mechanism_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type BalancingMechanismCompensation = z.infer<
  typeof BalancingMechanismCompensation
>;

export const SmartOACompensation = z.object({
  type: z.literal("smart_oa_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type SmartOACompensation = z.infer<typeof SmartOACompensation>;

export const RealTarifPricing = z.object({
  type: z.literal("real_tarif").default("real_tarif"),
  guaranteed_performance: z.record(z.number()),
  reference_tarif: z.record(z.number()),
  client_share: z.array(z.number()).min(1),
  tarif_cap_rate: z.union([z.number(), z.null()]).nullish(),
  records: z.array(EETarifRecord).nullish(),
});
export type RealTarifPricing = z.infer<typeof RealTarifPricing>;

export const FixedInflationPricing = z.object({
  type: z.literal("fixed_inflation").default("fixed_inflation"),
  initial_redevance: z.number().gt(0),
  yearly_rate: z.number(),
});
export type FixedInflationPricing = z.infer<typeof FixedInflationPricing>;

export const EETarif = z.object({
  type: z.literal("ee_tarif"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  pricing: z.discriminatedUnion("type", [
    RealTarifPricing,
    FixedInflationPricing,
  ]),
  prorata_temporis_mode: ProrataTemporisMode.nullish(),
});
export type EETarif = z.infer<typeof EETarif>;

export const SupplyEnergy = z.object({
  type: z.literal("supply_energy"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type SupplyEnergy = z.infer<typeof SupplyEnergy>;

export const SiteSchemaTiny = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
});
export type SiteSchemaTiny = z.infer<typeof SiteSchemaTiny>;

export const ClauseDeliveryPointSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  code: z.union([z.string(), z.null()]),
  reference: z.union([z.string(), z.null()]),
});
export type ClauseDeliveryPointSchema = z.infer<
  typeof ClauseDeliveryPointSchema
>;

export const ClauseDeliveryPointLinkSchema = z.object({
  deliverypoint: ClauseDeliveryPointSchema,
  start: z.coerce.date().nullable(),
  end: z.coerce.date().nullable(),
});
export type ClauseDeliveryPointLinkSchema = z.infer<
  typeof ClauseDeliveryPointLinkSchema
>;

export const ClauseSchema = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  type: ClauseTypes,
  parameters: z.discriminatedUnion("type", [
    CfdCr16,
    CfdCr17,
    CfdMajoration,
    CfdNegPriceCompensation,
    CfdM0YearlyDeviation,
    FeedInTarifWindMonthly,
    FeedInTarifSolar,
    FeedInTarifSolarTrial,
    DisconnectionCompensation,
    FeedInTarifHydro,
    MarketAccess,
    SpotManagementFee,
    MarketAccessImbalance,
    PpaDa,
    PpaDaIndexedFee,
    PPAImbalanceIndexedFee,
    PpaFixedManagementFee,
    PpaFix,
    PpaManagementFee,
    PpaThreshold,
    PpaForecastHedge,
    PpaMultiTarif,
    NegativePrice,
    CreditNoteCap,
    Goo,
    CfdAutoconso,
    Autoconso,
    AutoconsoInseeIndexed,
    Settlement,
    UndeliveredEnergyPenalty,
    FeedInTarifBiogas,
    PpaThresholdMultiPeriod,
    EnergyGuarantee,
    EnergyGuaranteeRiskPremium,
    PpaAsia,
    GuaranteeOfCapacity,
    ClauseMaintenance,
    Lease,
    CollectiveAutoconsumption,
    SunFlow,
    FlowTracking,
    BalancingMechanism,
    BalancingMechanismCapacityReservation,
    BalancingMechanismCompensation,
    SmartOACompensation,
    EETarif,
    SupplyEnergy,
  ]),
  sites: z.array(SiteSchemaTiny),
  deliverypoint_links: z.array(ClauseDeliveryPointLinkSchema),
});
export type ClauseSchema = z.infer<typeof ClauseSchema>;

export const ContractStatusHistorySchema = z.object({
  date: z.coerce.date(),
  status: ContractStatus,
});
export type ContractStatusHistorySchema = z.infer<
  typeof ContractStatusHistorySchema
>;

export const ContractDeliveryPointSchema = z.object({
  code: z.union([z.string(), z.null()]).nullish(),
  reference: z.union([z.string(), z.null()]).nullish(),
  name: z.string(),
});
export type ContractDeliveryPointSchema = z.infer<
  typeof ContractDeliveryPointSchema
>;

export const ContractPdfSchema = z.object({ uuid: z.string().uuid() });
export type ContractPdfSchema = z.infer<typeof ContractPdfSchema>;

export const ContractSchema = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  creation_date: z.coerce.date(),
  buyer: z.union([PersonSchema, CompanySchemaWithLogo]),
  seller: z.union([PersonSchema, CompanySchemaWithLogo]),
  type: ContractTypeSchema,
  current_status: ContractStatus,
  price_mechanism: PriceMechanism,
  invoice_source: InvoiceSource,
  invoice_submit_channel: InvoiceSubmitChannel,
  invoicing_granularity: TargetGranularity,
  pdf_line_granularity: z.union([TargetGranularity, z.null()]).nullish(),
  invoicing_on_hold_reasons: z.array(OnHoldReason),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  is_synchronized: z.boolean(),
  offset_consumption: z.boolean(),
  sites: z.array(SiteSchemaShort),
  target_assetowner: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  external_name: z.union([z.string(), z.null()]).nullish(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  order_number: z.union([z.string(), z.null()]).nullish(),
  market_number: z.union([z.string(), z.null()]).nullish(),
  payment_delay_cfg: z.array(PaymentDelay),
  vat_term: z.union([z.string(), z.null()]),
  vat_rate: z.number(),
  contract_vat_term: z.union([z.string(), z.null()]),
  contract_vat_rate: z.union([z.number(), z.null()]),
  clauses: z.array(ClauseSchema),
  statuses: z.array(ContractStatusHistorySchema),
  deliverypoints: z.array(ContractDeliveryPointSchema),
  invoicing_cfg: InvoicingCfg,
  invoicing_freq: z.union([InvoicingFreq, z.null()]),
  email_contacts: EmailContactsSchema,
  buyer_accounting_infos: z
    .union([AssetManagerLegalEntityLinkSchema, z.null()])
    .nullish(),
  pdf: z.union([ContractPdfSchema, z.null()]).nullish(),
  platform_credential: z
    .union([PlatformCredentialIdentitySchema, z.null()])
    .nullish(),
  site: z.union([SiteSchemaShort, z.null()]).nullish(),
});
export type ContractSchema = z.infer<typeof ContractSchema>;

export const ContractSchemaUpdate = z.object({
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  type: z.union([ContractTypeSchema, z.null()]).nullish(),
  buyer: z.union([z.string(), z.null()]).nullish(),
  seller: z.union([z.string(), z.null()]).nullish(),
  invoice_source: z.union([InvoiceSource, z.null()]).nullish(),
  invoicing_granularity: z.union([TargetGranularity, z.null()]).nullish(),
  pdf_line_granularity: z.union([TargetGranularity, z.null()]).nullish(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  invoice_submit_channel: z.union([InvoiceSubmitChannel, z.null()]).nullish(),
  invoicing_on_hold_reasons: z
    .union([z.array(OnHoldReason), z.null()])
    .nullish(),
  offset_consumption: z.union([z.boolean(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  external_name: z.union([z.string(), z.null()]).nullish(),
  order_number: z.union([z.string(), z.null()]).nullish(),
  market_number: z.union([z.string(), z.null()]).nullish(),
  target_assetowner: z.union([z.string(), z.null()]).nullish(),
  payment_delay_cfg: z.union([z.array(PaymentDelay), z.null()]).nullish(),
  contract_vat_term: z.union([z.string(), z.null()]).nullish(),
  contract_vat_rate: z.union([z.number(), z.null()]).nullish(),
});
export type ContractSchemaUpdate = z.infer<typeof ContractSchemaUpdate>;

export const PlatformCredentialSchema = z.object({
  email: z.union([z.string(), z.null()]).nullish(),
  password: z.string(),
});
export type PlatformCredentialSchema = z.infer<typeof PlatformCredentialSchema>;

export const MissingEDFOACredentials = z.object({
  name: z.literal("MissingEDFOACredentials"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type MissingEDFOACredentials = z.infer<typeof MissingEDFOACredentials>;

export const NotAPlatformSubmittedContract = z.object({
  name: z.literal("NotAPlatformSubmittedContract"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type NotAPlatformSubmittedContract = z.infer<
  typeof NotAPlatformSubmittedContract
>;

export const BaseContractSchema = z.object({
  uuid: z.string().uuid(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  creation_date: z.coerce.date(),
  buyer: z.union([PersonSchema, CompanySchemaShort]),
  seller: z.union([PersonSchema, CompanySchemaShort]),
  type: ContractTypeSchema,
  current_status: ContractStatus,
  price_mechanism: PriceMechanism,
  invoice_source: InvoiceSource,
  invoice_submit_channel: InvoiceSubmitChannel,
  invoicing_granularity: TargetGranularity,
  pdf_line_granularity: z.union([TargetGranularity, z.null()]).nullish(),
  invoicing_on_hold_reasons: z.array(OnHoldReason),
  invoicing_automation_start: z.coerce.date().nullable().nullish(),
  is_synchronized: z.boolean(),
  offset_consumption: z.boolean(),
  sites: z.array(SiteSchemaShort),
  target_assetowner: z.union([z.string(), z.null()]).nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  external_name: z.union([z.string(), z.null()]).nullish(),
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  order_number: z.union([z.string(), z.null()]).nullish(),
  market_number: z.union([z.string(), z.null()]).nullish(),
  payment_delay_cfg: z.array(PaymentDelay),
  vat_term: z.union([z.string(), z.null()]),
  vat_rate: z.number(),
  contract_vat_term: z.union([z.string(), z.null()]),
  contract_vat_rate: z.union([z.number(), z.null()]),
  site: z.union([SiteSchemaShort, z.null()]).nullish(),
});
export type BaseContractSchema = z.infer<typeof BaseContractSchema>;

export const PartialCfdCr16 = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_cr_16"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  management_bonus: z.union([z.number(), z.null()]).nullish(),
});
export type PartialCfdCr16 = z.infer<typeof PartialCfdCr16>;

export const PartialCfdCr17 = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_cr_17"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  management_bonus: z.union([z.number(), z.null()]).nullish(),
  energy_threshold: z.union([z.number(), z.null()]).nullish(),
  tarif_above_threshold: z.union([z.number(), z.null()]).nullish(),
});
export type PartialCfdCr17 = z.infer<typeof PartialCfdCr17>;

export const PartialCfdMajoration = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("cfd_majoration"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  staggering_group: z.union([StaggeringGroup, z.null()]).nullish(),
  postal_code_company: z.union([z.string(), z.null()]).nullish(),
  siret_company: z.union([z.string(), z.null()]).nullish(),
  postal_code_pdf: z.union([z.string(), z.null()]).nullish(),
  majoration: z.union([z.number(), z.null()]).nullish(),
  coef_k: z.union([z.number(), z.null()]).nullish(),
});
export type PartialCfdMajoration = z.infer<typeof PartialCfdMajoration>;

export const PartialCfdNegPriceCompensation = z.object({
  type: z.literal("cfd_neg_price_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  capacity_ratio: z.union([z.number(), z.null()]).nullish(),
  deductible_hours: z.union([z.number(), z.null()]).nullish(),
  compensated_hours: z.union([CompensatedHours, z.null()]).nullish(),
  capacity_threshold_ratio: z.union([z.number(), z.null()]).nullish(),
});
export type PartialCfdNegPriceCompensation = z.infer<
  typeof PartialCfdNegPriceCompensation
>;

export const PartialCfdM0YearlyDeviation = z.object({
  type: z.literal("cfd_m0_yearly_deviation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type PartialCfdM0YearlyDeviation = z.infer<
  typeof PartialCfdM0YearlyDeviation
>;

export const PartialFeedInTarifWindMonthly = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_wind_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
});
export type PartialFeedInTarifWindMonthly = z.infer<
  typeof PartialFeedInTarifWindMonthly
>;

export const PartialFeedInTarifSolar = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_solar"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  majoration: z.union([z.number(), z.null()]).nullish(),
  single_line: z.union([z.boolean(), z.null()]).nullish(),
  energy_threshold: z.union([z.number(), z.null()]).nullish(),
  tarif_above_threshold: z.union([z.number(), z.null()]).nullish(),
  apply_indexation_above_tarif: z.union([z.boolean(), z.null()]).nullish(),
  invoicing_mode: z
    .union([z.enum(["index", "load_profile"]), z.null()])
    .nullish(),
  index_interpolate_allowed: z.union([z.boolean(), z.null()]).nullish(),
  with_other_invoicing_mode_fallback: z
    .union([z.boolean(), z.null()])
    .nullish(),
  clause_threshold_mode: z.union([FTClauseThresholdMode, z.null()]).nullish(),
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
  use_prorata_temporis: z.union([z.boolean(), z.null()]).nullish(),
  acc_start_date: z.coerce.date().nullable().nullish(),
  acc_end_date: z.coerce.date().nullable().nullish(),
  octroi_de_mer_rate: z.union([z.number(), z.null()]).nullish(),
});
export type PartialFeedInTarifSolar = z.infer<typeof PartialFeedInTarifSolar>;

export const PartialFeedInTarifSolarTrial = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_solar_trial"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif_base: z.union([z.number(), z.null()]).nullish(),
  tarif_winter: z.union([z.number(), z.null()]).nullish(),
});
export type PartialFeedInTarifSolarTrial = z.infer<
  typeof PartialFeedInTarifSolarTrial
>;

export const PartialDisconnectionCompensation = z.object({
  type: z.literal("disconnection_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  records: z
    .union([z.array(DisconnectionCompensationRecord), z.null()])
    .nullish(),
});
export type PartialDisconnectionCompensation = z.infer<
  typeof PartialDisconnectionCompensation
>;

export const PartialFeedInTarifHydro = z.object({
  tarif_components: z.union([z.array(TarifComponent), z.null()]).nullish(),
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_hydro_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  max_quality_majoration: z.union([z.number(), z.null()]).nullish(),
  quality_coefficient: z.union([z.number(), z.null()]).nullish(),
  prorata: z.union([z.number(), z.null()]).nullish(),
  invoicing_mode: z
    .union([z.enum(["index", "load_profile"]), z.null()])
    .nullish(),
});
export type PartialFeedInTarifHydro = z.infer<typeof PartialFeedInTarifHydro>;

export const PartialMarketAccess = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("market_access"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  index_price_fallback: z.union([IndexPriceFallback, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_fee_coefficient: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
  exclude_neg_price_periods_for_management_fee: z
    .union([z.boolean(), z.null()])
    .nullish(),
  single_line: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialMarketAccess = z.infer<typeof PartialMarketAccess>;

export const PartialSpotManagementFee = z.object({
  type: z.literal("spot_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  index_price_fallback: z.union([IndexPriceFallback, z.null()]).nullish(),
  formula: z.union([z.string(), z.null()]).nullish(),
  min_value: z.union([z.number(), z.null()]).nullish(),
  max_value: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialSpotManagementFee = z.infer<typeof PartialSpotManagementFee>;

export const PartialMarketAccessImbalance = z.object({
  type: z.literal("market_access_imb"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type PartialMarketAccessImbalance = z.infer<
  typeof PartialMarketAccessImbalance
>;

export const PartialPpaDa = z.object({
  type: z.literal("ppa_da"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  formula: z.union([z.string(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_coef: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
  spot_price_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
});
export type PartialPpaDa = z.infer<typeof PartialPpaDa>;

export const PartialPpaDaIndexedFee = z.object({
  type: z.literal("ppa_da_indexed_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  management_coef: z.union([z.number(), z.null()]).nullish(),
  prorata: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
  spot_price_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
});
export type PartialPpaDaIndexedFee = z.infer<typeof PartialPpaDaIndexedFee>;

export const PartialPPAImbalanceIndexedFee = z.object({
  type: z.literal("ppa_imbalance_indexed_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  positive_imbalance_coef: z.union([z.number(), z.null()]).nullish(),
  negative_imbalance_coef: z.union([z.number(), z.null()]).nullish(),
});
export type PartialPPAImbalanceIndexedFee = z.infer<
  typeof PartialPPAImbalanceIndexedFee
>;

export const PartialPpaFixedManagementFee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_fixed_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fixed_management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type PartialPpaFixedManagementFee = z.infer<
  typeof PartialPpaFixedManagementFee
>;

export const PartialPpaFix = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_fix"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialPpaFix = z.infer<typeof PartialPpaFix>;

export const PartialPpaManagementFee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ppa_management_fee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialPpaManagementFee = z.infer<typeof PartialPpaManagementFee>;

export const PartialPpaThreshold = z.object({
  type: z.literal("ppa_threshold"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif_below_cap: z.union([PpaTarifComponent, z.null()]).nullish(),
  tarif_above_cap: z.union([PpaTarifComponent, z.null()]).nullish(),
  power_threshold: z
    .union([z.number(), z.array(PowerThreshCalRecord), z.null()])
    .nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  day_ahead_prices_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
});
export type PartialPpaThreshold = z.infer<typeof PartialPpaThreshold>;

export const PartialPpaForecastHedge = z.object({
  type: z.literal("ppa_forecast_hedge"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  forecast_share: z.union([z.number(), z.null()]).nullish(),
  monthly_forecast: z
    .union([z.array(MonthlyForecastRecord), z.null()])
    .nullish(),
  fix_prices_quarterly: z
    .union([z.array(QuarterlyFixPriceRecord), z.null()])
    .nullish(),
  uncovered_tarif: z.union([PpaTarifComponent, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
  spot_price_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
});
export type PartialPpaForecastHedge = z.infer<typeof PartialPpaForecastHedge>;

export const PartialPpaMultiTarif = z.object({
  tarif_components: z.union([z.array(TarifComponent), z.null()]).nullish(),
  type: z.literal("ppa_tarif_components"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
});
export type PartialPpaMultiTarif = z.infer<typeof PartialPpaMultiTarif>;

export const PartialNegativePrice = z.object({
  type: z.literal("neg_price"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price_type: z.union([PriceType, z.record(z.number()), z.null()]).nullish(),
  neg_prices_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
  spot_price_source_for_penalty: z
    .union([DayAheadPricesSource, z.null()])
    .nullish(),
  use_absolute_value: z.union([z.boolean(), z.null()]).nullish(),
  penalty_price_filter: z.union([PenaltyPriceFilter, z.null()]).nullish(),
  cap_by_contract_price: z.union([z.boolean(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  penalty_way_coefficient: z.union([z.number(), z.null()]).nullish(),
  neg_price_threshold: z.union([z.number(), z.null()]).nullish(),
  power_cutoff_threshold: z.union([z.number(), z.null()]).nullish(),
  penalize_excess_above_power_cutoff: z
    .union([z.boolean(), z.null()])
    .nullish(),
  neg_price_compensation: z
    .union([
      z.discriminatedUnion("type", [
        CapacityBasedCompensation,
        P50Compensation,
        ForecastCompensation,
        TrapezeCompensation,
      ]),
      z.null(),
    ])
    .nullish(),
});
export type PartialNegativePrice = z.infer<typeof PartialNegativePrice>;

export const PartialCreditNoteCap = z.object({
  type: z.literal("credit_note_cap"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  with_deferment: z.union([z.boolean(), z.null()]).nullish(),
  statuses: z.union([z.array(InvoiceStatus), z.null()]).nullish(),
  known_deferment: z.union([z.array(DefermentRecord), z.null()]).nullish(),
});
export type PartialCreditNoteCap = z.infer<typeof PartialCreditNoteCap>;

export const PartialGoo = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("goo"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.union([z.number(), z.null()]).nullish(),
});
export type PartialGoo = z.infer<typeof PartialGoo>;

export const PartialCfdAutoconso = z.object({
  type: z.literal("cfd_autoconso"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  premium_autoconso: z.union([z.number(), z.null()]).nullish(),
  yearly_max_capacity: z
    .union([z.array(MaxCapacityRecord), z.null()])
    .nullish(),
});
export type PartialCfdAutoconso = z.infer<typeof PartialCfdAutoconso>;

export const PartialAutoconso = z.object({
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
  payment_schedule_freq: z.union([PaymentFreq, z.null()]).nullish(),
  type: z.literal("autoconso"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  yearly_tarif: z.union([z.number(), z.null()]).nullish(),
  yearly_actualisation_rate: z.union([z.number(), z.null()]).nullish(),
  bonus_malus: z.union([z.array(BonusMalusRecord), z.null()]).nullish(),
  missing_bonus_malus_resilient: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialAutoconso = z.infer<typeof PartialAutoconso>;

export const PartialAutoconsoInseeIndexed = z.object({
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
  payment_schedule_freq: z.union([PaymentFreq, z.null()]).nullish(),
  type: z.literal("autoconso_insee_indexed"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  yearly_tarif: z.union([z.number(), z.null()]).nullish(),
  indexation_formula: z.union([InseeIndexationFormula, z.null()]).nullish(),
  indexation_coefficient: z
    .union([z.array(IndexCoefficient), z.null()])
    .nullish(),
  unavailability_penalty: z.union([UnavailabilityPenalty, z.null()]).nullish(),
});
export type PartialAutoconsoInseeIndexed = z.infer<
  typeof PartialAutoconsoInseeIndexed
>;

export const PartialSettlement = z.object({
  type: z.literal("settlement"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.union([z.number(), z.null()]).nullish(),
});
export type PartialSettlement = z.infer<typeof PartialSettlement>;

export const PartialUndeliveredEnergyPenalty = z.object({
  type: z.literal("undelivered_energy_penalty"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  pae: z.union([z.number(), z.null()]).nullish(),
  spot_prices_coefficient: z.union([z.number(), z.null()]).nullish(),
});
export type PartialUndeliveredEnergyPenalty = z.infer<
  typeof PartialUndeliveredEnergyPenalty
>;

export const PartialFeedInTarifBiogas = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("ft_biogas_monthly"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  contracted_capacity: z.union([z.number(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  shift_hours: z.union([z.number(), z.null()]).nullish(),
  effluents_rate: z.union([z.number(), z.null()]).nullish(),
  effluents_rate_threshold: z.union([z.number(), z.null()]).nullish(),
  effluents_tarif: z.union([z.number(), z.null()]).nullish(),
});
export type PartialFeedInTarifBiogas = z.infer<typeof PartialFeedInTarifBiogas>;

export const PartialPpaThresholdMultiPeriod = z.object({
  tarif_components: z.union([z.array(TarifComponent), z.null()]).nullish(),
  type: z.literal("ppa_threshold_multi_period"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  epex_fee: z.union([z.number(), z.null()]).nullish(),
  spot_price_source: z.union([DayAheadPricesSource, z.null()]).nullish(),
});
export type PartialPpaThresholdMultiPeriod = z.infer<
  typeof PartialPpaThresholdMultiPeriod
>;

export const PartialEnergyGuarantee = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("energy_guarantee"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
  yearly_cashflow: z.union([z.array(z.number()), z.null()]).nullish(),
  monthly_seasonality: z.union([z.array(z.number()), z.null()]).nullish(),
});
export type PartialEnergyGuarantee = z.infer<typeof PartialEnergyGuarantee>;

export const PartialEnergyGuaranteeRiskPremium = z.object({
  type: z.literal("energy_guarantee_risk_premium"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  guarantee_percentage: z.union([z.number(), z.null()]).nullish(),
});
export type PartialEnergyGuaranteeRiskPremium = z.infer<
  typeof PartialEnergyGuaranteeRiskPremium
>;

export const PartialPpaAsia = z.object({
  tarif_components: z.union([z.array(TarifComponent), z.null()]).nullish(),
  type: z.literal("ppa_asia"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  ft_cost: z.union([z.number(), z.null()]).nullish(),
  discount_rate: z.union([z.number(), z.null()]).nullish(),
});
export type PartialPpaAsia = z.infer<typeof PartialPpaAsia>;

export const PartialGuaranteeOfCapacity = z.object({
  type: z.literal("gc"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  records: z.union([z.array(GCRecord), z.null()]).nullish(),
  certification_fee: z.union([z.number(), z.null()]).nullish(),
  management_fee: z.union([z.number(), z.null()]).nullish(),
  separate_fee_invoice: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialGuaranteeOfCapacity = z.infer<
  typeof PartialGuaranteeOfCapacity
>;

export const PartialClauseMaintenance = z.object({
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
  payment_schedule_freq: z.union([PaymentFreq, z.null()]).nullish(),
  type: z.literal("maintenance"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  indexation_logic: z
    .union([
      z.discriminatedUnion("type", [InseeIndexation, FixedIndexation]),
      z.null(),
    ])
    .nullish(),
  price_logic: z
    .union([z.discriminatedUnion("type", [FixedPrice, SharePrice]), z.null()])
    .nullish(),
  infos: z.union([MaintenanceInformation, z.null()]).nullish(),
});
export type PartialClauseMaintenance = z.infer<typeof PartialClauseMaintenance>;

export const PartialLease = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
  payment_schedule_freq: z.union([PaymentFreq, z.null()]).nullish(),
  type: z.literal("lease"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  products: z.union([z.array(LeaseProduct), z.null()]).nullish(),
  first_invoice_products: z.union([z.array(LeaseProduct), z.null()]).nullish(),
  indexation_formula: z.union([z.string(), z.null()]).nullish(),
  l_can_decrease: z.union([z.boolean(), z.null()]).nullish(),
  generate_answer: z.union([z.boolean(), z.null()]).nullish(),
  payment_mode: z.union([PaymentMode, z.null()]).nullish(),
  as_letter: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialLease = z.infer<typeof PartialLease>;

export const PartialCollectiveAutoconsumption = z.object({
  l_index_cfg: z.union([LIndexCfg, z.null()]).nullish(),
  indexation: z.union([z.array(IndexationParameters), z.null()]).nullish(),
  floor_l_to_one: z.union([z.boolean(), z.null()]).nullish(),
  use_precomputed: z.union([z.boolean(), z.null()]).nullish(),
  pre_computed: z
    .union([z.array(LIndexComputationDetails), z.null()])
    .nullish(),
  reference_date: z.coerce.date().nullable().nullish(),
  type: z.literal("collective_autoconsumption"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.union([z.number(), z.null()]).nullish(),
  excise_type: z.union([ExciseType, z.null()]).nullish(),
});
export type PartialCollectiveAutoconsumption = z.infer<
  typeof PartialCollectiveAutoconsumption
>;

export const PartialSunFlow = z.object({
  type: z.literal("sunflow_acc"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  fix_price: z.union([z.number(), z.null()]).nullish(),
  rule: z.union([MarketPriceType, z.null()]).nullish(),
  split_share: z.union([z.number(), z.null()]).nullish(),
});
export type PartialSunFlow = z.infer<typeof PartialSunFlow>;

export const PartialFlowTracking = z.object({
  type: z.literal("flow_tracking"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  price: z.union([z.number(), z.null()]).nullish(),
  exclude_neg_price_periods: z.union([z.boolean(), z.null()]).nullish(),
});
export type PartialFlowTracking = z.infer<typeof PartialFlowTracking>;

export const PartialBalancingMechanism = z.object({
  type: z.literal("balancing_mechanism"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  redistribution_share: z
    .union([z.number(), z.array(ShareCapacityTier), z.null()])
    .nullish(),
});
export type PartialBalancingMechanism = z.infer<
  typeof PartialBalancingMechanism
>;

export const PartialBalancingMechanismCapacityReservation = z.object({
  type: z.literal("bm_capacity_reservation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  redistribution_share: z.union([z.number(), z.null()]).nullish(),
});
export type PartialBalancingMechanismCapacityReservation = z.infer<
  typeof PartialBalancingMechanismCapacityReservation
>;

export const PartialBalancingMechanismCompensation = z.object({
  type: z.literal("balancing_mechanism_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type PartialBalancingMechanismCompensation = z.infer<
  typeof PartialBalancingMechanismCompensation
>;

export const PartialSmartOACompensation = z.object({
  type: z.literal("smart_oa_compensation"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type PartialSmartOACompensation = z.infer<
  typeof PartialSmartOACompensation
>;

export const PartialEETarif = z.object({
  type: z.literal("ee_tarif"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
  pricing: z
    .union([
      z.discriminatedUnion("type", [RealTarifPricing, FixedInflationPricing]),
      z.null(),
    ])
    .nullish(),
  prorata_temporis_mode: z.union([ProrataTemporisMode, z.null()]).nullish(),
});
export type PartialEETarif = z.infer<typeof PartialEETarif>;

export const PartialSupplyEnergy = z.object({
  type: z.literal("supply_energy"),
  stored_energy_params: z.union([PartialEnergyParameters, z.null()]).nullish(),
  clause_invoicing_cfg: z.union([InvoicingCfg, z.null()]).nullish(),
});
export type PartialSupplyEnergy = z.infer<typeof PartialSupplyEnergy>;

export const ClauseUpdateSchema = z.object({
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  alias: z.union([z.string(), z.null()]).nullish(),
  parameters: z
    .union([
      z.discriminatedUnion("type", [
        PartialCfdCr16,
        PartialCfdCr17,
        PartialCfdMajoration,
        PartialCfdNegPriceCompensation,
        PartialCfdM0YearlyDeviation,
        PartialFeedInTarifWindMonthly,
        PartialFeedInTarifSolar,
        PartialFeedInTarifSolarTrial,
        PartialDisconnectionCompensation,
        PartialFeedInTarifHydro,
        PartialMarketAccess,
        PartialSpotManagementFee,
        PartialMarketAccessImbalance,
        PartialPpaDa,
        PartialPpaDaIndexedFee,
        PartialPPAImbalanceIndexedFee,
        PartialPpaFixedManagementFee,
        PartialPpaFix,
        PartialPpaManagementFee,
        PartialPpaThreshold,
        PartialPpaForecastHedge,
        PartialPpaMultiTarif,
        PartialNegativePrice,
        PartialCreditNoteCap,
        PartialGoo,
        PartialCfdAutoconso,
        PartialAutoconso,
        PartialAutoconsoInseeIndexed,
        PartialSettlement,
        PartialUndeliveredEnergyPenalty,
        PartialFeedInTarifBiogas,
        PartialPpaThresholdMultiPeriod,
        PartialEnergyGuarantee,
        PartialEnergyGuaranteeRiskPremium,
        PartialPpaAsia,
        PartialGuaranteeOfCapacity,
        PartialClauseMaintenance,
        PartialLease,
        PartialCollectiveAutoconsumption,
        PartialSunFlow,
        PartialFlowTracking,
        PartialBalancingMechanism,
        PartialBalancingMechanismCapacityReservation,
        PartialBalancingMechanismCompensation,
        PartialSmartOACompensation,
        PartialEETarif,
        PartialSupplyEnergy,
      ]),
      z.null(),
    ])
    .nullish(),
});
export type ClauseUpdateSchema = z.infer<typeof ClauseUpdateSchema>;

export const CompanySchema = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
  url: z.union([z.string(), z.null()]).nullish(),
  registration_number: z.union([z.string(), z.null()]).nullish(),
  registration_office: z.union([z.string(), z.null()]).nullish(),
  vat_number: z.union([z.string(), z.null()]).nullish(),
  legal_type: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
  address: z.union([AddressSchema, z.null()]).nullish(),
  contact_info_associations: z.array(ContactInfoWithRolesSchema),
  external_id: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
  is_external: z.union([z.boolean(), z.null()]).nullish(),
});
export type CompanySchema = z.infer<typeof CompanySchema>;

export const PersonCreateSchema = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.string(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  kind: z.literal("person"),
  address_uuid: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
});
export type PersonCreateSchema = z.infer<typeof PersonCreateSchema>;

export const ContactInfoAssociationUpdateSchema = z.object({
  uuid: z.string().uuid(),
  roles: z.array(ContactRole).nullish(),
});
export type ContactInfoAssociationUpdateSchema = z.infer<
  typeof ContactInfoAssociationUpdateSchema
>;

export const CompanyKindCreate = z.literal("offtaker");
export type CompanyKindCreate = z.infer<typeof CompanyKindCreate>;

export const CompanySchemaCreate = z.object({
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
  url: z.union([z.string(), z.null()]).nullish(),
  registration_number: z.union([z.string(), z.null()]).nullish(),
  registration_office: z.union([z.string(), z.null()]).nullish(),
  vat_number: z.union([z.string(), z.null()]).nullish(),
  legal_type: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
  address_uuid: z.union([z.string(), z.null()]).nullish(),
  contact_info_associations: z
    .union([z.array(ContactInfoAssociationUpdateSchema), z.null()])
    .nullish(),
  kind: CompanyKindCreate,
});
export type CompanySchemaCreate = z.infer<typeof CompanySchemaCreate>;

export const create_legal_entity_Body = z.union([
  PersonCreateSchema,
  CompanySchemaCreate,
]);
export type create_legal_entity_Body = z.infer<typeof create_legal_entity_Body>;

export const CounterpartySummarySchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  logo: z.union([CloudImageUrl, z.null()]).nullish(),
  url: z.union([z.string(), z.null()]).nullish(),
  kind: CompanyKind,
  contracted_capacity: z.number(),
  contract_count: z.number().int(),
  contacts: z.array(ContactInfoSchema),
});
export type CounterpartySummarySchema = z.infer<
  typeof CounterpartySummarySchema
>;

export const CompanySchemaWithPermissions = z.object({
  uuid: z.string().uuid(),
  kind: CompanyKind,
  name: z.string(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
  url: z.union([z.string(), z.null()]).nullish(),
  registration_number: z.union([z.string(), z.null()]).nullish(),
  registration_office: z.union([z.string(), z.null()]).nullish(),
  vat_number: z.union([z.string(), z.null()]).nullish(),
  legal_type: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
  address: z.union([AddressSchema, z.null()]).nullish(),
  contact_info_associations: z.array(ContactInfoWithRolesSchema),
  external_id: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
  is_external: z.union([z.boolean(), z.null()]).nullish(),
  editable: z.boolean(),
});
export type CompanySchemaWithPermissions = z.infer<
  typeof CompanySchemaWithPermissions
>;

export const PersonUpdateSchema = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  address_uuid: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
});
export type PersonUpdateSchema = z.infer<typeof PersonUpdateSchema>;

export const CompanySchemaUpdate = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  name_short: z.union([z.string(), z.null()]).nullish(),
  cloud_image: CompanyCloudImages.nullish(),
  url: z.union([z.string(), z.null()]).nullish(),
  registration_number: z.union([z.string(), z.null()]).nullish(),
  registration_office: z.union([z.string(), z.null()]).nullish(),
  vat_number: z.union([z.string(), z.null()]).nullish(),
  legal_type: z.union([z.string(), z.null()]).nullish(),
  iban_code: z.union([z.string(), z.null()]).nullish(),
  bic_code: z.union([z.string(), z.null()]).nullish(),
  address_uuid: z.union([z.string(), z.null()]).nullish(),
  contact_info_associations: z
    .union([z.array(ContactInfoAssociationUpdateSchema), z.null()])
    .nullish(),
});
export type CompanySchemaUpdate = z.infer<typeof CompanySchemaUpdate>;

export const update_legal_entity_Body = z.union([
  PersonUpdateSchema,
  CompanySchemaUpdate,
]);
export type update_legal_entity_Body = z.infer<typeof update_legal_entity_Body>;

export const LegalEntityAccountingInfoUpdateSchema = z.object({
  external_id: z.union([z.string(), z.null()]).nullish(),
  accounting_id: z.union([z.string(), z.null()]).nullish(),
  einvoice_routing_address: z.union([z.string(), z.null()]).nullish(),
});
export type LegalEntityAccountingInfoUpdateSchema = z.infer<
  typeof LegalEntityAccountingInfoUpdateSchema
>;

export const freq__3 = z.union([z.string(), z.null()]).nullish().default("1YS");
export type freq__3 = z.infer<typeof freq__3>;

export const TimeSeriesJSON = z.object({
  index: z.array(z.number()),
  data: z.array(z.union([z.number(), z.null()])),
  name: z.string(),
});
export type TimeSeriesJSON = z.infer<typeof TimeSeriesJSON>;

export const SiteSummarySchema = z.object({
  capacity: z.number(),
  site_number: z.number().int(),
});
export type SiteSummarySchema = z.infer<typeof SiteSummarySchema>;

export const ContractSummarySchema = z.object({
  contract_number: z.number().int(),
  contract_status: ContractStatus,
});
export type ContractSummarySchema = z.infer<typeof ContractSummarySchema>;

export const TSMetadata = z.object({
  freq: z.string(),
  unit: z.string(),
  sprovider: z.union([z.string(), z.null()]).nullish(),
  sp_code: z.union([z.record(z.any()), z.null()]).nullish(),
  horizon_start: z.coerce.date().nullable().nullish(),
  horizon_end: z.coerce.date().nullable().nullish(),
});
export type TSMetadata = z.infer<typeof TSMetadata>;

export const TSMetadataUpdate = z.object({ sp_code: z.record(z.any()) });
export type TSMetadataUpdate = z.infer<typeof TSMetadataUpdate>;

export const TSMetadataTags = z.object({
  code: z.string(),
  category: z.string(),
  name: z.string(),
  rank: z.number().int(),
});
export type TSMetadataTags = z.infer<typeof TSMetadataTags>;

export const TSMetadataOverview = z.object({
  name: z.string(),
  freq: z.string(),
  unit: z.string(),
  sprovider: z.union([z.string(), z.null()]).nullish(),
  sp_code: z.union([z.record(z.any()), z.null()]).nullish(),
  tags: z.array(TSMetadataTags),
});
export type TSMetadataOverview = z.infer<typeof TSMetadataOverview>;

export const BadRequest = z.object({
  name: z.literal("BadRequest"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type BadRequest = z.infer<typeof BadRequest>;

export const version = z
  .union([z.string(), z.coerce.date(), z.null()])
  .nullish();
export type version = z.infer<typeof version>;

export const missing_freq_tol = z.union([z.number(), z.null()]).nullish();
export type missing_freq_tol = z.infer<typeof missing_freq_tol>;

export const EditTimeseriesDataRecord = z.object({
  timestamp: z.coerce.date(),
  value: z.number(),
});
export type EditTimeseriesDataRecord = z.infer<typeof EditTimeseriesDataRecord>;

export const EditTimeseriesData = z.object({
  records: z.array(EditTimeseriesDataRecord),
});
export type EditTimeseriesData = z.infer<typeof EditTimeseriesData>;

export const InvalidTimeseriesIndex = z.object({
  name: z.literal("InvalidTimeseriesIndex"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type InvalidTimeseriesIndex = z.infer<typeof InvalidTimeseriesIndex>;

export const VersionStatistics = z.object({
  min_index: z.coerce.date(),
  max_index: z.coerce.date(),
  completeness: z.number(),
  min_value: z.number(),
  max_value: z.number(),
  avg_value: z.number(),
  std_value: z.union([z.number(), z.null()]).nullish(),
  count_value: z.number().int(),
  version: z.coerce.date(),
});
export type VersionStatistics = z.infer<typeof VersionStatistics>;

export const Statistics = z.object({
  min_index: z.coerce.date(),
  max_index: z.coerce.date(),
  completeness: z.number(),
  min_value: z.number(),
  max_value: z.number(),
  avg_value: z.number(),
  std_value: z.union([z.number(), z.null()]).nullish(),
  count_value: z.number().int(),
  tz: z.string(),
  freq: z.string(),
  versions: z.array(VersionStatistics).nullish(),
  version_freq: z.union([z.string(), z.null()]).nullish(),
  version_delta_horizon: z.union([z.string(), z.null()]).nullish(),
});
export type Statistics = z.infer<typeof Statistics>;

export const Body_get_timeseries_ids_from_model_api__model___uuid__timeseries_tsids_post =
  z.object({
    tags: z.array(
      z.union([z.string(), z.array(z.union([z.string(), z.null()])), z.null()])
    ),
  });
export type Body_get_timeseries_ids_from_model_api__model___uuid__timeseries_tsids_post =
  z.infer<
    typeof Body_get_timeseries_ids_from_model_api__model___uuid__timeseries_tsids_post
  >;

export const Body_get_timeseries_data_from_model_api__model___uuid__timeseries_data_post =
  z.object({
    tags: z.array(
      z.union([z.string(), z.array(z.union([z.string(), z.null()])), z.null()])
    ),
  });
export type Body_get_timeseries_data_from_model_api__model___uuid__timeseries_data_post =
  z.infer<
    typeof Body_get_timeseries_data_from_model_api__model___uuid__timeseries_data_post
  >;

export const Body_get_timeseries_metadata_from_model_api__model___uuid__timeseries_metadata_post =
  z.object({
    tags: z.array(
      z.union([z.string(), z.array(z.union([z.string(), z.null()])), z.null()])
    ),
  });
export type Body_get_timeseries_metadata_from_model_api__model___uuid__timeseries_metadata_post =
  z.infer<
    typeof Body_get_timeseries_metadata_from_model_api__model___uuid__timeseries_metadata_post
  >;

export const EventType = z.enum(["stop", "downgraded", "informative"]);
export type EventType = z.infer<typeof EventType>;

export const EventCause = z.enum([
  "preventive_maintenance",
  "corrective_maintenance",
  "network",
  "breakdown",
  "weather",
  "disaster",
  "work_handover",
  "negative_price",
  "balancing_mechanism",
  "smart_oa",
  "intraday",
  "unknown",
]);
export type EventCause = z.infer<typeof EventCause>;

export const EventLosses = z.object({
  energy_loss: z.union([z.number(), z.null()]).nullish(),
  revenue_loss: z.union([z.number(), z.null()]).nullish(),
});
export type EventLosses = z.infer<typeof EventLosses>;

export const EnergyEstimationMode = z.enum(["rolling_pr_30days", "budget"]);
export type EnergyEstimationMode = z.infer<typeof EnergyEstimationMode>;

export const EventSource = z.enum([
  "manual",
  "power_production",
  "ai",
  "alemca",
  "smart_oa",
  "elmy",
  "tilt_energy",
  "symphonics",
]);
export type EventSource = z.infer<typeof EventSource>;

export const EventSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  description: z.union([z.string(), z.null()]).nullish(),
  is_external: z.boolean(),
  is_validated: z.boolean(),
  uuid: z.string().uuid(),
  site: SiteSchemaShort,
  available_capacity: z.number(),
  creation_date: z.coerce.date(),
  last_update: z.coerce.date(),
  event_type: EventType,
  cause: z.union([EventCause, z.null()]).nullish(),
  losses: z.record(EnergyEstimationMode, EventLosses),
  source: EventSource,
});
export type EventSchema = z.infer<typeof EventSchema>;

export const EventSchemaUpdate = z.object({
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  event_type: z.union([EventType, z.null()]).nullish(),
  cause: z.union([EventCause, z.null()]).nullish(),
  description: z.union([z.string(), z.null()]).nullish(),
  available_capacity: z.union([z.number(), z.null()]).nullish(),
  unavailable_capacity: z.union([z.number(), z.null()]).nullish(),
  available_percentage: z.union([z.number(), z.null()]).nullish(),
  unavailable_percentage: z.union([z.number(), z.null()]).nullish(),
  site_uuid: z.union([z.string(), z.null()]).nullish(),
  is_external: z.union([z.boolean(), z.null()]).nullish(),
  is_validated: z.union([z.boolean(), z.null()]).nullish(),
});
export type EventSchemaUpdate = z.infer<typeof EventSchemaUpdate>;

export const EventNotEditable = z.object({
  name: z.literal("EventNotEditable"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type EventNotEditable = z.infer<typeof EventNotEditable>;

export const EventSchemaCreate = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  description: z.union([z.string(), z.null()]).nullish(),
  is_external: z.boolean(),
  is_validated: z.boolean(),
  event_type: z.union([EventType, z.null()]).nullish(),
  cause: z.union([EventCause, z.null()]).nullish(),
  available_capacity: z.union([z.number(), z.null()]).nullish(),
  unavailable_capacity: z.union([z.number(), z.null()]).nullish(),
  available_percentage: z.union([z.number(), z.null()]).nullish(),
  unavailable_percentage: z.union([z.number(), z.null()]).nullish(),
  site_uuid: z.string().uuid(),
});
export type EventSchemaCreate = z.infer<typeof EventSchemaCreate>;

export const Body_delete_events_api_events_delete = z.object({
  uuids: z.array(z.string()),
});
export type Body_delete_events_api_events_delete = z.infer<
  typeof Body_delete_events_api_events_delete
>;

export const sources = z.union([z.array(EventSource), z.null()]).nullish();
export type sources = z.infer<typeof sources>;

export const causes = z.union([z.array(EventCause), z.null()]).nullish();
export type causes = z.infer<typeof causes>;

export const DocKind = z.enum([
  "invoice",
  "e_invoice",
  "invoice_proforma",
  "lease_receipt",
  "invoice_attachment",
  "invoicing_frame",
  "payment_schedule",
  "neg_price_compensation_extract",
  "contract",
  "contract_trial",
  "productivity_study",
  "production_report",
  "finance_report",
  "operation_report",
  "commissioning_report",
  "performance_test_report",
  "final_acceptance_report",
  "data_extract",
  "contract_data_extract",
  "directory_company_info",
  "directory_contact_info",
  "load_profile_extract",
  "cumulative_index_report",
  "finance_landing_extract",
  "finance_extract",
  "technical_data_extract",
  "budget_extract",
  "invoice_lines_report",
  "invoices_accounting_report",
  "commissioning_reserves_extract",
  "performance_tests_extract",
  "pr_report",
  "edf_oa_account_details",
  "data_warrant",
  "enedis_rp52",
  "ee_supply_invoice",
  "invoicing_request",
]);
export type DocKind = z.infer<typeof DocKind>;

export const DocumentQueryModelName = z.enum([
  "assetmanager",
  "assetowner",
  "holding",
  "site",
  "portfolio",
  "meter",
  "submeter",
  "headmeter",
  "contract",
  "invoice",
  "supplyinvoice",
]);
export type DocumentQueryModelName = z.infer<typeof DocumentQueryModelName>;

export const DocumentSchemaShort = z.object({
  uuid: z.string().uuid(),
  kind: z.union([DocKind, z.null()]).nullish(),
  filename: z.string(),
  creation_date: z.coerce.date(),
  last_update: z.coerce.date(),
  target_uuid: z.string().uuid(),
  target_model: DocumentQueryModelName,
  file_exists: z.boolean(),
  is_attachment: z.boolean(),
});
export type DocumentSchemaShort = z.infer<typeof DocumentSchemaShort>;

export const SupplyInvoiceDocumentNotDeletable = z.object({
  name: z.literal("SupplyInvoiceDocumentNotDeletable"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type SupplyInvoiceDocumentNotDeletable = z.infer<
  typeof SupplyInvoiceDocumentNotDeletable
>;

export const docgen_kind = z.union([DocKind, z.null()]).nullish();
export type docgen_kind = z.infer<typeof docgen_kind>;

export const Body_create_document_api__model___uuid__document_post = z.object({
  files: z.array(z.instanceof(File)),
  kind: DocKind,
  is_attachment: z.boolean().nullish(),
});
export type Body_create_document_api__model___uuid__document_post = z.infer<
  typeof Body_create_document_api__model___uuid__document_post
>;

export const DocGenConfigSchema = z.object({
  kind: DocKind,
  skeleton_article_name: z.union([z.string(), z.null()]).nullish(),
  article_names: z.union([z.array(z.string()), z.record(z.array(z.string()))]),
  static_input: z.record(z.any()),
  description: z.union([z.string(), z.null()]).nullish(),
});
export type DocGenConfigSchema = z.infer<typeof DocGenConfigSchema>;

export const DocumentSchema = z.object({
  uuid: z.string().uuid(),
  kind: z.union([DocKind, z.null()]).nullish(),
  filename: z.string(),
  creation_date: z.coerce.date(),
  last_update: z.coerce.date(),
  target_uuid: z.string().uuid(),
  target_model: DocumentQueryModelName,
  file_exists: z.boolean(),
  is_attachment: z.boolean(),
  docgen_config: z.union([DocGenConfigSchema, z.null()]),
  gen_input: z.record(z.any()),
});
export type DocumentSchema = z.infer<typeof DocumentSchema>;

export const Body_refresh_documents_api_document_post = z.object({
  uuids: z.array(z.string()),
});
export type Body_refresh_documents_api_document_post = z.infer<
  typeof Body_refresh_documents_api_document_post
>;

export const ArticleName = z.enum([
  "apex_ubl",
  "images_section",
  "annexe",
  "skeleton_invoice",
  "invoice_header",
  "invoice_coordinates",
  "invoice_site_infos_short",
  "invoice_thailand_header",
  "invoice_thailand_about",
  "invoice_site_infos",
  "invoice_site_infos_with_external_id",
  "invoice_lines",
  "invoice_l_index_details",
  "invoice_acc_allocation",
  "neg_price_compensation_extract",
  "data_warrant",
  "data_warrant_body",
  "data_warrant_appendice",
  "portfolio_summary",
  "gauge_metrics",
  "map_sites",
  "close_map",
  "wide_map",
  "revenue_chart",
  "data_table",
  "production_chart",
  "production_base_table",
  "operation_base_table",
  "operation_chart",
  "pr_chart",
  "self_consumption_prod_chart",
  "self_consumption_chart",
  "availability_chart",
  "timeline_base",
  "availability_timeline",
  "operation_timeline",
  "event_base_table",
  "event_summary",
  "site_bullet_points",
  "site_bullet_reception",
  "site_technical_details",
  "site_autoconso_report",
  "performance_summary",
  "reception_summary",
  "skeleton_report",
  "arkolia_one_page_skeleton",
  "coverpage",
  "portfolio_overview",
  "operation_overview",
  "site_operation_overview",
  "detailed_technical_view",
  "availability_overview",
  "operation_metric_charts",
  "production_overview",
  "production_details",
  "finance_details",
  "pr_details",
  "availability_details",
  "detailed_overviews",
  "detailed_operation_overviews",
  "availability_and_metrics_overview",
  "performance_test",
  "final_acceptance_report",
  "cumulative_index_report",
  "invoice_lines_report",
  "data_extraction",
  "contract_data",
  "partner_data",
  "finance_extract",
  "invoices_accounting_report",
  "commissioning_reserves_extract",
  "performance_tests_extract",
  "technical_data_extract",
  "budget_extract",
  "directory_company_info",
  "directory_contact_info",
  "finance_landing_extract",
  "invoicing_request",
  "skeleton_lease_receipt",
  "lease_receipt_lines",
  "lease_receipt_letter",
  "lease_receipt_answer",
  "lease_l_index_details",
]);
export type ArticleName = z.infer<typeof ArticleName>;

export const NoKwargsReport = z.object({
  uuids: z.array(z.string()),
  kind: z.enum([
    "performance_test_report",
    "final_acceptance_report",
    "commissioning_report",
    "invoice_lines_report",
    "directory_company_info",
    "directory_contact_info",
    "technical_data_extract",
    "budget_extract",
  ]),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
});
export type NoKwargsReport = z.infer<typeof NoKwargsReport>;

export const DateRangeReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.enum([
    "pr_report",
    "invoices_accounting_report",
    "contract_data_extract",
    "finance_landing_extract",
    "performance_tests_extract",
    "commissioning_reserves_extract",
    "neg_price_compensation_extract",
  ]),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
});
export type DateRangeReportKwargs = z.infer<typeof DateRangeReportKwargs>;

export const DataExtractCategory = z.enum([
  "realtime",
  "metering",
  "technical_metering",
  "producible",
  "operation",
  "revenue",
  "budget",
  "self_consumption",
  "negative_hours",
]);
export type DataExtractCategory = z.infer<typeof DataExtractCategory>;

export const DataExtractReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("data_extract"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  categories: z.array(DataExtractCategory).nullish().default(["metering"]),
  aggregate: z.boolean().nullish(),
  freq: z.enum(["1D", "1MS", "1YS", "1YS-JAN"]).nullish().default("1MS"),
});
export type DataExtractReportKwargs = z.infer<typeof DataExtractReportKwargs>;

export const CumIndexReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("cumulative_index_report"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  date: z.coerce.date().nullable().nullish(),
});
export type CumIndexReportKwargs = z.infer<typeof CumIndexReportKwargs>;

export const GSMetricName = z.enum([
  "total_consumption",
  "self_consumption_rate",
  "self_production_rate",
  "coverage_rate",
  "pr_operational_attained",
  "pr_operational_estimated",
  "pr_business_plan_estimated",
  "trs_operational_attained",
  "trs_operational_estimated",
  "trs_business_plan_estimated",
  "availability_injection",
  "availability_detailed",
  "availability_energy_prorated",
  "availability_technical",
  "production_loss_internal",
  "production_loss_global",
  "trs",
  "pr",
  "reactive_positive",
  "reactive_negative",
  "production",
  "consumption",
  "energy",
  "revenue",
]);
export type GSMetricName = z.infer<typeof GSMetricName>;

export const OperationReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("operation_report"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  title: z.string().nullish().default("Rapport d'Exploitation"),
  metric_names: z
    .array(GSMetricName)
    .nullish()
    .default([
      "production",
      "availability_energy_prorated",
      "availability_technical",
      "pr",
    ]),
  budget_scope: z.union([BudgetScope, z.null()]).nullish(),
  skip_event_table: z.union([z.boolean(), z.null()]).nullish(),
  curate_events: z.union([z.boolean(), z.null()]).nullish().default(true),
});
export type OperationReportKwargs = z.infer<typeof OperationReportKwargs>;

export const FinanceReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("finance_report"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  title: z.string().nullish().default("Rapport Financier"),
  metric_names: z
    .array(GSMetricName)
    .nullish()
    .default(["production", "revenue"]),
  budget_scope: z.union([BudgetScope, z.null()]).nullish(),
});
export type FinanceReportKwargs = z.infer<typeof FinanceReportKwargs>;

export const LoadProfileExtractReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("load_profile_extract"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  reference: z.union([z.string(), z.null()]).nullish(),
  ways: z
    .array(z.enum(["PROD", "CONS", "INJ"]))
    .nullish()
    .default(["PROD"]),
});
export type LoadProfileExtractReportKwargs = z.infer<
  typeof LoadProfileExtractReportKwargs
>;

export const FinanceExtractReportKwargs = z.object({
  uuids: z.array(z.string()),
  kind: z.literal("finance_extract"),
  filename: z.union([z.string(), z.null()]).nullish(),
  skip_articles: z.union([z.array(ArticleName), z.null()]).nullish(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  freq: z.enum(["1D", "1MS", "1YS"]).nullish().default("1MS"),
  is_external: z.union([z.boolean(), z.null()]).nullish(),
});
export type FinanceExtractReportKwargs = z.infer<
  typeof FinanceExtractReportKwargs
>;

export const generate_documents_Body = z.discriminatedUnion("kind", [
  NoKwargsReport,
  DateRangeReportKwargs,
  DataExtractReportKwargs,
  CumIndexReportKwargs,
  OperationReportKwargs,
  FinanceReportKwargs,
  LoadProfileExtractReportKwargs,
  FinanceExtractReportKwargs,
]);

export type generate_documents_Body = z.infer<typeof generate_documents_Body>;

export const DocGenConfigNotFound = z.object({
  name: z.literal("DocGenConfigNotFound"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type DocGenConfigNotFound = z.infer<typeof DocGenConfigNotFound>;

export const Body_download_documents_api_document_content_post = z.object({
  uuid: z.array(z.string()).max(150),
});
export type Body_download_documents_api_document_content_post = z.infer<
  typeof Body_download_documents_api_document_content_post
>;

export const DeliveryPointSchemaUpdate = z.object({
  code: z.union([z.string(), z.null()]).nullish(),
  reference: z.union([z.string(), z.null()]).nullish(),
  network_voltage: z.union([NetworkVoltageKind, z.null()]).nullish(),
  code_kind: z.union([DeliveryPointCodeKind, z.null()]).nullish(),
  type: z.union([DeliveryPointType, z.null()]).nullish(),
  extra: z.union([z.record(z.any()), z.null()]).nullish(),
  network_extra: z.union([EnedisNetworkCfg, z.null()]).nullish(),
});
export type DeliveryPointSchemaUpdate = z.infer<
  typeof DeliveryPointSchemaUpdate
>;

export const DPContractualMappingSchemaCreate = z.object({
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  external_site_ref: z.union([z.string(), z.null()]).nullish(),
  default_on_hold_reasons: z.array(OnHoldReason).nullish(),
  deliverypoint_uuid: z.string().uuid(),
});
export type DPContractualMappingSchemaCreate = z.infer<
  typeof DPContractualMappingSchemaCreate
>;

export const DPContractualMappingSchemaUpdate = z.object({
  purchase_order: z.union([z.string(), z.null()]).nullish(),
  tarif: z.union([z.number(), z.null()]).nullish(),
  external_site_ref: z.union([z.string(), z.null()]).nullish(),
  default_on_hold_reasons: z.array(OnHoldReason).nullish(),
});
export type DPContractualMappingSchemaUpdate = z.infer<
  typeof DPContractualMappingSchemaUpdate
>;

export const SiteWithoutAnyPRM = z.object({
  name: z.literal("SiteWithoutAnyPRM"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type SiteWithoutAnyPRM = z.infer<typeof SiteWithoutAnyPRM>;

export const Way = z.enum(["consumption", "production", "injection"]);
export type Way = z.infer<typeof Way>;

export const MeasureType = z.enum(["load_profile", "index", "invoicing"]);
export type MeasureType = z.infer<typeof MeasureType>;

export const Body_trigger_enedis_backfill_api_etl_enedis_site__uuid__backfill_post =
  z.object({
    ways: z
      .array(Way)
      .nullish()
      .default(["consumption", "production", "injection"]),
    measure_types: z
      .array(MeasureType)
      .nullish()
      .default(["load_profile", "index", "invoicing"]),
    start: z.coerce.date(),
    end: z.coerce.date(),
  });
export type Body_trigger_enedis_backfill_api_etl_enedis_site__uuid__backfill_post =
  z.infer<
    typeof Body_trigger_enedis_backfill_api_etl_enedis_site__uuid__backfill_post
  >;

export const SpFactoryOrder = z.object({
  sp_name: z.string(),
  ts_groups: z.array(TSAcquisitionGroup),
});
export type SpFactoryOrder = z.infer<typeof SpFactoryOrder>;

export const Body_trigger_sp_factory_backfill_api_etl_sp_factory_site__uuid__backfill_post =
  z.object({
    orders: z.array(SpFactoryOrder),
    start: z.coerce.date(),
    end: z.coerce.date(),
  });
export type Body_trigger_sp_factory_backfill_api_etl_sp_factory_site__uuid__backfill_post =
  z.infer<
    typeof Body_trigger_sp_factory_backfill_api_etl_sp_factory_site__uuid__backfill_post
  >;

export const _BaseGaugeModel = z.object({
  value: z.union([z.number(), z.null()]).nullish(),
  versus: z.union([z.number(), z.null()]).nullish(),
  budgeted_value: z.union([z.number(), z.null()]).nullish(),
  weight: z.number().nullish().default(1),
  diff: z.union([z.number(), z.null()]),
  rate: z.union([z.number(), z.null()]),
  ratio: z.union([z.number(), z.null()]),
});
export type _BaseGaugeModel = z.infer<typeof _BaseGaugeModel>;

export const GaugeModel = z.object({
  value: z.union([z.number(), z.null()]).nullish(),
  versus: z.union([z.number(), z.null()]).nullish(),
  budgeted_value: z.union([z.number(), z.null()]).nullish(),
  weight: z.number().nullish().default(1),
  gauge_budget: z.union([_BaseGaugeModel, z.null()]).nullish(),
  diff: z.union([z.number(), z.null()]),
  rate: z.union([z.number(), z.null()]),
  ratio: z.union([z.number(), z.null()]),
});
export type GaugeModel = z.infer<typeof GaugeModel>;

export const HorizonLabel = z.enum([
  "ytd",
  "yt_last_month",
  "yt_quarter",
  "last_year",
  "current_year",
  "rolling_year",
  "current_month",
  "last_month",
  "rolling_month",
  "current_week",
  "last_week",
  "rolling_week",
  "today",
  "yesterday",
  "custom",
  "rolling_0_30",
  "rolling_0_90",
  "rolling_30_60",
  "rolling_30_120",
]);
export type HorizonLabel = z.infer<typeof HorizonLabel>;

export const ComputedHorizon = z.object({
  label: HorizonLabel,
  base: z.array(z.coerce.date()).nullish(),
  versus: z.union([z.array(z.coerce.date()), z.null()]).nullish(),
  base_str: z.string(),
  versus_str: z.union([z.string(), z.null()]),
});
export type ComputedHorizon = z.infer<typeof ComputedHorizon>;

export const GSGaugeMetric = z.object({
  name: z.string(),
  gauge: GaugeModel,
  horizon: ComputedHorizon,
});
export type GSGaugeMetric = z.infer<typeof GSGaugeMetric>;

export const MachineType = z.enum([
  "wind_turbine",
  "solar_field",
  "cogeneration",
  "hydro_turbine",
  "consumption_site",
  "battery",
]);
export type MachineType = z.infer<typeof MachineType>;

export const technos__2 = z
  .union([z.array(z.union([MachineType, MachineSubType])), z.null()])
  .nullish();
export type technos__2 = z.infer<typeof technos__2>;

export const GSFinanceView = z.enum([
  "clause_product",
  "status",
  "status_price_mechanism",
  "price_mechanism",
  "last_year",
  "budget",
  "revenue",
]);
export type GSFinanceView = z.infer<typeof GSFinanceView>;

export const views = z.union([z.array(GSFinanceView), z.null()]).nullish();
export type views = z.infer<typeof views>;

export const GSNegPriceSiteSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  machine_type: MachineSubType,
  capacity: z.number(),
  negprice_nb_hours: TimeSeriesJSON,
  negprice_nb_hours_produced: TimeSeriesJSON,
  negprice_nb_hours_not_produced: TimeSeriesJSON,
  negprice_energy_produced: TimeSeriesJSON,
});
export type GSNegPriceSiteSchema = z.infer<typeof GSNegPriceSiteSchema>;

export const GSNegPriceChartSchema = z.object({
  sites: z.array(GSNegPriceSiteSchema),
});
export type GSNegPriceChartSchema = z.infer<typeof GSNegPriceChartSchema>;

export const GSSiteSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  dp_codes: z.array(z.string()),
  is_external: z.boolean(),
  assetowner: CompanySchemaShort,
  capacity: z.number(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
});
export type GSSiteSchema = z.infer<typeof GSSiteSchema>;

export const GSSchema = z.object({
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  freq: z.enum(["1MS", "1D"]),
  site: GSSiteSchema,
  last_update: z.coerce.date(),
});
export type GSSchema = z.infer<typeof GSSchema>;

export const GSRefreshModel = z.enum([
  "site",
  "assetowner",
  "assetmanager",
  "holding",
]);
export type GSRefreshModel = z.infer<typeof GSRefreshModel>;

export const GSDataCategory = z.enum([
  "realtime",
  "metering",
  "technical_metering",
  "producible",
  "operation",
  "budget",
  "self_consumption",
  "negative_hours",
]);
export type GSDataCategory = z.infer<typeof GSDataCategory>;

export const GSRefreshParams = z.object({
  model: GSRefreshModel,
  uuids: z.array(z.string()),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  refresh_freqs: z
    .array(z.enum(["1D", "1MS"]))
    .nullish()
    .default(["1D", "1MS"]),
  to_refresh: z
    .array(GSDataCategory)
    .nullish()
    .default([
      "realtime",
      "metering",
      "technical_metering",
      "producible",
      "operation",
      "budget",
      "self_consumption",
      "negative_hours",
    ]),
});
export type GSRefreshParams = z.infer<typeof GSRefreshParams>;

export const GSFinanceRefreshParams = z.object({
  site_uuids: z.union([z.array(z.string()), z.null()]).nullish(),
  contract_uuids: z.union([z.array(z.string()), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
});
export type GSFinanceRefreshParams = z.infer<typeof GSFinanceRefreshParams>;

export const GSKpiLevel = z.enum([
  "site",
  "assetowner",
  "holding",
  "regional",
  "portfolio",
  "region",
  "machine_type",
  "machine_subtype",
]);
export type GSKpiLevel = z.infer<typeof GSKpiLevel>;

export const aggregation_levels = z
  .union([z.array(GSKpiLevel), z.null()])
  .nullish();
export type aggregation_levels = z.infer<typeof aggregation_levels>;

export const SiteGSKpi = z.object({
  target_name: z.string(),
  is_external: z.boolean(),
  site_statuses: z.record(z.number().int()),
  techno: z.record(z.number().int()),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  pr: z.union([GaugeModel, z.null()]).nullish(),
  availability_injection: z.union([GaugeModel, z.null()]).nullish(),
  target_model: z.literal("site"),
  target_uuid: z.string().uuid(),
  portfolio: z.union([z.string(), z.null()]).nullish(),
  region: z.union([z.string(), z.null()]).nullish(),
});
export type SiteGSKpi = z.infer<typeof SiteGSKpi>;

export const SubGroupGSKpiUuid = z.object({
  target_name: z.string(),
  is_external: z.boolean(),
  site_statuses: z.record(z.number().int()),
  techno: z.record(z.number().int()),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  pr: z.union([GaugeModel, z.null()]).nullish(),
  availability_injection: z.union([GaugeModel, z.null()]).nullish(),
  target_model: GSKpiLevel,
  target_uuid: z.string().uuid(),
  children: z.array(SiteGSKpi),
});
export type SubGroupGSKpiUuid = z.infer<typeof SubGroupGSKpiUuid>;

export const SubGroupGSKpi = z.object({
  target_name: z.string(),
  is_external: z.boolean(),
  site_statuses: z.record(z.number().int()),
  techno: z.record(z.number().int()),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  pr: z.union([GaugeModel, z.null()]).nullish(),
  availability_injection: z.union([GaugeModel, z.null()]).nullish(),
  target_model: GSKpiLevel,
  children: z.array(SiteGSKpi),
});
export type SubGroupGSKpi = z.infer<typeof SubGroupGSKpi>;

export const GroupGSKpi = z.object({
  target_name: z.string(),
  is_external: z.boolean(),
  site_statuses: z.record(z.number().int()),
  techno: z.record(z.number().int()),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  pr: z.union([GaugeModel, z.null()]).nullish(),
  availability_injection: z.union([GaugeModel, z.null()]).nullish(),
  target_model: z.enum(["region", "machine_subtype", "machine_type"]),
  children: z.array(z.union([SiteGSKpi, SubGroupGSKpiUuid, SubGroupGSKpi])),
});
export type GroupGSKpi = z.infer<typeof GroupGSKpi>;

export const GroupGSKpiUuid = z.object({
  target_name: z.string(),
  is_external: z.boolean(),
  site_statuses: z.record(z.number().int()),
  techno: z.record(z.number().int()),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  pr: z.union([GaugeModel, z.null()]).nullish(),
  availability_injection: z.union([GaugeModel, z.null()]).nullish(),
  target_model: z.enum(["portfolio", "holding", "assetowner"]),
  target_uuid: z.string().uuid(),
  children: z.array(z.union([SiteGSKpi, SubGroupGSKpiUuid, SubGroupGSKpi])),
});
export type GroupGSKpiUuid = z.infer<typeof GroupGSKpiUuid>;

export const GSKpiMachineSubType = z.object({
  n_sites: z.number().int(),
  n_spvs: z.number().int(),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  production_operational_estimated: z.union([z.number(), z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  revenue_operational_estimated: z.union([z.number(), z.null()]).nullish(),
  techno: MachineSubType,
});
export type GSKpiMachineSubType = z.infer<typeof GSKpiMachineSubType>;

export const GSKpiTechno = z.object({
  n_sites: z.number().int(),
  n_spvs: z.number().int(),
  capacity: z.number(),
  production: z.union([GaugeModel, z.null()]).nullish(),
  production_operational_estimated: z.union([z.number(), z.null()]).nullish(),
  revenue: z.union([GaugeModel, z.null()]).nullish(),
  revenue_operational_estimated: z.union([z.number(), z.null()]).nullish(),
  techno: MachineType,
  children: z.array(GSKpiMachineSubType).nullish(),
});
export type GSKpiTechno = z.infer<typeof GSKpiTechno>;

export const AddressSchemaWithPermission = z.object({
  uuid: z.string().uuid(),
  name: z.union([z.string(), z.null()]).nullish(),
  address: z.union([z.string(), z.null()]).nullish(),
  address_extra: z.union([z.string(), z.null()]).nullish(),
  postal_box: z.union([z.string(), z.null()]).nullish(),
  city: z.union([z.string(), z.null()]).nullish(),
  postal_code: z.union([z.string(), z.null()]).nullish(),
  country: z.union([z.string(), z.null()]).nullish(),
  editable: z.boolean(),
});
export type AddressSchemaWithPermission = z.infer<
  typeof AddressSchemaWithPermission
>;

export const AddressSchemaCreate = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  address: z.union([z.string(), z.null()]).nullish(),
  address_extra: z.union([z.string(), z.null()]).nullish(),
  postal_box: z.union([z.string(), z.null()]).nullish(),
  city: z.union([z.string(), z.null()]).nullish(),
  postal_code: z.union([z.string(), z.null()]).nullish(),
  country: z.union([z.string(), z.null()]).nullish(),
});
export type AddressSchemaCreate = z.infer<typeof AddressSchemaCreate>;

export const AddressSchemaLong = z.object({
  uuid: z.string().uuid(),
  name: z.union([z.string(), z.null()]).nullish(),
  address: z.union([z.string(), z.null()]).nullish(),
  address_extra: z.union([z.string(), z.null()]).nullish(),
  postal_box: z.union([z.string(), z.null()]).nullish(),
  city: z.union([z.string(), z.null()]).nullish(),
  postal_code: z.union([z.string(), z.null()]).nullish(),
  country: z.union([z.string(), z.null()]).nullish(),
  editable: z.boolean(),
  owner: z.union([CompanySchemaShort, z.null()]),
  companies: z.array(CompanySchemaShort),
  sites: z.array(SiteSchemaTiny),
});
export type AddressSchemaLong = z.infer<typeof AddressSchemaLong>;

export const AddressSchemaUpdate = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  address: z.union([z.string(), z.null()]).nullish(),
  address_extra: z.union([z.string(), z.null()]).nullish(),
  postal_box: z.union([z.string(), z.null()]).nullish(),
  city: z.union([z.string(), z.null()]).nullish(),
  postal_code: z.union([z.string(), z.null()]).nullish(),
  country: z.union([z.string(), z.null()]).nullish(),
});
export type AddressSchemaUpdate = z.infer<typeof AddressSchemaUpdate>;

export const ContactInfoSchemaWithPermission = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  uuid: z.string().uuid(),
  team_name: z.union([z.string(), z.null()]).nullish(),
  position: z.union([z.string(), z.null()]).nullish(),
  company_phone: z.union([z.string(), z.null()]).nullish(),
  editable: z.boolean(),
});
export type ContactInfoSchemaWithPermission = z.infer<
  typeof ContactInfoSchemaWithPermission
>;

export const ContactInfoSchemaCreate = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  team_name: z.union([z.string(), z.null()]).nullish(),
  position: z.union([z.string(), z.null()]).nullish(),
  company_phone: z.union([z.string(), z.null()]).nullish(),
});
export type ContactInfoSchemaCreate = z.infer<typeof ContactInfoSchemaCreate>;

export const ContactInfoTargetSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  model: z.string(),
});
export type ContactInfoTargetSchema = z.infer<typeof ContactInfoTargetSchema>;

export const ContactInfoAssociationSchema = z.object({
  target: ContactInfoTargetSchema,
  roles: z.array(ContactRole),
});
export type ContactInfoAssociationSchema = z.infer<
  typeof ContactInfoAssociationSchema
>;

export const ContactInfoSchemaLong = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  uuid: z.string().uuid(),
  team_name: z.union([z.string(), z.null()]).nullish(),
  position: z.union([z.string(), z.null()]).nullish(),
  company_phone: z.union([z.string(), z.null()]).nullish(),
  editable: z.boolean(),
  owner: z.union([CompanySchemaShort, z.null()]),
  associations: z.array(ContactInfoAssociationSchema),
});
export type ContactInfoSchemaLong = z.infer<typeof ContactInfoSchemaLong>;

export const ContactInfoSchemaUpdate = z.object({
  civility: z.union([Civility, z.null()]).nullish(),
  firstname: z.union([z.string(), z.null()]).nullish(),
  lastname: z.union([z.string(), z.null()]).nullish(),
  email: z.union([z.string(), z.null()]).nullish(),
  mobile_phone: z.union([z.string(), z.null()]).nullish(),
  cloud_image: ContactCloudImages.nullish(),
  team_name: z.union([z.string(), z.null()]).nullish(),
  position: z.union([z.string(), z.null()]).nullish(),
  company_phone: z.union([z.string(), z.null()]).nullish(),
});
export type ContactInfoSchemaUpdate = z.infer<typeof ContactInfoSchemaUpdate>;

export const freq__4 = z.union([z.enum(["1MS", "1D"]), z.null()]).nullish();
export type freq__4 = z.infer<typeof freq__4>;

export const DHCheckKind = z.enum([
  "config",
  "completeness",
  "freshness",
  "consistency",
]);
export type DHCheckKind = z.infer<typeof DHCheckKind>;

export const checks = z.union([z.array(DHCheckKind), z.null()]).nullish();
export type checks = z.infer<typeof checks>;

export const DHCategory = z.enum(["metering", "finance", "operation"]);
export type DHCategory = z.infer<typeof DHCategory>;

export const categories = z.union([z.array(DHCategory), z.null()]).nullish();
export type categories = z.infer<typeof categories>;

export const DHFlagCode = z.enum([
  "BCKM023",
  "CNC",
  "CNS",
  "ELD_NOT_CONFIGURED",
  "EXPMO",
  "ISPC",
  "MDE",
  "MDPC",
  "MF",
  "MI",
  "MPO",
  "MPRM",
  "WPRM",
  "NC",
  "NCCI",
  "NMDAT",
  "PDFA",
  "R172_LATE",
  "INDEX_RESET",
  "DATA_INCONSISTENCY",
  "WCS",
]);
export type DHFlagCode = z.infer<typeof DHFlagCode>;

export const flag_codes = z.union([z.array(DHFlagCode), z.null()]).nullish();
export type flag_codes = z.infer<typeof flag_codes>;

export const DHComments = z.object({
  error: z.array(z.string()).nullish(),
  info: z.array(z.string()).nullish(),
  aggregated: z.array(z.string()).nullish(),
});
export type DHComments = z.infer<typeof DHComments>;

export const BaseDHSiteSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  dp_codes: z.array(z.string()),
  is_external: z.boolean(),
  assetowner: CompanySchemaShort,
  capacity: z.number(),
  portfolio: z.union([PortfolioSchema, z.null()]).nullish(),
  commissioning: z.coerce.date().nullable().nullish(),
});
export type BaseDHSiteSchema = z.infer<typeof BaseDHSiteSchema>;

export const DHAssignee = z.enum(["client", "renewex"]);
export type DHAssignee = z.infer<typeof DHAssignee>;

export const DHActionLabel = z.enum([
  "inspect_config",
  "manual_backfill",
  "ask_for_mandate",
  "configure_this_case",
  "configure_timeseries",
  "inspect_goldensource",
  "send_contract",
  "fix_contract_synchronization",
]);
export type DHActionLabel = z.infer<typeof DHActionLabel>;

export const DHJob = z.enum([
  "backfill",
  "enedis_inspect_orders_job",
  "enedis_order_measurements_job",
  "enedis_order_m023_backfill_files_job",
  "enedis_files_job",
  "edfoa_synchronize_contracts_job",
]);
export type DHJob = z.infer<typeof DHJob>;

export const BaseDHFlagSchema = z.object({
  code: DHFlagCode,
  name: z.string(),
  assignee: z.union([DHAssignee, z.null()]).nullish(),
  action_label: z.union([DHActionLabel, z.null()]).nullish(),
  job: z.union([DHJob, z.null()]).nullish(),
});
export type BaseDHFlagSchema = z.infer<typeof BaseDHFlagSchema>;

export const DHSchema = z.object({
  uuid: z.string().uuid(),
  freq: z.union([z.enum(["1MS", "1D"]), z.null()]).nullish(),
  score: z.number(),
  check: DHCheckKind,
  category: DHCategory,
  comments: DHComments,
  last_update: z.coerce.date(),
  site: BaseDHSiteSchema,
  flags: z.array(BaseDHFlagSchema),
});
export type DHSchema = z.infer<typeof DHSchema>;

export const BaseDHSchema = z.object({
  uuid: z.string().uuid(),
  freq: z.union([z.enum(["1MS", "1D"]), z.null()]).nullish(),
  score: z.number(),
  check: DHCheckKind,
  category: DHCategory,
  comments: DHComments,
  last_update: z.coerce.date(),
});
export type BaseDHSchema = z.infer<typeof BaseDHSchema>;

export const SiteDHSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  capacity: z.number(),
  dp_codes: z.array(z.string()),
  commissioning: z.coerce.date().nullable().nullish(),
  external_id: z.union([z.string(), z.null()]).nullish(),
  datahealths: z.array(BaseDHSchema),
  is_external: z.boolean(),
});
export type SiteDHSchema = z.infer<typeof SiteDHSchema>;

export const DHScoreSchema = z.object({
  score: z.number(),
  name: z.union([z.string(), z.null()]).nullish(),
  subscores: z.array(z.record(z.any())),
});
export type DHScoreSchema = z.infer<typeof DHScoreSchema>;

export const Body_datahealth_model_level_refresh_api__model___uuid__datahealth_refresh_post =
  z.object({
    checks: z.union([z.array(DHCheckKind), z.null()]).nullish(),
    categories: z.union([z.array(DHCategory), z.null()]).nullish(),
  });
export type Body_datahealth_model_level_refresh_api__model___uuid__datahealth_refresh_post =
  z.infer<
    typeof Body_datahealth_model_level_refresh_api__model___uuid__datahealth_refresh_post
  >;

export const name = z.union([PastillaName, z.null()]).nullish();
export type name = z.infer<typeof name>;

export const horizon_label = z.union([HorizonLabel, z.null()]).nullish();
export type horizon_label = z.infer<typeof horizon_label>;

export const status = z.union([PastillaStatus, z.null()]).nullish();
export type status = z.infer<typeof status>;

export const PastillaSchema = z.object({
  name: PastillaName,
  status: PastillaStatus,
  site: GSSiteSchema,
  horizon_label: HorizonLabel,
  sources: z.array(z.string()),
  gauge_value: z.union([z.number(), z.null()]).nullish(),
  budget_value: z.union([z.number(), z.null()]).nullish(),
  last_update: z.coerce.date(),
  last_status_change: z.coerce.date(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  comment: z.union([z.string(), z.null()]).nullish(),
});
export type PastillaSchema = z.infer<typeof PastillaSchema>;

export const FilterKind = z.enum([
  "regions",
  "assetowners",
  "portfolios",
  "counterparts",
  "contract_types",
  "clause_types",
  "data_origins",
  "deliverypoints",
  "network_voltages",
  "dsos",
  "external_ids",
]);
export type FilterKind = z.infer<typeof FilterKind>;

export const NoteTagSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  category: z.union([z.string(), z.null()]).nullish(),
  color: z.union([z.string(), z.null()]).nullish(),
  description: z.union([z.string(), z.null()]).nullish(),
});
export type NoteTagSchema = z.infer<typeof NoteTagSchema>;

export const NoteQueryModelName = z.enum([
  "assetmanager",
  "assetowner",
  "holding",
  "site",
  "portfolio",
  "contract",
  "invoice",
]);
export type NoteQueryModelName = z.infer<typeof NoteQueryModelName>;

export const NoteTarget = z.object({
  target_uuid: z.string().uuid(),
  target_model: NoteQueryModelName,
  target_name: z.union([z.string(), z.null()]).nullish(),
});
export type NoteTarget = z.infer<typeof NoteTarget>;

export const NoteSchema = z.object({
  uuid: z.string().uuid(),
  body: z.union([z.string(), z.null()]).nullish(),
  tags: z.array(NoteTagSchema),
  target: NoteTarget,
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  creation_date: z.coerce.date(),
  update_date: z.coerce.date(),
  created_by: z.union([z.string(), z.null()]).nullish(),
  updated_by: z.union([z.string(), z.null()]).nullish(),
  pinned: z.boolean(),
  level: z.union([z.string(), z.null()]).nullish(),
  archived: z.boolean(),
});
export type NoteSchema = z.infer<typeof NoteSchema>;

export const NoteSchemaCreate = z.object({
  body: z.string(),
  tags: z.union([z.array(z.string()), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  pinned: z.union([z.boolean(), z.null()]).nullish(),
  level: z.union([z.string(), z.null()]).nullish(),
});
export type NoteSchemaCreate = z.infer<typeof NoteSchemaCreate>;

export const NoteSchemaUpdate = z.object({
  body: z.union([z.string(), z.null()]).nullish(),
  tags: z.union([z.array(z.string()), z.null()]).nullish(),
  start: z.coerce.date().nullable().nullish(),
  end: z.coerce.date().nullable().nullish(),
  pinned: z.union([z.boolean(), z.null()]).nullish(),
  level: z.union([z.string(), z.null()]).nullish(),
  target: z.union([NoteTarget, z.null()]).nullish(),
});
export type NoteSchemaUpdate = z.infer<typeof NoteSchemaUpdate>;

export const NoteTagSchemaCreate = z.object({
  name: z.string(),
  code: z.string(),
  category: z.union([z.string(), z.null()]).nullish(),
  color: z.union([z.string(), z.null()]).nullish(),
  description: z.union([z.string(), z.null()]).nullish(),
});
export type NoteTagSchemaCreate = z.infer<typeof NoteTagSchemaCreate>;

export const NoteTagSchemaUpdate = z.object({
  name: z.union([z.string(), z.null()]).nullish(),
  code: z.union([z.string(), z.null()]).nullish(),
  category: z.union([z.string(), z.null()]).nullish(),
  color: z.union([z.string(), z.null()]).nullish(),
  description: z.union([z.string(), z.null()]).nullish(),
});
export type NoteTagSchemaUpdate = z.infer<typeof NoteTagSchemaUpdate>;

export const ReportingFileNotGenerated = z.object({
  name: z.literal("ReportingFileNotGenerated"),
  message: z.string(),
  detail: z.union([z.unknown(), z.null()]).nullish(),
});
export type ReportingFileNotGenerated = z.infer<
  typeof ReportingFileNotGenerated
>;

export const endpoints = makeApi([
  {
    method: "get",
    path: "/api/:model/:uuid/artifacts",
    alias: "get_artifacts_filtered_by_model",
    description: `List the artifacts (source documents received for parsing) attached to a
site whose processing logs fall within the horizon, ordered by code.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.literal("site"),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "parse_flows",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
    ],
    response: z.array(ArtifactSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted site.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/contracts",
    alias: "get_contracts_filtered_by_model",
    description: `List the contracts attached to a site, or to every site of a company
(assetmanager, assetowner or holding), ordered by site name then most
recent start date.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetmanager", "assetowner", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "clause_type",
        type: "Query",
        schema: z.array(ClauseTypes).nullish().default([]),
      },
    ],
    response: z.array(BaseContractSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of the given model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/datahealth",
    alias: "get_datahealth",
    description: `List data-health scores for the operating sites in scope.

Each datahealth grades one data check (per category and frequency) on a
site; results are sorted by site name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "assetmanager",
          "holding",
          "portfolio",
          "region",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "freq",
        type: "Query",
        schema: freq__4,
      },
      {
        name: "checks",
        type: "Query",
        schema: checks,
      },
      {
        name: "categories",
        type: "Query",
        schema: categories,
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "technos",
        type: "Query",
        schema: technos__2,
      },
      {
        name: "flag_codes",
        type: "Query",
        schema: flag_codes,
      },
    ],
    response: z.array(DHSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the target model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of &#x60;model&#x60; found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/datahealth/refresh",
    alias: "datahealth_model_level_refresh",
    description: `Refresh every datahealth attached to the given scope.

A single site is refreshed synchronously and returned with its updated
datahealths. Any larger scope (company, holding, portfolio, region) is
delegated to a Dagster job and the endpoint returns immediately without
a body.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_datahealth_model_level_refresh_api__model___uuid__datahealth_refresh_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "assetmanager",
          "holding",
          "portfolio",
          "region",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.union([SiteDHSchema, z.null()]),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the target model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of &#x60;model&#x60; found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/document",
    alias: "get_documents_filtered_by_model",
    description: `List documents of the given model and of its related child models.

Children are included according to the following rules:
- AssetManager : AM + Holding files
- Holding : Holding + AO files
- AssetOwner : AO + Site files
- Site : Site + Contract files + DeliveryPoints files
- Contract : Contract + Invoices files`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "site",
          "portfolio",
          "meter",
          "submeter",
          "headmeter",
          "contract",
          "invoice",
          "supplyinvoice",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "docgen_kind",
        type: "Query",
        schema: docgen_kind,
      },
    ],
    response: z.array(DocumentSchemaShort),
    errors: [
      {
        status: 404,
        description: `No model instance for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/document",
    alias: "create_document",
    description: `Upload external files and attach them as documents to a model instance.

Files are stored on object storage and marked as external (not generated
by zefire).`,
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_create_document_api__model___uuid__document_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "site",
          "portfolio",
          "meter",
          "submeter",
          "headmeter",
          "contract",
          "invoice",
          "supplyinvoice",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(DocumentSchemaShort),
    errors: [
      {
        status: 404,
        description: `No model instance for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/document/detailed",
    alias: "get_detailed_document_by_model",
    description: `List the documents directly attached to the given model instance,
including their generation config and inputs (no child models included,
contrary to the non-detailed listing).`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "site",
          "portfolio",
          "meter",
          "submeter",
          "headmeter",
          "contract",
          "invoice",
          "supplyinvoice",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "docgen_kind",
        type: "Query",
        schema: docgen_kind,
      },
    ],
    response: z.array(DocumentSchema),
    errors: [
      {
        status: 404,
        description: `No model instance for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/events",
    alias: "get_events_filtered_by_model",
    description: `List events (unavailability/curtailment periods) for the sites in scope.

Events overlapping the &#x60;[start, end[&#x60; horizon are returned, sorted by
site name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "portfolio_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "is_event_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "is_site_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "technos",
        type: "Query",
        schema: technos,
      },
      {
        name: "sources",
        type: "Query",
        schema: sources,
      },
      {
        name: "causes",
        type: "Query",
        schema: causes,
      },
      {
        name: "assetowner_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "buyer_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "network_voltage_codes",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "is_validated",
        type: "Query",
        schema: is_external,
      },
    ],
    response: z.array(EventSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the target model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of &#x60;model&#x60; found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource",
    alias: "get_goldensource_filtered_by_model",
    description: `List the site golden sources (one per site and frequency) under the
target scope, with their available data horizon and last refresh time.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "freq",
        type: "Query",
        schema: z.enum(["1D", "1MS"]),
      },
    ],
    response: z.array(GSSchema),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/finance/timeseries",
    alias: "get_goldensource_finance_timeseries",
    description: `Return the finance golden-source timeseries (revenues per view) for a
site or an aggregation scope, as one JSON-serialized dataframe per
finance view.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "site",
          "assetowner",
          "holding",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: z.enum(["1D", "1MS"]).nullish().default("1MS"),
      },
      {
        name: "technos",
        type: "Query",
        schema: technos__2,
      },
      {
        name: "views",
        type: "Query",
        schema: views,
      },
      {
        name: "budget_percentile",
        type: "Query",
        schema: z.enum(["p50", "p90", "budget_default"]).nullish(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/gauges",
    alias: "get_goldensource_gauges",
    description: `Compute several golden-source gauge metrics (realized vs budget vs
comparison horizon) in a single request, for a site or an aggregation
scope (assetowner, assetmanager, holding). Used by the dashboard gauge
widgets.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "names",
        type: "Query",
        schema: z.array(GSMetricName),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: unit,
      },
      {
        name: "comparison_horizon_type",
        type: "Query",
        schema: z.enum(["last_year", "preceding", "none"]),
      },
      {
        name: "budget_percentile",
        type: "Query",
        schema: z.enum(["p50", "p90", "budget_default"]).nullish(),
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
    ],
    response: z.array(GSGaugeMetric),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid, or the target site has no golden source at the resolved frequency (&#x60;MissingDataError&#x60;).`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/kpis",
    alias: "get_goldensource_kpis",
    description: `Compute portfolio-level KPIs (production, revenue, availability, ...)
from the golden source of the sites under the target company scope,
optionally broken down by aggregation level.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "aggregation_levels",
        type: "Query",
        schema: aggregation_levels,
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "technos",
        type: "Query",
        schema: technos__2,
      },
      {
        name: "portfolios",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "regions",
        type: "Query",
        schema: portfolio_names,
      },
    ],
    response: z.array(
      z.union([
        SiteGSKpi,
        GroupGSKpi,
        GroupGSKpiUuid,
        SubGroupGSKpi,
        SubGroupGSKpiUuid,
      ])
    ),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/kpis/techno",
    alias: "get_goldensource_kpis_per_techno",
    description: `Compute golden-source KPIs broken down by technology (solar, wind,
hydro, ...) for the sites under the target company scope.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "budget_percentile",
        type: "Query",
        schema: z.enum(["p50", "p90", "budget_default"]).nullish(),
      },
    ],
    response: z.array(GSKpiTechno),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/negprices",
    alias: "get_goldensource_negprices",
    description: `Return per-site negative-price exposure timeseries (hours produced /
curtailed during negative spot prices, and the energy produced during
those hours) for the sites under the target scope. Sites without any
negative-price data are excluded.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "freq",
        type: "Query",
        schema: z.enum(["1D", "1MS"]),
      },
    ],
    response: GSNegPriceChartSchema,
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/goldensource/timeseries",
    alias: "get_goldensource_timeseries",
    description: `Return golden-source timeseries data (production, availability,
budget, ...) for a site or an aggregation scope, as a JSON-serialized
dataframe. This is the data source of the golden-source charts.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "assetowner", "assetmanager", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "chart_type",
        type: "Query",
        schema: z
          .enum(["production", "operation", "self_consumption"])
          .nullish(),
      },
      {
        name: "columns",
        type: "Query",
        schema: z.array(z.string()).nullish(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "freq",
        type: "Query",
        schema: z.enum(["1D", "1MS", "1YS", "1YS-JAN"]),
      },
      {
        name: "budget_percentile",
        type: "Query",
        schema: z.enum(["p50", "p90", "budget_default"]).nullish(),
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "technos",
        type: "Query",
        schema: technos__2,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices",
    alias: "get_invoices_filtered_by_model",
    description: `List the invoices attached to a model (site, contract, asset owner,
holding or asset manager), optionally restricted to a [start, end[
horizon on their invoicing period and narrowed by body filters.
For a site, every invoice of a contract bound to that site only is returned; on a
multi-site contract, only invoices with at least one line targeting that site, its
asset owner, or one of its delivery points are.

For company-scope models (assetmanager, holding, assetowner) the horizon
is mandatory unless the query is narrowed with the &#x27;uuids&#x27; filter, as the
full invoice history is too expensive to compute.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: InvoiceQueryFilters,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "holding",
          "assetmanager",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
    ],
    response: z.array(InvoiceSchemaShort),
    errors: [
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Missing [start, end[ horizon on a company-scope listing (assetmanager, holding, assetowner) without a &#x27;uuids&#x27; filter.`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/invoices/accountable",
    alias: "get_accountable_invoices",
    description: `List the invoices of a model that are ready to be accounted but not yet
accounted, for export to the accounting system. Restricted to
asset-manager users.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "holding",
          "assetmanager",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "only_paid",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "scopes",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "accounting_date_start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "accounting_date_end",
        type: "Query",
        schema: commissioning_from,
      },
    ],
    response: z.array(InvoiceSchemaSync),
    errors: [
      {
        status: 403,
        description: `The authenticated user&#x27;s company is not an AssetManager.`,
        schema: SecurityError,
      },
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices/compute",
    alias: "compute_invoice_from_model",
    description: `Trigger the computation of the invoices of a site or contract for the
selected [start, end[ horizon, or refresh a single invoice (its own
horizon is used and the body horizon is discarded). Returns the invoices
grouped by computation outcome (created, refreshed, ...). A failed single-invoice
refresh rolls back its changes and commits only the computation error log (and, when
a meter change could not be applied, the contract&#x27;s counter-reset hold) before raising.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_compute_invoice_from_model_api__model___uuid__invoices_compute_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "contract", "invoice"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InvoiceComputationSchema,
    errors: [
      {
        status: 400,
        description: `Refreshing a specific invoice whose computation can no longer be replaced (non-refreshable status), or a credit note (its lines are those of the invoice it cancels).`,
        schema: InvoiceStatusError,
      },
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Invoice lines have missing or ambiguous targets, or cannot be split at the contract&#x27;s invoicing granularity.`,
        schema: InvoiceLineTargetError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices/payment_schedule",
    alias: "create_payment_schedule_from_model",
    description: `Create the payment schedule invoices of an autoconso contract on the
selected [start, end[ horizon. Fails with 409 while the contract&#x27;s
invoicing is on hold.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_create_payment_schedule_from_model_api__model___uuid__invoices_payment_schedule_post,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(InvoiceSchema),
    errors: [
      {
        status: 404,
        description: `No contract found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `The contract&#x27;s invoicing is on hold.`,
        schema: InvoicingOnHoldError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices/publish",
    alias: "publish_invoice_from_model",
    description: `Publish the invoices of a site or contract for the selected horizon, or a
single invoice (its own horizon is used and the body horizon is
discarded). Only invoices in a publishable status get their pdf document
generated and move to &#x27;published&#x27; (or &#x27;draft&#x27;); the others are returned
untouched. Site-scoped actions select the sites covered by invoice lines,
falling back to the contract&#x27;s sites for invoices without lines.

Asset-manager-sourced invoices are numbered by the asset manager (see
POST /invoices/{uuid}/publish_with_id), so only their draft document can be
generated here: a definitive publication is rejected with 422.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_publish_invoice_from_model_api__model___uuid__invoices_publish_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "contract", "invoice"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(InvoiceSchema),
    errors: [
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `The invoicing of a targeted contract is on hold (definitive publication only: draft publication stays allowed).`,
        schema: InvoicingOnHoldError,
      },
      {
        status: 422,
        description: `A targeted invoice is not numbered by Renewex (its invoice source, frozen on the invoice else its contract&#x27;s, is &#x27;assetmanager&#x27; or a buyer one): only draft publication is available for those.`,
        schema: InvoicePublicationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/invoices/recover",
    alias: "get_invoices_to_recover",
    description: `List, per site of the company, the revenue amounts still to be recovered
(computed/validated/paid, from the GoldenSource finance view) together with
the contracts and the count of recoverable invoices since the start of
Renewex invoicing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetowner", "assetmanager"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(InvoicesToRecoverSchema),
    errors: [
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices/submit",
    alias: "submit_invoice_from_model",
    description: `Submit the invoices of a site or contract for the selected horizon, or a
single invoice (its own horizon is used and the body horizon is
discarded). Only invoices in a submittable status are submitted —
published first when needed, then moved to &#x27;submitting&#x27; according to the
contract&#x27;s submit channel; the others are returned untouched.
Internal credit notes are never submitted: they are left untouched on a site
or contract, and a single one fails with 409.
Site-scoped actions select the sites covered by invoice lines, falling back
to the contract&#x27;s sites for invoices without lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_submit_invoice_from_model_api__model___uuid__invoices_submit_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum(["site", "contract", "invoice"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(InvoiceSchema),
    errors: [
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `&#x60;InvoicingOnHoldError&#x60; — the invoicing of a targeted contract is on hold; &#x60;InternalCreditNoteNotSubmittable&#x60; — the targeted invoice is an internal credit note, which is never submitted.`,
        schema: z.union([
          InvoicingOnHoldError,
          InternalCreditNoteNotSubmittable,
        ]),
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/invoices/summary",
    alias: "get_invoices_summary_filtered_by_model",
    description: `Summarize the invoices of a model over a mandatory [start, end[ horizon:
invoice uuids grouped by invoicing group status, then invoice status, then
calendar month (CET) of computable_asof.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: InvoiceQueryFilters,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "holding",
          "assetmanager",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "with_amounts",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(InvoicingGroup),
    errors: [
      {
        status: 404,
        description: `No model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/invoices/sync",
    alias: "get_invoices_filtered_by_last_meaningful_update",
    description: `List the invoices of a model whose latest meaningful log action (in the
given statuses) falls within the [start, end[ horizon, so an ERP can sync
only what changed. Restricted to asset-manager users.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "assetowner",
          "holding",
          "assetmanager",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "statuses",
        type: "Query",
        schema: z.array(InvoiceStatus).nullish().default([]),
      },
    ],
    response: z.array(InvoiceSchemaSync),
    errors: [
      {
        status: 403,
        description: `The authenticated user&#x27;s company is not an AssetManager.`,
        schema: SecurityError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/note",
    alias: "get_notes_filtered_by_model",
    description: `List notes attached to a model instance and to its business relations.

Depending on the model, notes attached to related entities are included
(e.g. a site&#x27;s notes include those of its contracts and invoices), sorted
most recent first.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "site",
          "portfolio",
          "contract",
          "invoice",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "tags",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "include_archived",
        type: "Query",
        schema: is_external,
      },
      {
        name: "pinned",
        type: "Query",
        schema: is_external,
      },
      {
        name: "level",
        type: "Query",
        schema: unit,
      },
      {
        name: "get_all_children",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(NoteSchema),
    errors: [
      {
        status: 404,
        description: `No model instance for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/note",
    alias: "create_note",
    description: `Create a note attached to a model instance.

&#x60;&#x60;pinned&#x60;&#x60; and &#x60;&#x60;level&#x60;&#x60; are stored as tags on the note. Creating a note
with level &#x27;action&#x27; also posts an alert to the user company&#x27;s Slack
channel, when one is configured.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: NoteSchemaCreate,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "site",
          "portfolio",
          "contract",
          "invoice",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NoteSchema,
    errors: [
      {
        status: 404,
        description: `No model instance for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/pastillas",
    alias: "get_pastillas_filtered_by_model",
    description: `List the supervision pastillas (health indicators computed per site and
horizon) visible to the user, scoped to a site or a whole company, ordered
by site name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetmanager", "site", "assetowner", "holding"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "name",
        type: "Query",
        schema: name,
      },
      {
        name: "horizon_label",
        type: "Query",
        schema: horizon_label,
      },
      {
        name: "status",
        type: "Query",
        schema: status,
      },
    ],
    response: z.array(PastillaSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of the given model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/pastillas/underperforming",
    alias: "get_underperforming_pastillas",
    description: `List the performance-ratio pastillas of the company&#x27;s sites currently in
warning or error status, i.e. the sites flagged as underperforming, ordered
by site name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["holding", "assetowner", "assetmanager"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(PastillaSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted company.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of the given model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/sites",
    alias: "get_sites_filtered_by_model",
    description: `List the sites attached to the given scoping model (asset manager, asset
owner, holding, portfolio, region, or a single site), restricted to the
current user&#x27;s organization. All filters are optional and combined with
a logical AND; omitted filters do not restrict the listing.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "portfolio",
          "region",
          "site",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "is_external",
        type: "Query",
        schema: is_external,
      },
      {
        name: "statuses",
        type: "Query",
        schema: statuses,
      },
      {
        name: "technos",
        type: "Query",
        schema: technos,
      },
      {
        name: "portfolio_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "no_portfolio",
        type: "Query",
        schema: is_external,
      },
      {
        name: "assetowner_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "contract_statuses",
        type: "Query",
        schema: contract_statuses,
      },
      {
        name: "purchase_orders",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "data_origins",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "commissioning_from",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "commissioning_to",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "creation_date_from",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "creation_date_to",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "dp_codes",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "dp_references",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "site_types",
        type: "Query",
        schema: site_types,
      },
      {
        name: "network_voltage_codes",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "dso_names",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "external_ids",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "has_budget",
        type: "Query",
        schema: is_external,
      },
    ],
    response: z.array(SiteSchemaOverview),
    errors: [
      {
        status: 404,
        description: `No object of the given model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/sites/datahealth",
    alias: "get_sites_with_datahealth_filtered_by_model",
    description: `List the operating sites in scope, each with its datahealths grouped.

Sites without any datahealth are still returned, with an empty
&#x60;datahealths&#x60; list; results are sorted by site name.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "assetowner",
          "holding",
          "portfolio",
          "region",
          "site",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(SiteDHSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the target model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of &#x60;model&#x60; found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/:model/:uuid/supply-invoices",
    alias: "get_supply_invoices",
    description: `Supply invoices of an energy-efficiency site, with yearly averages and proposed tarifs.

Yearly summaries cover the energy-efficiency clauses: per energy for &#x60;real_tarif&#x60; ones, to
validate their proposed tarifs against the recorded ones; pooled for &#x60;fixed_inflation&#x60; ones,
whose redevance is set by the contract. Other sites have no yearly summaries.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.literal("site"),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteSupplyInvoices,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/timeseries/data",
    alias: "get_timeseries_data_from_model",
    description: `Read the records of the timeseries attached to an object (resolved by
tag filters, walking down the object&#x27;s hierarchy), as a single dataframe.
Timestamps are returned in UTC. Fails if any matched timeseries has no
data on the horizon.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_get_timeseries_data_from_model_api__model___uuid__timeseries_data_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "contract",
          "invoice",
          "offtaker",
          "assetmanager_legal_entity_link",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "version",
        type: "Query",
        schema: version,
      },
      {
        name: "freq",
        type: "Query",
        schema: unit,
      },
      {
        name: "unit",
        type: "Query",
        schema: unit,
      },
      {
        name: "missing_freq_tol",
        type: "Query",
        schema: missing_freq_tol,
      },
      {
        name: "io_format",
        type: "Query",
        schema: io_format,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/timeseries/metadata",
    alias: "get_timeseries_metadata_from_model",
    description: `Return the metadata (name, frequency, unit, service provider, tags) of
the timeseries attached to an object, resolved by tag filters while
walking down the object&#x27;s hierarchy.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_get_timeseries_metadata_from_model_api__model___uuid__timeseries_metadata_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "contract",
          "invoice",
          "offtaker",
          "assetmanager_legal_entity_link",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "stop_at_first",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(TSMetadataOverview),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/:uuid/timeseries/tsids",
    alias: "get_timeseries_ids_from_model",
    description: `List the names (timeserie_ids) of the timeseries attached to an
object, filtered by tags. For most models the lookup walks down the
object&#x27;s hierarchy; counterparty models only expose their own timeseries.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_get_timeseries_ids_from_model_api__model___uuid__timeseries_tsids_post,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "site",
          "contract",
          "invoice",
          "offtaker",
          "assetmanager_legal_entity_link",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(z.string()),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/:model/document",
    alias: "generate_documents",
    description: `Generate one report document per target uuid.

A Document record is created immediately for each target; the file itself
is produced asynchronously on Dagster unless &#x60;&#x60;wait&#x60;&#x60; is true. The
filename defaults to a timestamped name built from the target and report
kind when not provided.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: generate_documents_Body,
      },
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetmanager", "assetowner", "holding", "site"]),
      },
      {
        name: "wait",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(DocumentSchemaShort),
    errors: [
      {
        status: 404,
        description: `At least one of the target uuids matches no model instance.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/addresses",
    alias: "get_addresses",
    description: `List the addresses in the user&#x27;s scope (linked to its contract parties,
contracts, sites, holdings or owned by the company), each with an
&#x60;editable&#x60; flag reflecting the user&#x27;s write permission.`,
    requestFormat: "json",
    response: z.array(AddressSchemaWithPermission),
  },
  {
    method: "post",
    path: "/api/addresses",
    alias: "create_address",
    description: `Create an address, owned by the user&#x27;s company (or Renewex when the
user&#x27;s company cannot own objects).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddressSchemaCreate,
      },
    ],
    response: AddressSchema,
    errors: [
      {
        status: 409,
        description: `An identical address already exists.`,
        schema: ObjectAlreadyExists,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/addresses/:uuid",
    alias: "get_address_details",
    description: `Get an address&#x27; details, including the companies and sites it is
linked to (restricted to those in the user&#x27;s scope) and its owner.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AddressSchemaLong,
    errors: [
      {
        status: 404,
        description: `No address found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/addresses/:uuid",
    alias: "update_address",
    description: `Update an address. Requires write permission on the address
(owned by the user&#x27;s company or one of its asset owners).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddressSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AddressSchema,
    errors: [
      {
        status: 404,
        description: `No address found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/addresses/:uuid",
    alias: "delete_address",
    description: `Delete an address. Requires write permission on the address.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No address found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/artifacts/:uuid",
    alias: "get_artifact",
    description: `Retrieve a single artifact together with its full processing history.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ArtifactSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this artifact.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No artifact found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/assetmanager/:uuid/invoices/publish-invoicing-request",
    alias: "publish_invoicing_request_document",
    description: `Generate an invoicing request as an excel file.

Creates an &#x60;&#x60;invoicing_request&#x60;&#x60; Document on the asset manager and runs the docgen
pipeline: the article finds the excel-channel invoices in &#x27;waiting_for_publication&#x27;
status within [start, end) (both bounds optional) and builds the Excel file. The
exported invoices are then transitioned to &#x27;published&#x27;. Download the file via
GET /document/{uuid}/content.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
    ],
    response: DocumentSchemaShort,
    errors: [
      {
        status: 404,
        description: `The asset manager has no invoicing_request docgen config (also 404 when no asset manager matches the uuid).`,
        schema: DocGenConfigNotFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/auth/me",
    alias: "get_me",
    description: `Return the authenticated user&#x27;s identity, permissions, company and,
for assetmanager users, search credentials and enabled UI features.`,
    requestFormat: "json",
    response: User,
  },
  {
    method: "get",
    path: "/api/companies",
    alias: "get_companies",
    description: `Legacy alias for listing legal entities.`,
    requestFormat: "json",
    response: z.array(z.union([CompanySchema, PersonSchema])),
  },
  {
    method: "get",
    path: "/api/companies/:uuid",
    alias: "get_company_details",
    description: `Legacy alias for retrieving legal-entity details.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.union([CompanySchemaWithPermissions, PersonSchema]),
    errors: [
      {
        status: 404,
        description: `No legal entity found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/contact-infos",
    alias: "get_contact_infos",
    description: `List the contact infos in the user&#x27;s scope (linked to its company contract parties,
contracts, sites, holdings or owned by the company), each with an
&#x60;editable&#x60; flag reflecting the user&#x27;s write permission.`,
    requestFormat: "json",
    response: z.array(ContactInfoSchemaWithPermission),
  },
  {
    method: "post",
    path: "/api/contact-infos",
    alias: "create_contact_info",
    description: `Create a contact info, owned by the user&#x27;s company (or Renewex when
the user&#x27;s company cannot own objects).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ContactInfoSchemaCreate,
      },
    ],
    response: ContactInfoSchema,
    errors: [
      {
        status: 409,
        description: `An identical contact info already exists.`,
        schema: ObjectAlreadyExists,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/contact-infos/:uuid",
    alias: "get_contact_info_details",
    description: `Get a contact info&#x27;s details, including its owner and the objects it is
associated to (restricted to those in the user&#x27;s scope) with their roles.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ContactInfoSchemaLong,
    errors: [
      {
        status: 404,
        description: `No contact info found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/contact-infos/:uuid",
    alias: "update_contact_info",
    description: `Update a contact info. Requires write permission on the contact info
(owned by the user&#x27;s company or one of its asset owners).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ContactInfoSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ContactInfoSchema,
    errors: [
      {
        status: 404,
        description: `No contact info found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/contact-infos/:uuid",
    alias: "delete_contact_info",
    description: `Delete a contact info. Requires write permission on the contact info.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No contact info found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/contracts/:uuid",
    alias: "get_contract",
    description: `Retrieve the full details of a contract: clauses, status history,
delivery points, invoicing configuration and accounting information.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ContractSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this contract.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No contract found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/contracts/:uuid",
    alias: "update_contract",
    description: `Partially update a contract (dates, type, invoicing configuration,
external references, VAT overrides, ...). Fields absent from the payload
are left untouched; an empty payload is rejected.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ContractSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ContractSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this contract.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `The selected buyer or seller does not exist.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The update payload is empty.`,
        schema: PeregreenValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/contracts/:uuid/invoices/regulation",
    alias: "create_regulation_invoice_for_contract",
    description: `Compute now a regularisation of [start, end[ (intermediate, or again on a regularised
period): it nets every invoice issued on the period, regularisations included, and is
netted by the next ones. Creates &#x60;computed&#x60; invoices, none when there is nothing to
regularise. Only for clauses regularised on a chosen period (energy efficiency).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_create_regulation_invoice_for_contract_api_contracts__uuid__invoices_regulation_post,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(InvoiceSchema),
    errors: [
      {
        status: 404,
        description: `No contract found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `A regularisation created here on some of these months isn&#x27;t published yet.`,
        schema: PendingRegulation,
      },
      {
        status: 422,
        description: `The period isn&#x27;t month-aligned, leaves the contract, spans two contract years, or the contract has no clause regularised on a chosen period. A period cutting an invoiced line is refused with the same status (&#x60;RegulationPartialLineError&#x60;).`,
        schema: InvalidRegulation,
      },
    ],
  },
  {
    method: "get",
    path: "/api/contracts/:uuid/platform-credential",
    alias: "get_contract_platform_credential",
    description: `Reveal the decrypted credential of the platform account used to submit
the contract&#x27;s invoices, so the user can log into the platform.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: PlatformCredentialSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this contract.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No platform account is configured for the contract&#x27;s site or asset owner, or the platform is not EDF OA (the only platform whose credentials are stored). Also returned when no contract exists for this uuid.`,
        schema: MissingEDFOACredentials,
      },
      {
        status: 409,
        description: `The contract&#x27;s invoices are not platform-submitted.`,
        schema: NotAPlatformSubmittedContract,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/contracts/clause/:uuid",
    alias: "update_clause",
    description: `Partially update a clause: validity horizon, alias, or its typed
parameters. Parameters are merged into the existing ones and re-validated;
the clause type itself cannot be changed through this endpoint.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ClauseUpdateSchema,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ClauseSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this clause&#x27;s contract.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No clause found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/datahealth/:uuid/refresh",
    alias: "datahealth_refresh",
    description: `Recompute a single datahealth score synchronously.

The score is recalculated from the underlying data, persisted immediately,
and returned with its subscores.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DHScoreSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on this datahealth.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No datahealth found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/deliverypoints/:uuid",
    alias: "update_deliverypoint",
    description: `Partially update a delivery point&#x27;s identification and metering
attributes. Fields absent from the payload are left untouched.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DeliveryPointSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DeliveryPointSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on this delivery point.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No delivery point found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/deliverypoints/contractual-mapping",
    alias: "create_dp_contractual_mapping",
    description: `Attach a new contractual mapping to a delivery point, describing how it
must be referenced in contractual/invoicing exchanges over a period.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DPContractualMappingSchemaCreate,
      },
    ],
    response: DPContractualMappingSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted delivery point.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No delivery point found for &#x60;deliverypoint_uuid&#x60;.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/deliverypoints/contractual-mapping/:uuid",
    alias: "update_dp_contractual_mapping",
    description: `Partially update a delivery point&#x27;s contractual mapping. Fields absent
from the payload are left untouched.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: DPContractualMappingSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DPContractualMappingSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the mapping&#x27;s delivery point.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No contractual mapping found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/deliverypoints/contractual-mapping/:uuid",
    alias: "delete_dp_contractual_mapping",
    description: `Permanently delete a delivery point&#x27;s contractual mapping. This cannot
be undone.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the mapping&#x27;s delivery point.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No contractual mapping found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/document",
    alias: "refresh_documents",
    description: `Re-generate existing documents from their stored generation config.

Regeneration runs asynchronously on Dagster unless &#x60;&#x60;wait&#x60;&#x60; is true, in which
case the files are refreshed before responding. Fails when none of the given
uuids match a document.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_refresh_documents_api_document_post,
      },
      {
        name: "wait",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(DocumentSchemaShort),
    errors: [
      {
        status: 404,
        description: `None of the given uuids match a document.`,
        schema: NoDataFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/document/:uuid",
    alias: "get_document",
    description: `Retrieve a single document from its uuid.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: DocumentSchemaShort,
    errors: [
      {
        status: 404,
        description: `No document found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/document/:uuid",
    alias: "delete_document",
    description: `Delete a document record and its file on object storage.

A missing file on storage is tolerated: the record is deleted anyway. A supply invoice&#x27;s PDF
is never deleted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No document found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `The document is a supply invoice&#x27;s PDF: the invoice is excluded, never deleted.`,
        schema: SupplyInvoiceDocumentNotDeletable,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/document/:uuid/content",
    alias: "download_document",
    description: `Stream the document&#x27;s file from object storage as an attachment.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `The document&#x27;s file does not exist on storage (also 404 when no document matches the uuid).`,
        schema: NoDataFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/document/content",
    alias: "download_documents",
    description: `Fetch the documents&#x27; files from object storage and stream them back as
a single zip archive named after the current timestamp.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_download_documents_api_document_content_post,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No document matches the given uuids, or none of the matched documents has a file on storage.`,
        schema: NoDataFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/etl/enedis/site/:uuid/backfill",
    alias: "trigger_enedis_backfill",
    description: `Order historical measurements (M023 backfill files) from Enedis for each
PRM of a site, by submitting one Dagster job per deliverypoint. The order
is asynchronous: data lands in the timeseries once Enedis delivers the
files and they are processed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_trigger_enedis_backfill_api_etl_enedis_site__uuid__backfill_post,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `The site has no deliverypoint with a PRM defined.`,
        schema: SiteWithoutAnyPRM,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/etl/enedis/site/:uuid/intraday",
    alias: "trigger_enedis_intraday_order",
    description: `Order Intraday measures from enedis for a given site.

Caution: Timeseries may take some time before being updated.
Updates are done once the following steps are done:
- send the order to enedis through an API call
- enedis send us the files (several minutes)
- the files are decrypted (sensor every few minutes)
- the infraj files are processed (sensor every minute)

Enedis intraday endpoint:
- https://www.enedis.fr/sites/default/files/import/Enedis-GUI-CF_009E.pdf

&#x60;Un appel à cette API permet au demandeur de disposer, pour un point de connexion actif,
des données brutes de courbes (de charge, de tension), d&#x27;index, et/ou de paramètres de
tarifs dynamiques issues du compteur, avec une fraicheur des données inférieure à 30 min.&#x60;`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `The site has no deliverypoint with a PRM defined.`,
        schema: SiteWithoutAnyPRM,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/etl/enedis/site/:uuid/static-data",
    alias: "trigger_enedis_static_data_order",
    description: `Order static data from Enedis for all PRMs of a site.

This triggers the enedis_order_prms_static_data_job and
enedis_order_prms_light_static_data_job dagster jobs to fetch
contract and technical data for the site&#x27;s deliverypoints that have Enedis as DSO.
The order is asynchronous: this endpoint returns as soon as the jobs are
submitted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `The site has no deliverypoint with a PRM and Enedis as DSO.`,
        schema: SiteWithoutAnyPRM,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/etl/sp_factory/site/:uuid/backfill",
    alias: "trigger_sp_factory_backfill",
    description: `Order historical measurements for a site from non-DSO service providers,
by submitting the Dagster acquisition job(s) configured on each service
provider for the requested timeseries groups. The backfill is
asynchronous: this endpoint returns as soon as the jobs are submitted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema:
          Body_trigger_sp_factory_backfill_api_etl_sp_factory_site__uuid__backfill_post,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid, no service provider found with one of the given names, or a service provider lacks a Dagster backfill job for a requested group (&#x60;MissingAcquisitionDasgterJobCfg&#x60;).`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/events",
    alias: "create_event",
    description: `Create a manual event on a site.

The event is recorded with source &#x60;manual&#x60; and committed immediately.
Fails if the target site does not exist or the user&#x27;s company lacks
permissions on it.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EventSchemaCreate,
      },
    ],
    response: EventSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company does not have permissions on the target site.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No site found for &#x60;site_uuid&#x60;.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/events",
    alias: "delete_events",
    description: `Delete several events at once.

All-or-nothing: rights are checked on every event before any deletion, so
a single unknown uuid or permission failure aborts the whole batch.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_delete_events_api_events_delete,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on at least one event&#x27;s site.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `At least one uuid matches no event.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/events/:uuid",
    alias: "get_event",
    description: `Retrieve a single event (production unavailability/curtailment period) from its uuid.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EventSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company is neither the site&#x27;s asset owner, main asset manager, nor a party to one of its contracts.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No event found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/events/:uuid",
    alias: "update_event",
    description: `Partially update a manual event.

Only events with an editable source can be modified: events imported from
automated sources raise &#x60;EventNotEditable&#x60;. Capacities and percentages are
recomputed consistently against the (possibly new) site&#x27;s capacity before
being persisted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EventSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: EventSchema,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the event&#x27;s site or on the new target site.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No event found for this uuid, or no site found for &#x60;site_uuid&#x60;.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The event comes from an automated source and cannot be edited manually.`,
        schema: EventNotEditable,
      },
    ],
  },
  {
    method: "get",
    path: "/api/filters",
    alias: "get_filters_values",
    description: `Return the possible values of UI filter dropdowns.

Values are scoped to the entities the user&#x27;s company can access, sorted
alphabetically and deduplicated.`,
    requestFormat: "json",
    parameters: [
      {
        name: "kinds",
        type: "Query",
        schema: z.array(FilterKind),
      },
    ],
    response: z.record(
      z.union([z.array(z.string()), z.record(z.array(z.string()))])
    ),
    errors: [
      {
        status: 401,
        description: `The &#x60;assetowners&#x60; kind was requested by a company that is not an asset manager.`,
        schema: Unauthorized,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/filters/:model/:uuid/docgen_configs",
    alias: "get_defined_docgen_configs",
    description: `List the document-generation configs applicable to a model.

Includes configs inherited from related entities (a site also gets its
asset owner&#x27;s, main asset manager&#x27;s, and holding&#x27;s configs), deduplicated
and sorted by kind.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum(["assetmanager", "assetowner", "holding", "site"]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(DocGenConfigSchema),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no rights on the target model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of &#x60;model&#x60; found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/goldensource/finance/refresh",
    alias: "refresh_goldensources_finance",
    description: `Schedule a recomputation of the finance golden source (revenue views)
for the given sites and/or contracts by submitting a Dagster job. The
refresh is asynchronous: this endpoint returns as soon as the job is
submitted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GSFinanceRefreshParams,
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `No site/contract found for these uuids.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/goldensource/refresh",
    alias: "refresh_goldensources",
    description: `Schedule a golden-source recomputation for the targeted sites or
aggregation scopes by submitting Dagster refresh jobs (one per
non-site target). The refresh is asynchronous: this endpoint returns as
soon as the jobs are submitted, and the golden-source data is updated
once they complete.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GSRefreshParams,
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 404,
        description: `No object found for this model/uuids.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/invoices/:uuid",
    alias: "get_invoice",
    description: `Retrieve a single invoice with its lines, status and related contract info.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InvoiceSchema,
    errors: [
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "put",
    path: "/api/invoices/:uuid",
    alias: "update_invoice",
    description: `Replace the computed production values of an invoice with manually
entered ones (meter indexes or prod/conso energies), keeping the invoice
lines in sync. The invoice is marked as computed again (incomplete while it still
rests on estimated meter-change readings); a meter change entered here is persisted
on the delivery point so later computations keep applying it. Only allowed while
the invoice is still refreshable.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: InvoiceUpdateSchema,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InvoiceSchema,
    errors: [
      {
        status: 400,
        description: `The invoice is not refreshable anymore: its computation can no longer be replaced by a manual entry.`,
        schema: InvoiceStatusError,
      },
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The invoice has no ft-solar production computation yet, the entered values don&#x27;t match the invoicing mode (index vs load_profile), or the index lists don&#x27;t carry one entry per production period of the invoice.`,
        schema: MeterChangeInputError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/:uuid/:status_or_action",
    alias: "set_invoice_status_or_action",
    description: `Apply a guarded manual status transition or validation action on an
invoice. Statuses: &#x27;submitted&#x27; requires a published invoice; &#x27;paid&#x27; a
published/submitted/payment one; &#x27;waiting_for_publication&#x27; validates a
computed excel-channel asset-manager invoice; &#x27;computed&#x27; reverts an
incomplete or waiting_for_publication invoice back to computed. Actions
&#x27;accept&#x27;/&#x27;reject&#x27; answer the external-lines proposition of a to_validate
invoice. Fails with 400 when the transition conditions are not met.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "status_or_action",
        type: "Path",
        schema: z.enum([
          "submitted",
          "paid",
          "computed",
          "waiting_for_publication",
          "accept",
          "reject",
        ]),
      },
    ],
    response: InvoiceSchema,
    errors: [
      {
        status: 400,
        description: `The invoice does not meet the conditions for the requested transition (wrong current status, invoice source, submit channel, or missing external lines).`,
        schema: InvoiceStatusError,
      },
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/:uuid/accounted",
    alias: "set_invoice_accounted",
    description: `Mark an invoice as accounted in the ERP (or clear the mark): sets or
resets its accounted date. Only allowed on invoices in an accountable
status.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ is_accounted: z.boolean().nullish().default(true) }),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InvoiceSchema,
    errors: [
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The invoice status is not accountable.`,
        schema: PeregreenValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/:uuid/cancel",
    alias: "cancel_invoice",
    description: `Cancel an invoice using the selected action. Returns the impacted
invoices: the invoice itself (plus the credit note and replacement invoice
for &#x27;credit_note&#x27;), or an empty list for &#x27;delete&#x27;. Fails with 400 when
deleting an invoice canceled by a credit note, with 409 while the
contract&#x27;s invoicing is on hold, and with 422 when unpublishing an
invoice whose e-invoice has already been transmitted, that has already
been accounted or that is a credit note, when an internal credit note is not allowed, or
when canceling with a credit note an invoice without lines or holding credit-note lines.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "cancel_action",
        type: "Query",
        schema: z.enum(["unpublish", "credit_note", "delete"]),
      },
      {
        name: "is_internal_credit_note",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: z.array(InvoiceSchema),
    errors: [
      {
        status: 400,
        description: `The invoice can&#x27;t be deleted: a credit note cancels its lines.`,
        schema: InvoiceStatusError,
      },
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `The contract&#x27;s invoicing is on hold.`,
        schema: InvoicingOnHoldError,
      },
      {
        status: 422,
        description: `&#x60;InvoicePublicationError&#x60; — the invoice can&#x27;t be unpublished: its publication id was issued by the asset manager, its e-invoice has already been transmitted, it has already been accounted, or it is a credit note; &#x60;InternalCreditNoteNotAllowed&#x60; — an internal credit note was requested with another action than &#x27;credit_note&#x27;, for an asset manager without the feature, or on a self-billed invoice; &#x60;CreditNoteWithoutLines&#x60; — the invoice has no line to cancel with a credit note; &#x60;CreditNoteOfCreditNoteLines&#x60; — the invoice holds credit-note lines, which can&#x27;t be canceled with a credit note yet.`,
        schema: z.union([
          InvoicePublicationError,
          InternalCreditNoteNotAllowed,
          CreditNoteWithoutLines,
          CreditNoteOfCreditNoteLines,
        ]),
      },
    ],
  },
  {
    method: "get",
    path: "/api/invoices/:uuid/document",
    alias: "get_invoice_pdf",
    description: `Download the pdf document of an invoice, as an attachment.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 400,
        description: `The invoice has not been documented yet (waiting, computed, error or incomplete status).`,
        schema: InvoiceStatusError,
      },
      {
        status: 404,
        description: `The invoice has no pdf attached, or the stored file is missing.`,
        schema: MissingDataError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/invoices/:uuid/excel",
    alias: "get_invoice_data",
    description: `Download the excel file detailing the invoice computation, as an attachment.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `The invoice has no excel attached, or the stored file is missing.`,
        schema: NoDataFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/:uuid/publish_with_id",
    alias: "publish_invoice_using_publication_id",
    description: `Attach the publication number allocated by the assetmanager to an
invoice waiting for publication, and mark it &#x27;published&#x27;. Renewex still
produces the PDF afterwards; the external service is only the numbering
authority. Only asset-manager-sourced invoices are eligible: the source
frozen on the invoice, else its contract&#x27;s one.

Credit notes follow the same flow: a credit note created in Renewex for an
assetmanager contract carries no Renewex number — it joins the
publication queue and receives its own number here, while the canceled
invoice keeps the original one.

## Client process to follow
1. Pull the queue: list the invoices in status &#x27;waiting_for_publication&#x27;.
2. Allocate the number, POST it here, durably record the (invoice, number)
   pair.
3. On 409, skip that invoice and continue with the rest of the queue.

## Idempotency
Retrying with the same (invoice, publication_id) couple is always safe and
returns 200, whatever status the invoice has reached since — a 200 means
&quot;this number is attached to this invoice&quot;, not &quot;the invoice is in status
&#x27;published&#x27;&quot;. Retrying with a different publication_id is rejected: a
publication number is final once assigned.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "publication_id",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: InvoiceSchema,
    errors: [
      {
        status: 404,
        description: `No invoice found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `Discriminate on the error &#x60;name&#x60;: &#x60;InvoiceNotReadyForPublication&#x60; — the invoice is not in status &#x27;waiting_for_publication&#x27; (skip it, re-pull the queue later); &#x60;InvoiceNotConcernedByPublication&#x60; — the invoice is numbered by Renewex rather than by the asset manager, it will never be publishable here (skip it, report the routing error); &#x60;InvoiceAlreadyHasPublicationId&#x60; — the invoice already carries a different publication id, which is final (stop, reconcile manually); &#x60;InvoicePublicationIdAlreadyUsed&#x60; — the posted id is already used by another invoice of the same contract (stop, reconcile manually).`,
        schema: z.union([
          InvoiceNotReadyForPublication,
          InvoiceNotConcernedByPublication,
          InvoiceAlreadyHasPublicationId,
          InvoicePublicationIdAlreadyUsed,
        ]),
      },
      {
        status: 422,
        description: `The publication id is empty (after stripping whitespace).`,
        schema: InvoicePublicationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/document",
    alias: "get_invoices_pdf",
    description: `Download the pdf documents of several invoices as a single zip archive.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_get_invoices_pdf_api_invoices_document_post,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `None of the requested invoices has a pdf attached.`,
        schema: MissingDataError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/invoices/excel",
    alias: "get_invoices_data",
    description: `Download the computation excels of several invoices as a single zip archive.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: Body_get_invoices_data_api_invoices_excel_post,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `None of the requested invoices has a computation excel attached.`,
        schema: MissingDataError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/invoices/lines/:uuid",
    alias: "update_invoice_line",
    description: `Manually edit a single invoice line. Each changed field is traced as a
manual-edit entry in the invoice log actions.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: InvoiceLineSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: InvoiceLineSchema,
    errors: [
      {
        status: 404,
        description: `Empty update payload: no field to modify on the invoice line.`,
        schema: MissingDataError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/legacy/:model/:uuid/goldensource/finance/timeseries",
    alias: "legacy_get_goldensource_finance_timeseries",
    description: `Deprecated shim kept for external consumers: returns golden-source
finance timeseries (external best-of) as a serialized dataframe.`,
    requestFormat: "json",
    parameters: [
      {
        name: "model",
        type: "Path",
        schema: z.enum([
          "assetmanager",
          "site",
          "assetowner",
          "holding",
          "contract",
        ]),
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: z.enum(["1D", "1MS"]).nullish().default("1MS"),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the targeted model.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No object of the given model found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/legal_entities",
    alias: "get_legal_entities",
    description: `List the companies and physical contracting parties in the user&#x27;s scope.`,
    requestFormat: "json",
    response: z.array(z.union([CompanySchema, PersonSchema])),
  },
  {
    method: "post",
    path: "/api/legal_entities",
    alias: "create_legal_entity",
    description: `Create an offtaker company or a physical contracting party.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: create_legal_entity_Body,
      },
    ],
    response: z.union([CompanySchema, PersonSchema]),
    errors: [
      {
        status: 404,
        description: `The selected address does not exist.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `A legal entity with the same identity already exists.`,
        schema: ObjectAlreadyExists,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/legal_entities/:uuid",
    alias: "get_legal_entity_details",
    description: `Get a legal entity&#x27;s details and, for companies, its write-permission flag.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.union([CompanySchemaWithPermissions, PersonSchema]),
    errors: [
      {
        status: 404,
        description: `No legal entity found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/legal_entities/:uuid",
    alias: "update_legal_entity",
    description: `Update a company or physical person.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: update_legal_entity_Body,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.union([CompanySchema, PersonSchema]),
    errors: [
      {
        status: 404,
        description: `No legal entity found for this UUID, or a referenced address or contact info does not exist.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `A legal entity with the same identity already exists.`,
        schema: ObjectAlreadyExists,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/legal_entities/:uuid/accounting_infos",
    alias: "update_legal_entity_accounting_infos",
    description: `Update accounting identifiers for a legal entity.

Asset owners and holdings retain their company-level accounting fields;
other legal entities use an accounting link scoped to the user&#x27;s asset manager.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: LegalEntityAccountingInfoUpdateSchema,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.union([CompanySchema, AssetManagerLegalEntityLinkSchema]),
    errors: [
      {
        status: 404,
        description: `No legal entity found for this UUID.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/legal_entities/counterparties",
    alias: "get_counterparties",
    description: `List legal entities on the opposite side of the user&#x27;s contracts.

Each counterparty includes its aggregated contract count and contracted site
capacity for the current year.`,
    requestFormat: "json",
    response: z.array(CounterpartySummarySchema),
  },
  {
    method: "get",
    path: "/api/note/:uuid",
    alias: "get_note",
    description: `Retrieve a single note from its uuid.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NoteSchema,
    errors: [
      {
        status: 404,
        description: `No note found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/note/:uuid",
    alias: "patch_note",
    description: `Partially update a note.

Providing &#x60;&#x60;tags&#x60;&#x60; replaces the whole tag list; &#x60;&#x60;level&#x60;&#x60; and &#x60;&#x60;pinned&#x60;&#x60;
are translated to their tag counterparts (overriding any previous level /
pinned tag); &#x60;&#x60;target&#x60;&#x60; re-attaches the note to another model instance.
Fails when the payload contains no field at all.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: NoteSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NoteSchema,
    errors: [
      {
        status: 404,
        description: `No note found for this uuid, or the payload contains nothing to modify.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/note/:uuid",
    alias: "delete_note",
    description: `Permanently delete a note. Prefer archiving to keep the history.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No note found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/note/:uuid/archive",
    alias: "archive_note",
    description: `Archive a note: it stays stored but is excluded from listings by default.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NoteSchema,
    errors: [
      {
        status: 404,
        description: `No note found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/notetag",
    alias: "get_note_tag",
    description: `List the note tags available to the user&#x27;s asset manager.`,
    requestFormat: "json",
    parameters: [
      {
        name: "include_commons",
        type: "Query",
        schema: z.boolean().nullish().default(true),
      },
    ],
    response: z.array(NoteTagSchema),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/notetag",
    alias: "create_notetag",
    description: `Create a note tag owned by the user&#x27;s asset manager.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: NoteTagSchemaCreate,
      },
    ],
    response: NoteTagSchema,
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/notetag/:uuid",
    alias: "patch_notetag",
    description: `Partially update a note tag owned by the user&#x27;s asset manager.

Common (shared) tags cannot be modified.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: NoteTagSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NoteTagSchema,
    errors: [
      {
        status: 403,
        description: `The note tag does not belong to the user&#x27;s company (common tags cannot be modified).`,
        schema: SecurityError,
      },
      {
        status: 404,
        description: `The payload contains nothing to modify.`,
        schema: MissingDataError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/notetag/:uuid",
    alias: "delete_notetag",
    description: `Delete a note tag owned by the user&#x27;s asset manager.

Common (shared) tags cannot be deleted.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 403,
        description: `The note tag does not belong to the user&#x27;s company (common tags cannot be deleted).`,
        schema: SecurityError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/reporting/v1",
    alias: "check_reporting_credentials",
    description: `Authenticated no-op: the base URL of the Power BI data source.

Power BI files the credentials under the &#x60;Web.Contents&#x60; base URL, without the
&#x60;RelativePath&#x60; of the file, and the Power BI Service tests them with a request to that
base URL: it must answer 200 once authenticated, or saving the credentials fails. The
router dependency does all the work (Entra token, tenant to company, asset managers
only).`,
    requestFormat: "json",
    response: z.unknown(),
  },
  {
    method: "get",
    path: "/api/reporting/v1/:table.csv.gz",
    alias: "download_reporting_file",
    description: `A reporting table of the caller&#x27;s organization, as a gzip-compressed CSV.

- &#x60;site_gs_timeseries_monthly&#x60;: one row per &#x60;(site, date)&#x60; for every month of
  golden source data of each site, up to December 31st of the current year to keep the
  full-year budgets.
- &#x60;site_gs_timeseries_daily&#x60;: one row per &#x60;(site, date)&#x60; for every day of the
  current year.
- &#x60;site_static&#x60;: one row per site with its static data (identity, location, topology,
  owner, current tariff).
- &#x60;site_deliverypoints&#x60;: one row per site and delivery point, joining &#x60;site_static&#x60;.
- &#x60;contract_static&#x60;: one row per contract and site it covers (parties, type, status,
  horizon, invoicing, VAT).
- &#x60;contract_gs_timeseries_monthly&#x60;: financial golden source (revenue per clause, product
  and status) of each contract and site, every month up to today (no forecast).
- &#x60;contract_gs_timeseries_daily&#x60;: same, per day of the current year up to today.
- &#x60;supervision_events&#x60;: every supervision event (unavailability periods) of each site,
  validated or not.

UTF-8, header row, &#x60;,&#x60; delimiter, &#x60;.&#x60; decimals, ISO dates (Paris time), empty cells for
missing values.

Answers with a &#x60;302&#x60; to a signed object storage URL valid for a few minutes, which
serves the &#x60;.csv.gz&#x60; file itself (&#x60;Content-Type: application/gzip&#x60;, no
&#x60;Content-Encoding&#x60;): decompress it client-side, e.g. Power Query&#x27;s
&#x60;Binary.Decompress(..., Compression.GZip)&#x60;.`,
    requestFormat: "json",
    parameters: [
      {
        name: "table",
        type: "Path",
        schema: z.enum([
          "site_gs_timeseries_monthly",
          "site_gs_timeseries_daily",
          "site_static",
          "contract_static",
          "supervision_events",
          "contract_gs_timeseries_monthly",
          "contract_gs_timeseries_daily",
          "site_deliverypoints",
        ]),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 302,
        description: `Redirect to a short-lived signed URL of the file on object storage. Follow it without the &#x60;Authorization&#x60; header.`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `The file has not been generated yet for the caller&#x27;s organization.`,
        schema: ReportingFileNotGenerated,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/site_status",
    alias: "log_site_status",
    description: `Log a new status event in a site&#x27;s status history. The site&#x27;s current
status and commissioning date are derived from this history.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SiteStatusHistorySchemaCreate,
      },
    ],
    response: SiteStatusHistorySchema,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/site_status/:uuid",
    alias: "edit_site_status",
    description: `Update a single site status event. Since the site&#x27;s current status and
commissioning date are derived from the status history, they are
recomputed accordingly.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SiteStatusHistorySchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteStatusHistorySchema,
    errors: [
      {
        status: 404,
        description: `No site status found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "delete",
    path: "/api/site_status/:uuid",
    alias: "delete_site_status",
    description: `Delete a status event from a site&#x27;s status history. The site&#x27;s current
status and commissioning date are recomputed from the remaining history.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No site status found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/sites",
    alias: "create_site",
    description: `Create a site and its corresponding structure
(Site &#x3D;&gt; DeliveryPoints &#x3D;&gt; Assets) in a single transaction, and return
the uuid of the new site.

The whole creation is rolled back if any part of the structure fails to
be created (e.g. unknown DSO, manufacturer or region, or a uniqueness
conflict on an existing site).`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SiteSchemaCreate,
      },
    ],
    response: z.string().uuid(),
    errors: [
      {
        status: 404,
        description: `The asset owner is missing or not found (required when the current user is not an asset owner).`,
        schema: MissingDataError,
      },
      {
        status: 409,
        description: `A site with conflicting unique attributes already exists.`,
        schema: ObjectAlreadyExists,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid",
    alias: "get_site",
    description: `Get the entire physical description of a site: delivery points, assets,
machines and their characteristics.

The powercurve will be a list of tuple [wind_speed (m/s), power (kW)] like so:
- [..., [4.0, 92.1], [4.5, 141.7], [5.0, 200.8], ...]`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteSchema,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/sites/:uuid",
    alias: "update_site",
    description: `Update a single site and return its full description.

Linked objects are re-attributed by name: the asset owner and portfolio
are looked up within the current user&#x27;s organization, region and country
by name. Removing a portfolio from a site is not supported — only
(re)attribution.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SiteSchemaUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteSchema,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid, or a referenced linked object (asset owner, portfolio, region, country) was not found within your organization.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/budgets",
    alias: "get_site_budgets",
    description: `List the budgets attached to a site.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(BudgetSchema),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/sites/:uuid/hand_over",
    alias: "run_site_hand_over",
    description: `Hand over a site: generate the final acceptance report document and log
the resulting status in the site&#x27;s history.

The logged status is handed_over when the performance test passed, or
handed_over_with_reserve when it failed. The site must be ready for hand
over: no ongoing blocking reserve notes, a work acceptance status, and a
conclusive performance test (for full-service sites); otherwise the
request is rejected without any side effect.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteStatusHistorySchema,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 409,
        description: `The site has already been handed over.`,
        schema: DuplicateSiteStatusError,
      },
      {
        status: 422,
        description: `The site is not ready for hand over (ongoing blocking reserve notes, missing work acceptance, or no performance test done).`,
        schema: SiteNotReadyForHandOverError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/load_factor",
    alias: "get_load_factor",
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: freq,
      },
      {
        name: "missing_left_idx_tol",
        type: "Query",
        schema: missing_left_idx_tol,
      },
      {
        name: "io_format",
        type: "Query",
        schema: io_format,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The horizon x freq combination targets more than 120,000 data points.`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/load_factor_quantiles",
    alias: "get_load_factor_quantiles",
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: freq,
      },
      {
        name: "quantiles",
        type: "Query",
        schema: quantiles,
      },
      {
        name: "year_range",
        type: "Query",
        schema: year_range,
      },
      {
        name: "extrapolation_window",
        type: "Query",
        schema: extrapolation_window,
      },
      {
        name: "io_format",
        type: "Query",
        schema: io_format,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The horizon x freq combination targets more than 120,000 data points.`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/metering",
    alias: "get_site_metering",
    description: `Get the metering topology of a site: its delivery points (head meters,
sub-meters, meters), their kinds and how they are linked together.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteMetering,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/sites/:uuid/performance_test",
    alias: "run_performance_test",
    description: `Run the performance test for a site and generate the performance test
report document.

If the site already has a conclusive test status (test_passed or
test_failed) in its history, the test is not re-run: the report is
(re)generated and the existing conclusive status is returned. Otherwise
the test runs and logs a new status; when the outcome is not conclusive
(e.g. still in progress), the new status is returned without generating
the report.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "permissive",
        type: "Query",
        schema: z.boolean().nullish(),
      },
      {
        name: "failure_mode",
        type: "Query",
        schema: z.boolean().nullish(),
      },
    ],
    response: SiteStatusHistorySchema,
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/production",
    alias: "get_site_production_chart",
    description: `Get the production timeseries of a site, aggregated over its delivery
points and resampled to the requested frequency and unit.

missing_freq_tol and missing_data_resilient can be tuned in order to be
more or less permissive on data holes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "freq",
        type: "Query",
        schema: z.string().nullish().default("1MS"),
      },
      {
        name: "unit",
        type: "Query",
        schema: z.string().nullish().default("MWh"),
      },
      {
        name: "missing_freq_tol",
        type: "Query",
        schema: z.number().nullish().default(1),
      },
      {
        name: "missing_data_resilient",
        type: "Query",
        schema: z.boolean().nullish().default(true),
      },
      {
        name: "io_format",
        type: "Query",
        schema: z.enum(["json", "feather"]).nullish(),
      },
      {
        name: "prm_uuids",
        type: "Query",
        schema: portfolio_names,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The horizon x freq combination targets more than 120,000 data points.`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/reactive",
    alias: "get_site_reactive_chart",
    description: `Get reactive chart data for a given site.
The goal is to reproduce the chart provided by Enedis in the network contract.
https://www.enedis.fr/media/1898/download

Note: The conventions are inversed compared to the other timeseries because we are looking
from Enedis point of view. For both active and reactive power timeseries:
- Inject / Production: negative
- Offtake / Consumption: Positive`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
    ],
    response: z.array(ReactiveChartDataOnPeriod),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/supervision",
    alias: "get_site_supervision_chart",
    description: `Get the supervision chart of a site: production alongside its expected
baseline, for monitoring purposes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: freq__2,
      },
      {
        name: "unit",
        type: "Query",
        schema: unit,
      },
      {
        name: "io_format",
        type: "Query",
        schema: io_format,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The horizon is too deep for the requested intraday frequency (max 14 days at 5min, 31 days at 15min, 62 days at 30min).`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/:uuid/voltage",
    alias: "get_site_voltage_chart",
    description: `Get the voltage timeseries of a site, resampled to the requested frequency.

missing_freq_tol and missing_data_resilient can be tuned in order to be
more or less permissive on data holes.`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "freq",
        type: "Query",
        schema: z.string().nullish().default("1MS"),
      },
      {
        name: "unit",
        type: "Query",
        schema: z.string().nullish().default("MWh"),
      },
      {
        name: "missing_freq_tol",
        type: "Query",
        schema: z.number().nullish().default(1),
      },
      {
        name: "missing_data_resilient",
        type: "Query",
        schema: z.boolean().nullish().default(true),
      },
      {
        name: "io_format",
        type: "Query",
        schema: z.enum(["json", "feather"]).nullish(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for this uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `The horizon x freq combination targets more than 120,000 data points.`,
        schema: QueryTooExpensive,
      },
    ],
  },
  {
    method: "get",
    path: "/api/sites/commissioning",
    alias: "get_sites_for_commissioning",
    description: `List the sites of the current user&#x27;s organization commissioned on the
given &#x60;[start, end[&#x60; period, with their commissioning performance details
and their commissioning group/subgroup classification. Sites for which
the computation fails are skipped.`,
    requestFormat: "json",
    parameters: [
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
    ],
    response: z.array(CommissioningSite),
    errors: [
      {
        status: 400,
        description: `The commissioning computation failed for every site on the requested period.`,
        schema: CommissioningComputationError,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/supply-invoices/:uuid",
    alias: "update_supply_invoice",
    description: `Exclude or re-include a supply invoice, or fix its extracted values.

Edited amounts or lines switch the invoice&#x27;s provenance to &#x60;manual_fix&#x60;. Returns the site&#x27;s supply
invoices with the yearly averages recomputed.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SupplyInvoiceUpdate,
      },
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: SiteSupplyInvoices,
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company has no permission on the invoice&#x27;s supply contract.`,
        schema: Unauthorized,
      },
      {
        status: 404,
        description: `No supply invoice found for this uuid, or a line uuid is not one of its lines.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `A line&#x27;s delivery point is not on the invoice&#x27;s supply contract, or its period ends before it starts.`,
        schema: InvalidSupplyInvoiceUpdate,
      },
    ],
  },
  {
    method: "get",
    path: "/api/timeseries/:name/data",
    alias: "get_timeseries_data",
    description: `Read the records of a timeseries over a horizon, optionally resampled
to another frequency and converted to another unit. Timestamps are
returned in UTC.`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "version",
        type: "Query",
        schema: version,
      },
      {
        name: "freq",
        type: "Query",
        schema: unit,
      },
      {
        name: "unit",
        type: "Query",
        schema: unit,
      },
      {
        name: "missing_freq_tol",
        type: "Query",
        schema: missing_freq_tol,
      },
      {
        name: "io_format",
        type: "Query",
        schema: io_format,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No timeseries found with this name.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "post",
    path: "/api/timeseries/:name/data",
    alias: "post_timeseries_data",
    description: `Manually insert or overwrite records of a timeseries. The previous and
inserted values are journaled in a log artifact for auditability before
the upsert is applied; if the artifact cannot be created, nothing is
written. Requires write permission on one of the timeseries&#x27; targets.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EditTimeseriesData,
      },
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No timeseries found with this name.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Some record timestamps do not fall on the timeseries&#x27; frequency grid, or are in the future.`,
        schema: InvalidTimeseriesIndex,
      },
    ],
  },
  {
    method: "get",
    path: "/api/timeseries/:name/metadata",
    alias: "get_timeseries_metadata",
    description: `Return the metadata of a timeseries: frequency, unit, service provider
and its acquisition code, and the available data horizon.`,
    requestFormat: "json",
    parameters: [
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TSMetadata,
    errors: [
      {
        status: 404,
        description: `No timeseries found with this name.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "patch",
    path: "/api/timeseries/:name/metadata",
    alias: "patch_timeseries_metadata",
    description: `Edit the metadata of a timeseries. For now only the sp_code (the
service-provider acquisition code) can be edited; the timeseries must
already have a service provider set. Requires write permission on one of
the timeseries&#x27; targets.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ sp_code: z.record(z.any()) }),
      },
      {
        name: "name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TSMetadataOverview,
    errors: [
      {
        status: 400,
        description: `The timeseries has no service provider, so no sp_code can be set.`,
        schema: BadRequest,
      },
      {
        status: 404,
        description: `No timeseries found with this name.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/timeseries/:timeserie_id/stats",
    alias: "get_timeseries_stats",
    description: `Return descriptive statistics (count, min, max, mean, ...) of a
timeseries&#x27; records over a horizon, computed by the timeseries store.`,
    requestFormat: "json",
    parameters: [
      {
        name: "timeserie_id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "start",
        type: "Query",
        schema: commissioning_from,
      },
      {
        name: "end",
        type: "Query",
        schema: commissioning_from,
      },
    ],
    response: Statistics,
    errors: [
      {
        status: 404,
        description: `No timeseries found with this name.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/topology/capacity",
    alias: "contracted_capacity",
    description: `Provide a portfolio view of installed capacity (kW) over &#x60;[start, end[&#x60;,
split by resampling period and site contract status. Only available to
AssetManager, AssetOwner and Holding companies.`,
    requestFormat: "json",
    parameters: [
      {
        name: "start",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "end",
        type: "Query",
        schema: z.coerce.date(),
      },
      {
        name: "freq",
        type: "Query",
        schema: freq__3,
      },
      {
        name: "company_uuid",
        type: "Query",
        schema: unit,
      },
    ],
    response: z.record(TimeSeriesJSON),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/topology/contracts/summary",
    alias: "portfolio_contract_summary",
    description: `Count distinct contracts by price mechanism and status for an AssetManager,
AssetOwner or Holding. Asset managers are scoped by the contract&#x27;s manager;
asset owners and holdings by the sites linked through its clauses&#x27; delivery points.`,
    requestFormat: "json",
    parameters: [
      {
        name: "company_uuid",
        type: "Query",
        schema: unit,
      },
    ],
    response: z.record(z.array(ContractSummarySchema)),
    errors: [
      {
        status: 401,
        description: `The user&#x27;s company cannot access the requested portfolio.`,
        schema: Unauthorized,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/topology/physical",
    alias: "get_sites_topology",
    description: `Export the physical topology (sites, delivery points, assets) of the
selected sites as an Excel file. When no site is specified, the whole
portfolio of the company is exported.`,
    requestFormat: "json",
    parameters: [
      {
        name: "sites",
        type: "Query",
        schema: portfolio_names,
      },
      {
        name: "company_uuid",
        type: "Query",
        schema: unit,
      },
    ],
    response: z.unknown(),
    errors: [
      {
        status: 404,
        description: `No site found for a given uuid.`,
        schema: NoObjectFound,
      },
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
  {
    method: "get",
    path: "/api/topology/sites/summary",
    alias: "portfolio_site_summary",
    description: `Provide a portfolio summary of operating sites grouped by technology
(solar, wind, ...): number of sites and total installed capacity (kW).
Only available to AssetManager, AssetOwner and Holding companies.`,
    requestFormat: "json",
    parameters: [
      {
        name: "company_uuid",
        type: "Query",
        schema: unit,
      },
    ],
    response: z.record(SiteSummarySchema),
    errors: [
      {
        status: 422,
        description: `Validation Error`,
        schema: HTTPValidationError,
      },
    ],
  },
]);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
